import { useState, useEffect, useMemo, useCallback, useRef, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import { useCredits } from '@/contexts/CreditsContext';
import { useReportVisibility } from '@/contexts/ReportVisibilityContext';
import axios from 'axios';
import { toast } from 'sonner';
import Header from '@/components/Header';
import FCRVersionGuidanceNote from '@/components/FCRVersionGuidanceNote';
import HelpBenefits, { HELP_URLS } from '@/components/HelpBenefits';
import FeatureVideoHero from '@/components/FeatureVideoHero';
import WingmanTriggerPrompts from '@/components/WingmanTriggerPrompts';
import WingmanInsight from '@/components/WingmanInsight';
import WingmanNudge, { detectGrowthModelPatterns } from '@/components/WingmanNudge';
import RecordHistoryPanel, { saveMfiToHistory } from '@/components/RecordHistoryPanel';
import ModuleExamplesBar from '@/components/ModuleExamplesBar';
import UpdateCuratedExampleButton from '@/components/UpdateCuratedExampleButton';
import ReportGlossary from '@/components/ReportGlossary';
import { useCuratedExamples } from '@/hooks/useCuratedExamples';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

import { ArrowLeft, TrendingUp, AlertTriangle, CheckCircle, Maximize2, X, Info, Save, FolderOpen, Trash2, RefreshCw, Lock, Unlock, ChevronDown, ChevronUp, ArrowUpCircle, ArrowDownCircle, Search, Download, Brain, GitCompareArrows, Scale, Pencil } from 'lucide-react';
import { InlineRenameDouble } from '@/components/InlineRename';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, ReferenceLine, ReferenceArea, Label as RechartsLabel } from 'recharts';
import { COMING_SOON_SPECIES } from '@/config/species';
import { CHART_EXPORT_CONFIG, validateConfigParity } from '@/config/chartExportConfig';
import { GrowthModelStockIntegrity } from '@/components/StockIntegrityScore';
import { useApplySpeciesDefaults } from '@/hooks/useSpeciesDefaults';
import { TrajectoryJumpAlert, SampleJumpAnalysisPanel, FCRAnalysisPanel, MissingFishIntelligenceReport, PlanningEstimateSummary } from '@/components/MissingFishIntelligence';
import GuardrailsToggle from '@/components/GuardrailsToggle';
import { generateMissingFishIntelligence } from '@/utils/generateIntelligence';
import { applySSOTOverride } from '@/utils/ssotOverride';
import AdminHiddenSectionsOverview from '@/components/AdminHiddenSectionsOverview';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function GrowthModelAnalysis() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { formatCostLabel } = useCredits();
  const { isAdmin } = useReportVisibility();
  const isDemo = user?.is_demo === true;
  // Read-only mode: user not approved (pending admin approval)
  const isReadOnly = !user?.is_approved && !user?.is_unlimited && !user?.is_admin;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [temperatureRegions, setTemperatureRegions] = useState([]);
  const [chartExpanded, setChartExpanded] = useState(false);
  const [showLegendInfo, setShowLegendInfo] = useState(false);
  const [showAllColumns, setShowAllColumns] = useState(false);
  const [showDailyTable, setShowDailyTable] = useState(true);
  const [showAllDailyRows, setShowAllDailyRows] = useState(false);

  // Growth Records State
  const [showRecords, setShowRecords] = useState(false);
  const [records, setRecords] = useState({ ongoing: [], closed: [] });
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [activeRecordId, setActiveRecordId] = useState(null);
  const [sortBy, setSortBy] = useState('updated_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showReplaceDialog, setShowReplaceDialog] = useState(false);
  const [pendingSaveData, setPendingSaveData] = useState(null);
  const [showDeleteAllDialog, setShowDeleteAllDialog] = useState(false);
  const [deleteAllConfirmText, setDeleteAllConfirmText] = useState('');

  // Multi-select deletion state
  const [selectedGrowthRecordIds, setSelectedGrowthRecordIds] = useState(new Set());

  // Missing Fish Intelligence state (replaces basic Missing Fish Analysis)
  const [missingFishIntelligence, setMissingFishIntelligence] = useState(null);
  const [showMissingFishIntelligence, setShowMissingFishIntelligence] = useState(false);
  const [graphInsight, setGraphInsight] = useState(null);
  const [trajectoryJump, setTrajectoryJump] = useState(null);
  const [assumedFishNumber, setAssumedFishNumber] = useState(null);
  const [detectionMethod, setDetectionMethod] = useState('jump'); // 'jump' | 'fcr'
  const [isRecordClosedRaw, setIsRecordClosedRaw] = useState(false);
  const [excludedSamples, setExcludedSamples] = useState(new Set());
  const [userSpecies, setUserSpecies] = useState([]);
  
  // ★ ADMIN OVERRIDE: Admins can edit closed records
  const isRecordClosed = isRecordClosedRaw && !isAdmin;

  // ★ BEST FIT ADJUSTED MODEL — calculated when Missing Fish Intelligence runs
  const [bestFitAdjustment, setBestFitAdjustment] = useState(null); // { percent: 8, reason: 'missing fish' }
  const [manualFishOverride, setManualFishOverride] = useState(null); // Manual fish count override
  const [showAdjustedModel, setShowAdjustedModel] = useState(true); // Toggle visibility of adjusted model line

  // ★ USER-INPUT BIOLOGICAL FCR — for improved accuracy comparison
  const [userReportedFCR, setUserReportedFCR] = useState('');
  const [showFCRInput, setShowFCRInput] = useState(false);

  // ★ MASTER OVERRIDE: View All Data — forces decision-grade across ALL sections
  const [showAllDataOverride, setShowAllDataOverride] = useState(false);

  // ★ SSOT Override — compute overriddenOutput for all child components
  const _rawOverriddenOutput = useMemo(() => {
    if (!missingFishIntelligence) return null;
    return applySSOTOverride(
      missingFishIntelligence,
      missingFishIntelligence.decisionContext,
      missingFishIntelligence.ssot
    );
  }, [missingFishIntelligence]);

  // ★ EFFECTIVE OUTPUT: When showAllDataOverride is active, force decision-grade on ALL data
  const overriddenOutput = useMemo(() => {
    if (!showAllDataOverride || !_rawOverriddenOutput) return _rawOverriddenOutput;
    const fd = _rawOverriddenOutput.finalDecision || {};
    const ssot = missingFishIntelligence?.ssot || {};
    
    // ★ When override is active, ensure missing fish count is displayed
    const missingFishCount = ssot?.missingFishCount || fd.missingFishCount || 0;
    const adjustedFishCount = ssot?.adjustedFishCount || 0;
    
    return {
      ..._rawOverriddenOutput,
      finalDecision: {
        ...fd,
        _originalConfidenceMode: fd._originalConfidenceMode || fd.confidenceMode, // Preserve original for banner logic
        confidenceMode: 'decision',
        confidenceModeLabel: 'Full Data (Override)',
        missingFishUserVisible: true,
        hasMissingFish: fd.hasMissingFish !== false ? true : fd.hasMissingFish,
        showFinancialImpact: true,
        financialIsIndicative: false,
        canUseMissingFishWording: true,
        financialContextLevel: fd._originalFinancialContextLevel || fd.financialContextLevel || 'confirmed',
        financialContextReason: null,
        flags: { ...fd.flags, validationRequired: false, notDecisionGrade: false },
        // ★ Force missing fish count display when override is active
        missingFishCount: missingFishCount,
        missingFishCountDisplay: missingFishCount > 0 ? `~${missingFishCount.toLocaleString()} (Indicative)` : null,
        adjustedFishCountLabel: adjustedFishCount > 0 ? `~${adjustedFishCount.toLocaleString()} (Indicative)` : fd.adjustedFishCountLabel,
        modeLabels: {
          ...fd.modeLabels,
          estimatedMissingFishLabel: 'Estimated Missing Fish (Indicative)',
          revenueRiskLabel: 'Missing Fish Revenue Risk',
          reportTitle: 'FCR Signal Analysis',
        },
      },
    };
  }, [showAllDataOverride, _rawOverriddenOutput, missingFishIntelligence?.ssot]);

  // PDF Export state and ref
  const [exportingPdf, setExportingPdf] = useState(false);
  const pdfExportRef = useRef(null);
  const mfiPdfRef = useRef(null);
  // formRef lets the curated "Try Seabass" example trigger a real submit
  // after we've poured the preset into formData + weightSamples.
  const formRef = useRef(null);
  // Set to true by `loadExampleAndRun` and consumed inside `handleSubmit`
  // so curated "Try [Species]" runs POST with `?example=1` (quota-free).
  const exampleRunRef = useRef(false);
  // Tracks the curated example just loaded so the admin "Update Try-X" CTA
  // can render. Cleared whenever the admin loads a different (non-curated)
  // saved record or clears the form.
  const [loadedCurated, setLoadedCurated] = useState(null);

  // Curated "Try [Species]" examples — admin-controlled via AdminConsole.
  const { examples: growthModelExamples, refetch: refetchExamples } = useCuratedExamples('growth_model');
  
  // Cycle comparison state - overlay saved analyses on current graph
  const [comparisonAnalyses, setComparisonAnalyses] = useState([]);
  const [loadingComparisonId, setLoadingComparisonId] = useState(null);
  const [showComparisonSelector, setShowComparisonSelector] = useState(false);
  
  // Line visibility state - control which lines are shown on chart
  const [lineVisibility, setLineVisibility] = useState({
    modelGrowth: true,        // Original unadjusted model (blue)
    adjustedModel: true,      // Best-fit adjusted model (green)
    actualGrowth: true,       // Actual growth trajectory (red)
    samplePoints: true,       // Sample points (teal dots)
  });
  
  // Comparison colors for overlaid analyses
  const COMPARISON_COLORS = [
    { model: '#8b5cf6', actual: '#a78bfa', name: 'Purple' },
    { model: '#06b6d4', actual: '#22d3ee', name: 'Cyan' },
    { model: '#f59e0b', actual: '#fbbf24', name: 'Amber' },
    { model: '#10b981', actual: '#34d399', name: 'Emerald' },
    { model: '#ec4899', actual: '#f472b6', name: 'Pink' },
  ];

  const [formData, setFormData] = useState({
    species: 'Seabream',
    region: '',
    genetic_origin: 'Greek',
    fcr_version_id: '',  // FCR origin version for multiplier
    arrival_average_weight_g: '',
    arrival_date: '',
    temperature_region: '',
    unit: '',
    batch_code: '',
    starting_fish_number: '',
    average_mortality_percentage: '',
    model_adjustment_percent: '100',  // Default 100% (no adjustment)
    model_source: 'TPS',  // 'TPS' or 'CUSTOM'
    custom_model_id: '',  // Selected custom model ID
  });

  // FCR Origins state for version selection
  const [fcrOrigins, setFcrOrigins] = useState([]);
  const [loadingFcrOrigins, setLoadingFcrOrigins] = useState(false);

  // Auto-populate species-specific defaults across the platform
  // (genetic origin, FCR version, temperature region, model source).
  useApplySpeciesDefaults({
    token,
    species: formData.species,
    keys: ['genetic_origin', 'fcr_version_id', 'temperature_region', 'model_source'],
    onApply: (patch) => setFormData((prev) => ({ ...prev, ...patch })),
  });

  // Custom model selection state
  const [approvedModels, setApprovedModels] = useState([]);
  const [loadingModels, setLoadingModels] = useState(false);

  // Monthly temperatures for Tilapia (12 months)
  const [monthlyTemperatures, setMonthlyTemperatures] = useState({
    january: '', february: '', march: '', april: '',
    may: '', june: '', july: '', august: '',
    september: '', october: '', november: '', december: ''
  });
  const [temperaturePreview, setTemperaturePreview] = useState(null);

  // Weight samples - minimum 1 required (arrival), up to 7 total
  const [weightSamples, setWeightSamples] = useState([
    { sample_number: 1, sample_date: '', actual_weight_g: '', label: 'Sample 1 (Required)', required: true },
    { sample_number: 2, sample_date: '', actual_weight_g: '', label: 'Sample 2 (Optional)', required: false },
    { sample_number: 3, sample_date: '', actual_weight_g: '', label: 'Sample 3 (Optional)', required: false },
    { sample_number: 4, sample_date: '', actual_weight_g: '', label: 'Sample 4 (Optional)', required: false },
    { sample_number: 5, sample_date: '', actual_weight_g: '', label: 'Sample 5 (Optional)', required: false },
    { sample_number: 6, sample_date: '', actual_weight_g: '', label: 'Sample 6 (Optional)', required: false },
    { sample_number: 7, sample_date: '', actual_weight_g: '', label: 'Harvest (Optional)', required: false },
  ]);

  // ═══════════════════════════════════════════════════════════════════════════
  // USER-INPUT FCR COMPARISON — Calculate missing fish from reported biological FCR
  // ═══════════════════════════════════════════════════════════════════════════
  // Uses the same methodology as snapshot analysis: if reported FCR is higher than
  // expected model FCR, the difference implies missing fish (fewer fish eating more feed)
  // ═══════════════════════════════════════════════════════════════════════════
  // FCR COMPARISON — Uses EXACT same model FCR lookup as Snapshot Analysis
  // Model FCR is retrieved from backend API based on latest sample weight.
  // NO FALLBACKS — if model FCR is unavailable, no FCR estimate is produced.
  // 
  // LOGIC (aquaculture-specific):
  //   - Reported FCR < Model FCR → MISSING FISH (fewer fish eating same feed = better efficiency)
  //   - Reported FCR ≥ Model FCR → Likely extra fish or sample error (rare in aquaculture)
  //
  // Formula: adjustedFish = original × (reportedFCR / modelFCR)
  //          missingFish = original − adjustedFish (only if reported < model)
  // ═══════════════════════════════════════════════════════════════════════════
  const userFcrComparison = useMemo(() => {
    const reportedFcr = parseFloat(userReportedFCR);
    if (!reportedFcr || reportedFcr <= 0) return null;
    
    // ★ GET MODEL FCR FROM INTELLIGENCE (same source as Snapshot Analysis)
    // This value comes from the backend API lookup at the latest sample weight
    // Note: sharedMetrics is at top level of intelligence object, not inside ssot
    const modelFcr = missingFishIntelligence?.sharedMetrics?.model_fcr;
    
    // ★ NO FALLBACKS — if model FCR is not available, return null
    if (!modelFcr || modelFcr <= 0) return null;
    
    const originalFishCount = parseInt(formData.starting_fish_number) || missingFishIntelligence?.originalFishNumber || 0;
    if (originalFishCount <= 0) return null;
    
    // ★ FCR GAP CALCULATION
    // Negative gap = reported is LOWER than model (missing fish signal)
    // Positive gap = reported is HIGHER than model (extra fish or error)
    const fcrGapPercent = ((reportedFcr - modelFcr) / modelFcr) * 100;
    
    let estimatedMissing = 0;
    let adjustedFishCount = originalFishCount;
    let interpretation = '';
    
    // ★ MISSING FISH CALCULATION (only if reported < model)
    // Lower FCR than expected = fewer fish eating the feed = missing fish
    if (reportedFcr < modelFcr) {
      // Adjusted count = original × (reported FCR / model FCR)
      adjustedFishCount = Math.round(originalFishCount * (reportedFcr / modelFcr));
      // Missing fish = original − adjusted
      estimatedMissing = originalFishCount - adjustedFishCount;
      interpretation = 'missing_fish';
    } else if (reportedFcr > modelFcr) {
      // Higher FCR than expected = likely extra fish or sample error
      interpretation = 'extra_fish_or_error';
    } else {
      // Exactly equal (rare)
      interpretation = 'matches_model';
    }
    
    return {
      reportedFcr,
      modelFcr,
      fcrGapPercent: Math.round(fcrGapPercent * 10) / 10,
      originalFishCount,
      adjustedFishCount,
      estimatedMissing,
      interpretation,
      explanation: interpretation === 'missing_fish'
        ? `Your reported FCR (${reportedFcr.toFixed(2)}) is ${Math.abs(fcrGapPercent).toFixed(1)}% lower than the model FCR (${modelFcr.toFixed(2)}). This suggests approximately ${estimatedMissing.toLocaleString()} missing fish.`
        : interpretation === 'extra_fish_or_error'
        ? `Your reported FCR (${reportedFcr.toFixed(2)}) is ${Math.abs(fcrGapPercent).toFixed(1)}% higher than the model FCR (${modelFcr.toFixed(2)}). This is unusual — likely indicates extra fish or a sample/data error.`
        : `Your reported FCR (${reportedFcr.toFixed(2)}) matches the model FCR (${modelFcr.toFixed(2)}).`,
    };
  }, [userReportedFCR, missingFishIntelligence, formData.starting_fish_number]);

  // Fetch available temperature regions on component mount
  useEffect(() => {
    const fetchTemperatureRegions = async () => {
      try {
        const authToken = token; // from useAuth
        // First try to fetch with custom models (requires auth)
        if (token) {
          try {
            const response = await axios.get(
              `${BACKEND_URL}/api/calculator/temperature-regions-with-custom`,
              { headers: { 'Authorization': `Bearer ${token}` } }
            );
            // Combine standard regions, custom models, and public models
            const standardRegions = response.data.standard_regions || [];
            const customModels = response.data.custom_models || [];
            const publicModels = response.data.public_models || [];
            
            // Format: standard regions first, then user's custom models, then public models
            const allRegions = [
              ...standardRegions,
              ...customModels.map(m => ({ 
                id: m.id, 
                name: m.name, 
                isCustom: true,
                displayName: `My Model: ${m.name}`
              })),
              ...publicModels.map(m => ({ 
                id: m.id, 
                name: m.name, 
                isPublic: true,
                displayName: `Shared: ${m.name}`
              }))
            ];
            setTemperatureRegions(allRegions);
            return;
          } catch (err) {
            // Fallback to standard regions silently
          }
        }
        
        // Fallback: standard regions only
        const response = await axios.get(`${BACKEND_URL}/api/calculator/temperature-regions`);
        setTemperatureRegions(response.data.regions || []);
      } catch (err) {
        console.error('Failed to fetch temperature regions:', err);
        toast.error('Failed to load temperature regions');
      }
    };
    
    fetchTemperatureRegions();
  }, []);

  // Fetch user-specific species (Tripletail restricted to specific accounts)
  useEffect(() => {
    const fetchUserSpecies = async () => {
      try {
        const authToken = token; // from useAuth
        const resp = await axios.get(`${BACKEND_URL}/api/user-species`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setUserSpecies(resp.data.species || []);
      } catch { /* fallback: only base species shown */ }
    };
    fetchUserSpecies();
  }, []);


  // Fetch approved custom models and user's model assignment
  useEffect(() => {
    const fetchModelData = async () => {
      setLoadingModels(true);
      try {
        const authToken = token; // from useAuth
        
        // Fetch approved models
        const modelsResponse = await axios.get(
          `${BACKEND_URL}/api/custom-models/approved?model_type=SGR`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        setApprovedModels(modelsResponse.data || []);
        
        // Fetch user's model assignment (for pre-selection)
        try {
          const assignmentResponse = await axios.get(
            `${BACKEND_URL}/api/user/model-assignment`,
            { headers: { 'Authorization': `Bearer ${token}` } }
          );
          
          // Pre-select based on admin assignment
          if (assignmentResponse.data.sgr_model_source === 'CUSTOM' && assignmentResponse.data.sgr_custom_model_id) {
            setFormData(prev => ({
              ...prev,
              model_source: 'CUSTOM',
              custom_model_id: assignmentResponse.data.sgr_custom_model_id
            }));
          }
        } catch (assignmentErr) {
          // Assignment fetch failed - use defaults silently
        }
      } catch (err) {
        console.error('Failed to fetch approved models:', err);
      } finally {
        setLoadingModels(false);
      }
    };
    
    fetchModelData();
  }, []);

  // Fetch growth records
  const fetchRecords = useCallback(async () => {
    setRecordsLoading(true);
    try {
      const authToken = token; // from useAuth
      const response = await axios.get(
        `${BACKEND_URL}/api/growth-records?sort_by=${sortBy}&sort_order=${sortOrder}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setRecords({
        ongoing: response.data.ongoing || [],
        closed: response.data.closed || []
      });
    } catch (err) {
      console.error('Failed to fetch growth records:', err);
      toast.error('Failed to load growth records');
    } finally {
      setRecordsLoading(false);
    }
  }, [sortBy, sortOrder]);

  // Fetch records on mount and when sort changes, or when comparison selector opens
  useEffect(() => {
    if (showRecords || showComparisonSelector) {
      fetchRecords();
    }
  }, [showRecords, showComparisonSelector, fetchRecords]);

  // Fetch FCR Origins for version selection
  useEffect(() => {
    const fetchFcrOrigins = async () => {
      setLoadingFcrOrigins(true);
      try {
        const authToken = token; // from useAuth
        const response = await axios.get(`${BACKEND_URL}/api/fcr-origins`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setFcrOrigins(response.data?.origins || []);
        
        // Set default version if not already set and genetic origin matches
        if (!formData.fcr_version_id && formData.genetic_origin) {
          const matchingOrigin = (response.data?.origins || []).find(
            o => o.name === formData.genetic_origin
          );
          if (matchingOrigin?.default_version_id) {
            setFormData(prev => ({
              ...prev,
              fcr_version_id: matchingOrigin.default_version_id
            }));
          }
        }
      } catch (err) {
        console.error('Failed to fetch FCR origins:', err);
      } finally {
        setLoadingFcrOrigins(false);
      }
    };
    
    fetchFcrOrigins();
  }, []);

  // Update FCR version when genetic origin changes.
  //
  // Bug fix (May 2026): the previous guard `!formData.fcr_version_id`
  // meant the version only updated when EMPTY. So switching Seabream's
  // origin from Greek → Italian left fcr_version_id on Greek v1, since
  // it was already populated. Result: customers picked Italian but the
  // analysis silently kept running with the Greek FCR table.
  //
  // Correct behaviour: whenever genetic_origin changes, if the current
  // fcr_version_id does NOT belong to any version of the newly-chosen
  // origin, switch to the new origin's default. This still respects a
  // user who manually picked v2 of the same origin (their pick survives).
  useEffect(() => {
    if (fcrOrigins.length === 0 || !formData.genetic_origin) return;
    const matchingOrigin = fcrOrigins.find(o => o.name === formData.genetic_origin);
    if (!matchingOrigin?.default_version_id) return;

    const currentBelongsToNewOrigin = (matchingOrigin.versions || []).some(
      v => v.id === formData.fcr_version_id
    );
    if (currentBelongsToNewOrigin) return;

    setFormData(prev => ({
      ...prev,
      fcr_version_id: matchingOrigin.default_version_id,
    }));
  }, [formData.genetic_origin, fcrOrigins]);

  // Phase B: auto-select the Tilapia / Seabass default FCR version on species change.
  useEffect(() => {
    if (fcrOrigins.length > 0 && formData.species === 'Tilapia' && !formData.fcr_version_id) {
      const tilapiaOrigin = fcrOrigins.find(o => (o.code || '').toLowerCase() === 'tilapia');
      if (tilapiaOrigin?.default_version_id) {
        setFormData(prev => ({
          ...prev,
          fcr_version_id: tilapiaOrigin.default_version_id
        }));
      }
    } else if (fcrOrigins.length > 0 && formData.species === 'Seabass' && !formData.fcr_version_id) {
      const seabassOrigin = fcrOrigins.find(o => (o.code || '').toLowerCase() === 'seabass');
      if (seabassOrigin?.default_version_id) {
        setFormData(prev => ({
          ...prev,
          fcr_version_id: seabassOrigin.default_version_id
        }));
      }
    }
  }, [formData.species, fcrOrigins]);

  // Load a record into the form
  const loadRecord = (record) => {
    setFormData({
      species: record.species || 'Seabream',
      region: record.region || '',
      genetic_origin: record.genetic_origin || 'Greek',
      arrival_average_weight_g: record.arrival_average_weight_g?.toString() || '',
      arrival_date: record.arrival_date || '',
      temperature_region: record.temperature_region || '',
      unit: record.unit || '',
      batch_code: record.batch_code || '',
      starting_fish_number: record.starting_fish_number?.toString() || '',
      average_mortality_percentage: record.average_mortality_percentage?.toString() || '',
      model_adjustment_percent: record.model_adjustment_percent?.toString() || '100',
    });

    // Load samples
    const newSamples = weightSamples.map((s, idx) => {
      const savedSample = record.samples?.find(rs => rs.sample_number === idx + 1);
      if (savedSample) {
        return {
          ...s,
          sample_date: savedSample.sample_date || '',
          actual_weight_g: savedSample.actual_weight_g?.toString() || ''
        };
      }
      return { ...s, sample_date: '', actual_weight_g: '' };
    });
    setWeightSamples(newSamples);

    setActiveRecordId(record.id);
    setLoadedCurated(null);  // loading a saved record from the panel resets the curated context
    setResult(null);
    setIsRecordClosedRaw(!!record.cage_closed || !!record.is_closed);
    // Restore the user-reported biological FCR captured under the
    // "Enter Your Biological FCR" comparison input. Stored as a number on
    // the record; the input is a string-controlled <input type=number>.
    setUserReportedFCR(
      record.user_reported_fcr !== null && record.user_reported_fcr !== undefined
        ? record.user_reported_fcr.toString()
        : ''
    );
    setShowRecords(false);
    toast.success(`Loaded record: ${record.unit} - ${record.batch_code}`);
  };

  // Try-with-Example handler — pours the curated preset into the form
  // (farm info + sample rows) and immediately submits so the chart renders
  // with all three lines (Model / Actual / Adjusted Best-Fit).
  const loadExampleAndRun = (preset) => {
    if (isReadOnly) return;
    const inputs = preset?.inputs || {};
    const sampleArr = Array.isArray(inputs.weight_samples) ? inputs.weight_samples : [];

    // Build the FULL reset patch from the curated example. Previously only
    // 7 fields were in the patch but the curated extractor emits 11+
    // (fcr_version_id, model_source, custom_model_id,
    //  average_mortality_percentage, …). The spread over `prev` then kept
    // the prior species' FCR version UUID / model source, silently running
    // the new species against the wrong FCR table — visible to users as
    // "old data leaking into Try Sea Bass after Try Seabream".
    const examplePatch = {
      species: inputs.species || 'Seabass',
      genetic_origin: inputs.genetic_origin ?? '',
      fcr_version_id: inputs.fcr_version_id ?? '',
      model_source: inputs.model_source || 'TPS',
      custom_model_id: inputs.custom_model_id ?? '',
      temperature_region: inputs.temperature_region ?? '',
      arrival_date: inputs.arrival_date ?? '',
      arrival_average_weight_g: inputs.arrival_average_weight_g != null ? String(inputs.arrival_average_weight_g) : '',
      starting_fish_number: inputs.starting_fish_number != null ? String(inputs.starting_fish_number) : '',
      average_mortality_percentage: inputs.average_mortality_percentage != null ? String(inputs.average_mortality_percentage) : '',
      model_adjustment_percent: inputs.model_adjustment_percent != null ? String(inputs.model_adjustment_percent) : '100',
    };

    setFormData((prev) => ({ ...prev, ...examplePatch }));

    if (sampleArr.length > 0) {
      setWeightSamples((prevRows) =>
        prevRows.map((row, idx) => {
          const match = sampleArr.find((s) => Number(s.sample_number) === idx + 1);
          if (match) {
            return {
              ...row,
              sample_date: match.sample_date || '',
              actual_weight_g: match.actual_weight_g != null ? String(match.actual_weight_g) : '',
            };
          }
          return { ...row, sample_date: '', actual_weight_g: '' };
        })
      );
    }

    toast.success(`Loaded ${preset.species_label} example — running analysis…`);
    // Flag this submit as a curated example run so the POST goes out with
    // `?example=1` and the backend skips credit billing.
    exampleRunRef.current = true;
    // Pin the curated context so the admin "Update Try-X Example" CTA can
    // render. We mirror the source record id into activeRecordId so the
    // existing "Save" flow naturally targets the same record (admin
    // amendments propagate via the inline-update endpoint, not this state).
    if (preset?.source_record_id) {
      setActiveRecordId(preset.source_record_id);
      setLoadedCurated({
        module: 'growth_model',
        species: preset.species_label,
        source_record_id: preset.source_record_id,
        current_name: preset.source_record_name || `Try-${preset.species_label} Example`,
      });
    }
    // Re-apply the example patch after the species-defaults hook has had a
    // chance to run (it would otherwise clobber temperature_region with the
    // user's per-species default), then submit on the next tick.
    setTimeout(() => {
      setFormData((prev) => ({ ...prev, ...examplePatch }));
      setTimeout(() => {
        formRef.current?.requestSubmit?.();
      }, 60);
    }, 80);
    // ★ Examples always render with guardrails OFF — they're meant to
    //   showcase every signal the platform can produce. Users still see
    //   the toggle and can switch back to Standard Mode at any time.
    setShowAllDataOverride(true);
  };

  // Save current form as a record
  const saveRecord = async (replaceExisting = false) => {
    const authToken = token; // from useAuth
    if (!token) {
      toast.error('Please log in to save records');
      return;
    }

    // Validate required fields for saving
    if (!formData.unit || !formData.batch_code) {
      toast.error('Unit Name and Batch Code are required to save a record');
      return;
    }

    const validSamples = weightSamples
      .filter(s => s.sample_date && s.actual_weight_g)
      .map(s => ({
        sample_number: s.sample_number,
        sample_date: s.sample_date,
        actual_weight_g: parseFloat(s.actual_weight_g)
      }));

    const recordData = {
      unit: formData.unit,
      batch_code: formData.batch_code,
      species: formData.species,
      genetic_origin: formData.genetic_origin,
      temperature_region: formData.temperature_region,
      arrival_date: formData.arrival_date,
      arrival_average_weight_g: parseFloat(formData.arrival_average_weight_g) || 0,
      starting_fish_number: formData.starting_fish_number ? parseInt(formData.starting_fish_number) : null,
      average_mortality_percentage: formData.average_mortality_percentage ? parseFloat(formData.average_mortality_percentage) : null,
      model_adjustment_percent: parseFloat(formData.model_adjustment_percent) || 100,
      region: formData.region || null,
      samples: validSamples,
      // Persist the comparison-panel "Enter Your Biological FCR" input so
      // the value survives reload. Empty string → null (explicit clear).
      user_reported_fcr: userReportedFCR === '' || userReportedFCR === null || userReportedFCR === undefined
        ? null
        : (Number.isFinite(parseFloat(userReportedFCR)) ? parseFloat(userReportedFCR) : null)
    };

    try {
      // Check for duplicate if not replacing
      if (!replaceExisting && !activeRecordId) {
        const checkResponse = await axios.get(
          `${BACKEND_URL}/api/growth-records/check-duplicate?unit=${encodeURIComponent(formData.unit)}&batch_code=${encodeURIComponent(formData.batch_code)}`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );

        if (checkResponse.data.exists) {
          setPendingSaveData(recordData);
          setShowReplaceDialog(true);
          return;
        }
      }

      let response;
      if (activeRecordId) {
        // Update existing record
        response = await axios.put(
          `${BACKEND_URL}/api/growth-records/${activeRecordId}`,
          recordData,
          { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }
        );
        toast.success('Record updated successfully');
      } else {
        // Create new record
        response = await axios.post(
          `${BACKEND_URL}/api/growth-records?replace_existing=${replaceExisting}`,
          recordData,
          { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }
        );
        setActiveRecordId(response.data.id);
        toast.success(replaceExisting ? 'Record replaced successfully' : 'Record saved successfully');
      }

      setShowReplaceDialog(false);
      setPendingSaveData(null);
      fetchRecords();
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to save record';
      toast.error(errorMsg);
    }
  };

  // Toggle cage closed status
  const toggleCageClosed = async (recordId) => {
    const authToken = token; // from useAuth
    try {
      await axios.patch(
        `${BACKEND_URL}/api/growth-records/${recordId}/toggle-close`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      toast.success('Cage status updated');
      fetchRecords();
    } catch (err) {
      toast.error('Failed to update cage status');
    }
  };

  // Delete a single record
  const deleteRecord = async (recordId) => {
    const authToken = token; // from useAuth
    try {
      await axios.delete(
        `${BACKEND_URL}/api/growth-records/${recordId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      toast.success('Record deleted');
      if (activeRecordId === recordId) {
        setActiveRecordId(null);
        clearForm();
      }
      setSelectedGrowthRecordIds(prev => { const n = new Set(prev); n.delete(recordId); return n; });
      fetchRecords();
    } catch (err) {
      toast.error('Failed to delete record');
    }
  };

  const renameGrowthRecord = async (recordId, updates) => {
    const authToken = token; // from useAuth
    await axios.patch(
      `${BACKEND_URL}/api/growth-records/${recordId}/rename`,
      updates,
      { headers: { 'Authorization': `Bearer ${token}` } }
    );
    toast.success('Record renamed');
    fetchRecords();
  };

  // Delete selected records (batch)
  const deleteSelectedGrowthRecords = async () => {
    if (selectedGrowthRecordIds.size === 0) return;
    const authToken = token; // from useAuth
    let deleted = 0;
    for (const id of selectedGrowthRecordIds) {
      try {
        await axios.delete(
          `${BACKEND_URL}/api/growth-records/${id}`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        deleted++;
        if (activeRecordId === id) { setActiveRecordId(null); clearForm(); }
      } catch { /* skip */ }
    }
    toast.success(`Deleted ${deleted} record${deleted !== 1 ? 's' : ''}`);
    setSelectedGrowthRecordIds(new Set());
    fetchRecords();
  };

  // Delete all records
  const deleteAllRecords = async () => {
    if (deleteAllConfirmText !== 'DELETE ALL') {
      toast.error('Please type "DELETE ALL" to confirm');
      return;
    }

    const authToken = token; // from useAuth
    try {
      const response = await axios.delete(
        `${BACKEND_URL}/api/growth-records?confirm=true`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      setShowDeleteAllDialog(false);
      setDeleteAllConfirmText('');
      setActiveRecordId(null);
      fetchRecords();
    } catch (err) {
      toast.error('Failed to delete all records');
    }
  };

  // Clear form
  const clearForm = () => {
    setFormData({
      species: 'Seabream',
      region: '',
      genetic_origin: 'Greek',
      arrival_average_weight_g: '',
      arrival_date: '',
      temperature_region: '',
      unit: '',
      batch_code: '',
      starting_fish_number: '',
      average_mortality_percentage: '',
      model_adjustment_percent: '100',
    });
    setWeightSamples([
      { sample_number: 1, sample_date: '', actual_weight_g: '', label: 'Sample 1 (Required)', required: true },
      { sample_number: 2, sample_date: '', actual_weight_g: '', label: 'Sample 2 (Optional)', required: false },
      { sample_number: 3, sample_date: '', actual_weight_g: '', label: 'Sample 3 (Optional)', required: false },
      { sample_number: 4, sample_date: '', actual_weight_g: '', label: 'Sample 4 (Optional)', required: false },
      { sample_number: 5, sample_date: '', actual_weight_g: '', label: 'Sample 5 (Optional)', required: false },
      { sample_number: 6, sample_date: '', actual_weight_g: '', label: 'Sample 6 (Optional)', required: false },
      { sample_number: 7, sample_date: '', actual_weight_g: '', label: 'Harvest (Optional)', required: false },
    ]);
    setActiveRecordId(null);
    setResult(null);
    setError('');
  };


  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
    setResult(null);
  };

  const handleSampleChange = (index, field, value) => {
    setWeightSamples(prev => prev.map((sample, i) => 
      i === index ? { ...sample, [field]: value } : sample
    ));
    setError('');
    setResult(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setResult(null);

    try {
      const authToken = token; // from useAuth
      if (!token) {
        navigate('/auth');
        return;
      }

      // Filter samples that have both date and weight
      const validSamples = weightSamples.filter(s => s.sample_date && s.actual_weight_g);
      
      // Validate at least one sample
      if (validSamples.length === 0) {
        throw new Error('Please provide at least one sample with date and weight');
      }

      // Validate starting fish number is provided
      if (!formData.starting_fish_number || parseInt(formData.starting_fish_number) <= 0) {
        throw new Error('Starting Fish Number is required');
      }

      // Validate genetic origin only for Seabream (Tilapia / Seabass / Tripletail have no strain)
      if (formData.species !== 'Tilapia' && formData.species !== 'Seabass' && formData.species !== 'Tripletail' && !formData.genetic_origin) {
        throw new Error('Please select a genetic origin');
      }

      const payload = {
        species: formData.species,
        genetic_origin: (formData.species === 'Tilapia' || formData.species === 'Seabass')
          ? null
          : formData.genetic_origin,  // Tilapia / Seabass have no genetic origin
        arrival_average_weight_g: parseFloat(formData.arrival_average_weight_g),
        arrival_date: formData.arrival_date,
        temperature_region: formData.species === 'Tilapia' ? 'Tilapia-Custom' : formData.species === 'Tripletail' ? 'N/A' : formData.temperature_region,  // REQUIRED for Seabream / Seabass SGR model
        weight_samples: validSamples.map(s => ({
          sample_number: s.sample_number,
          sample_date: s.sample_date,
          actual_weight_g: parseFloat(s.actual_weight_g)
        })),
        model_adjustment_percent: parseFloat(formData.model_adjustment_percent || 100)
      };
      
      // Phase B: Tilapia uses the static time-driven model. No monthly
      // temperatures are required/sent. The backend bypasses the SGR path.
      // Static-model floor/ceiling are enforced server-side (20g / 1,402g).
      
      // Add optional fields only if they have values
      if (formData.region) payload.region = formData.region;
      if (formData.unit) payload.unit = formData.unit;
      if (formData.starting_fish_number) payload.starting_fish_number = parseInt(formData.starting_fish_number);
      if (formData.average_mortality_percentage) payload.average_mortality_percentage = parseFloat(formData.average_mortality_percentage);

      const isExampleRun = exampleRunRef.current;
      exampleRunRef.current = false;

      const response = await axios.post(
        `${BACKEND_URL}/api/analysis/growth-model${isExampleRun ? '?example=1' : ''}`,
        payload,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setResult(response.data);
      toast.success('Growth analysis completed!');
      // Reset missing fish intelligence when new analysis is run
      setMissingFishIntelligence(null);
      setShowMissingFishIntelligence(false);
      setExcludedSamples(new Set());
      
      // Auto-save trajectories to active record for future comparison use
      if (activeRecordId && response.data.model_trajectory && response.data.actual_growth_trajectory) {
        try {
          await axios.patch(
            `${BACKEND_URL}/api/growth-records/${activeRecordId}/trajectories`,
            {
              model_trajectory: response.data.model_trajectory,
              actual_growth_trajectory: response.data.actual_growth_trajectory,
            },
            { headers: { 'Authorization': `Bearer ${token}` } }
          );
        } catch { /* silently ignore trajectory save errors */ }
      }

      // Run Sample Jump Detection via the shared intelligence engine
      const intel = generateMissingFishIntelligence({ growthModelResult: response.data, detectionMethod });
      if (intel?.trajectoryJump) {
        setTrajectoryJump(intel.trajectoryJump);
      } else {
        setTrajectoryJump(null);
      }
      
      // Initialize assumed fish number from method resolution
      const fishN = response.data.starting_fish_number;
      if (intel?.initialAssumedFishNumber) {
        setAssumedFishNumber(intel.initialAssumedFishNumber);
      } else if (fishN) {
        setAssumedFishNumber(fishN);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || 'Analysis failed';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Run Missing Fish Intelligence Analysis (shared engine — same as Spreadsheet page)
  const runMissingFishIntelligence = async () => {
    if (isRecordClosed) {
      toast.error('Missing fish estimation not applicable for closed cages');
      return;
    }

    if (!result || !result.comparisons || result.comparisons.length === 0) {
      toast.error('No sample data available for analysis');
      return;
    }

    // Fetch weight-based model FCR from backend (same method as Snapshot Analysis)
    let weightBasedModelFCR = null;
    try {
      const lastComp = result.comparisons[result.comparisons.length - 1];
      const lastWeight = parseFloat(lastComp.actual_weight_g);
      const authToken = token; // from useAuth
      const resp = await axios.get(`${BACKEND_URL}/api/model-fcr`, {
        params: { 
          species: formData.species, 
          weight_g: lastWeight, 
          genetic_origin: formData.genetic_origin,
          fcr_version_id: formData.fcr_version_id || undefined
        },
        headers: { 'Authorization': `Bearer ${token}` },
      });
      weightBasedModelFCR = resp.data.model_fcr;
    } catch (err) {
      // Model FCR lookup failed — FCR-based detection will be skipped
    }

    const intel = generateMissingFishIntelligence({ growthModelResult: result, weightBasedModelFCR, detectionMethod, excludedIndices: excludedSamples, modelScalingFactor: calculatedBestFitAdjustment?.factor || 1 });

    if (!intel) {
      toast.error('Unable to generate intelligence from available data');
      return;
    }

    // Populate state from the unified result object
    setMissingFishIntelligence(intel);
    setShowMissingFishIntelligence(true);
    setGraphInsight(intel.graphInsight);
    setAssumedFishNumber(intel.initialAssumedFishNumber);
    // Update trajectory jump if intelligence produced better data
    if (intel.trajectoryJump) {
      setTrajectoryJump(intel.trajectoryJump);
    }

    // ★ Publish the active SSOT to the global bridge so WingmanChat /
    //   WingmanTriggerPrompts can read it without prop-drilling. Schema
    //   matches the shared trigger-prompt spec (Feb 2026).
    if (typeof window !== 'undefined') {
      const _ssot = intel?.ssot || {};
      const _allRecords = [...(records.ongoing || []), ...(records.closed || [])];
      const _activeRecord = _allRecords.find((r) => r.id === activeRecordId) || null;
      const _hasMissingSignal = !!_ssot.hasMissingFish
        && _ssot.missingFishRangeLabel && _ssot.missingFishRangeLabel !== 'Within margin';
      const _detectionType = _hasMissingSignal
        ? (_ssot.primaryMethod === 'jump' ? 'jump'
           : _ssot.primaryMethod === 'fcr' ? 'fcr'
           : 'none')
        : 'none';
      const _hasAnomaly = !!(
        _ssot.hasSuspectedError
        || (_ssot.fcrOutliers && _ssot.fcrOutliers.count > 0)
        || (intel?.trajectoryJump?.suspect_samples?.length > 0)
      );
      window.__pfd_active_record_ssot = {
        record_name: _activeRecord?.name || formData?.unit || null,
        species: _activeRecord?.species || formData?.species || null,
        confidenceLabel: _ssot.confidenceLabel || null,
        actionability: _ssot.actionability || null,
        missingFishRangeLabel: _ssot.missingFishRangeLabel || null,
        feedingState: _ssot.feedingState || null,
        hasConflict: !!_ssot.hasConflict,
        detectionType: _detectionType,
        hasAnomaly: _hasAnomaly,
        hasMissingFishSignal: _hasMissingSignal,
        moduleName: 'Growth vs Model',
      };
    }

    toast.success('Missing Fish Intelligence analysis complete');
    
    // Save MFI result to record history for progression tracking
    if (activeRecordId && token) {
      saveMfiToHistory(token, 'growth', activeRecordId, {
        estimated_missing: intel.ssot?.missingFishCount || 0,
        deviation_percent: intel.ssot?.deviationPercent || null,
        detection_strength: intel.ssot?.detectionStrength || 'Low',
        confidence_mode: intel.ssot?.confidenceMode || 'signal',
        actionability: intel.ssot?.actionability || intel.decisionContext?.decisionClass || null,
        interpretation: intel.ssot?.keyInsight || '',
        has_missing_fish: intel.ssot?.hasMissingFish || false,
        sample_count: result?.comparisons?.length || 0,
      });
    }
  };

  // Check if there are sample weights to analyze
  const hasSampleWeights = result && result.comparisons && result.comparisons.length > 0;

  // Compute suspect sample comparison indices from trajectory jump suspect_samples
  const suspectComparisonIndices = useMemo(() => {
    const indices = new Set();
    if (trajectoryJump?.suspect_samples?.length > 0 && result?.comparisons) {
      for (const ss of trajectoryJump.suspect_samples) {
        // Match by sampleIndex (the index within comparisons array)
        // suspect_samples[].sampleIndex is the index within the samplePoints array used
        // by detectConfirmedSampleJump, which maps to comparisons[sampleIndex - 0]
        // since comparisons excludes the arrival point. The suspect sampleIndex is from
        // the samplePoints built by buildSamplePointsFromGrowthModel which directly
        // maps to comparisons[idx].
        if (ss.sampleIndex != null) {
          indices.add(ss.sampleIndex);
        }
      }
    }
    return indices;
  }, [trajectoryJump, result]);

  // Toggle a sample exclusion and re-run intelligence
  const toggleSampleExclusion = useCallback((compIdx) => {
    setExcludedSamples(prev => {
      const next = new Set(prev);
      if (next.has(compIdx)) {
        next.delete(compIdx);
      } else {
        next.add(compIdx);
      }
      return next;
    });
  }, []);

  // Re-run intelligence when excludedSamples changes (only if intelligence is already showing)
  useEffect(() => {
    if (!showMissingFishIntelligence || !result?.comparisons?.length) return;
    const rerun = async () => {
      let wModelFCR = null;
      try {
        const lastComp = result.comparisons[result.comparisons.length - 1];
        const lastWeight = parseFloat(lastComp.actual_weight_g);
        const authToken = token; // from useAuth
        const resp = await axios.get(`${BACKEND_URL}/api/model-fcr`, {
          params: { 
            species: formData.species, 
            weight_g: lastWeight, 
            genetic_origin: formData.genetic_origin,
            fcr_version_id: formData.fcr_version_id || undefined
          },
          headers: { 'Authorization': `Bearer ${token}` },
        });
        wModelFCR = resp.data.model_fcr;
      } catch { /* ignore */ }
      const reIntel = generateMissingFishIntelligence({
        growthModelResult: result,
        weightBasedModelFCR: wModelFCR,
        detectionMethod,
        excludedIndices: excludedSamples,
        modelScalingFactor: calculatedBestFitAdjustment?.factor || 1,
      });
      if (reIntel) {
        setMissingFishIntelligence(reIntel);
        setGraphInsight(reIntel.graphInsight);
        if (reIntel.trajectoryJump) setTrajectoryJump(reIntel.trajectoryJump);
      }
    };
    rerun();
  }, [excludedSamples]); // Only re-run when exclusions change

  // Export to PDF
  const handleExportPdf = async () => {
    if (!result) {
      toast.error('No analysis results to export');
      return;
    }

    setExportingPdf(true);
    toast.info('Generating PDF report...');
    
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 12;
      let yPos = margin;

      // ===== PAGE 1: HEADER =====
      pdf.setFillColor(8, 145, 178);
      pdf.rect(0, 0, pageWidth, 28, 'F');
      
      pdf.setFontSize(18);
      pdf.setTextColor(255, 255, 255);
      pdf.text('Growth vs Model Analysis Report', pageWidth / 2, 12, { align: 'center' });
      
      pdf.setFontSize(9);
      pdf.text('Generated: ' + new Date().toLocaleString(), pageWidth / 2, 20, { align: 'center' });
      
      yPos = 35;

      // ===== INTERPRETATION SECTION =====
      const interpretationText = result.interpretation || 'No interpretation available.';
      pdf.setFillColor(239, 246, 255);
      pdf.setDrawColor(191, 219, 254);
      
      const interpretationLines = pdf.splitTextToSize(interpretationText, pageWidth - (margin * 2) - 10);
      const interpretationBoxHeight = Math.max(18, 10 + (interpretationLines.length * 4));
      
      pdf.rect(margin, yPos, pageWidth - (margin * 2), interpretationBoxHeight, 'FD');
      
      pdf.setFontSize(9);
      pdf.setTextColor(30, 64, 175);
      pdf.setFont(undefined, 'bold');
      pdf.text('Interpretation', margin + 4, yPos + 6);
      
      pdf.setFont(undefined, 'normal');
      pdf.setFontSize(8);
      pdf.setTextColor(55, 65, 81);
      pdf.text(interpretationLines, margin + 4, yPos + 12);
      
      yPos += interpretationBoxHeight + 8;

      // ===== GROWTH COMPARISON CURVE (MANDATORY) =====
      // Desktop: Native capture (unchanged, confirmed working)
      // Mobile PDF Export Fix for "Growth vs Model" chart ONLY
      // 
      // ROOT CAUSE OF RECURRENCE:
      // When forcing width changes via DOM manipulation, Recharts ResponsiveContainer
      // recalculates SVG viewBox but Line paths and dot positions may render with
      // different coordinate transforms (lines render first, dots render async).
      // This causes line-to-dot mismatch in the captured canvas.
      //
      // FIX: For mobile, wait longer after resize to ensure full SVG re-render,
      // and validate that the chart uses the same data series for lines and dots.
      // Width multiplier: 1.33x (+33%) applied only for mobile PDF export.
      
      // Validate config parity before export (dev mode only)
      if (process.env.NODE_ENV === 'development') {
        validateConfigParity();
      }
      
      if (pdfExportRef.current) {
        const chartElement = pdfExportRef.current;
        const isMobile = window.innerWidth < 768;
        
        // Hide interactive UI controls from PDF capture
        const pdfHideElements = chartElement.querySelectorAll('[data-pdf-hide]');
        const hiddenOriginals = [];
        pdfHideElements.forEach(el => {
          hiddenOriginals.push({ el, display: el.style.display });
          el.style.display = 'none';
        });
        
        // ============================================================
        // MOBILE PDF EXPORT FIX - "Growth vs Model" chart ONLY
        // Problem: Line series not matching dots (different coordinate transforms)
        // Fix: Width increase +33% (1.33x), extended re-render wait, parity validation
        // Branch point: isMobile check below
        // ============================================================
        
        let canvas;
        
        if (isMobile) {
          // MOBILE-ONLY PATH
          const chartContainer = chartElement.querySelector('[data-chart-container="true"]');
          
          // Store original dimensions for restoration
          const originalStyles = {
            elementWidth: chartElement.style.width,
            elementMinWidth: chartElement.style.minWidth,
            elementMaxWidth: chartElement.style.maxWidth,
            containerHeight: chartContainer?.style.height || ''
          };
          
          // ========== WIDTH MULTIPLIER: 2.5x (+150%) ==========
          const MOBILE_WIDTH_MULTIPLIER = 2.5;  // +150% width increase (+25% from previous 2.0x)
          const currentWidth = chartElement.offsetWidth;
          const targetWidth = Math.round(currentWidth * MOBILE_WIDTH_MULTIPLIER);
          const currentHeight = chartContainer?.offsetHeight || 280;
          
          // Force wider width, keep height UNCHANGED
          chartElement.style.width = `${targetWidth}px`;
          chartElement.style.minWidth = `${targetWidth}px`;
          chartElement.style.maxWidth = `${targetWidth}px`;
          
          // Preserve exact height
          if (chartContainer) {
            chartContainer.style.height = `${currentHeight}px`;
          }
          
          // ========== FORCE COMPLETE SVG RE-RENDER ==========
          // Issue: Lines and dots render with different coordinate transforms
          // Fix: Multiple resize events with extended waits to force full re-render
          
          // First resize - triggers ResponsiveContainer dimension recalc
          window.dispatchEvent(new Event('resize'));
          
          // Wait for first render pass
          await new Promise(resolve => setTimeout(resolve, 100));
          
          // Second resize - ensures all SVG elements recalculate
          window.dispatchEvent(new Event('resize'));
          
          // Extended wait for SVG path AND dot position calculations
          await new Promise(resolve => {
            let frameCount = 0;
            const waitForFrames = () => {
              frameCount++;
              if (frameCount < 20) {  // Wait 20 animation frames
                requestAnimationFrame(waitForFrames);
              } else {
                // Additional delay for SVG finalization
                setTimeout(resolve, 500);
              }
            };
            requestAnimationFrame(waitForFrames);
          });
          
          // Third resize - final sync to ensure parity
          window.dispatchEvent(new Event('resize'));
          await new Promise(resolve => setTimeout(resolve, 200));
          
          // ========== PARITY VALIDATION (dev mode) ==========
          if (process.env.NODE_ENV === 'development') {
            const svgElement = chartElement.querySelector('svg');
            if (svgElement) {
              const lines = svgElement.querySelectorAll('path.recharts-line-curve');
              const dots = svgElement.querySelectorAll('circle.recharts-dot');
            }
          }
          
          try {
            // Capture with same resolution (scale:3)
            canvas = await html2canvas(chartElement, CHART_EXPORT_CONFIG);
          } finally {
            // ALWAYS restore original styles
            chartElement.style.width = originalStyles.elementWidth;
            chartElement.style.minWidth = originalStyles.elementMinWidth;
            chartElement.style.maxWidth = originalStyles.elementMaxWidth;
            if (chartContainer) {
              chartContainer.style.height = originalStyles.containerHeight;
            }
            // Restore mobile layout
            window.dispatchEvent(new Event('resize'));
          }
        } else {
          // DESKTOP PATH: Native capture (unchanged, confirmed working)
          canvas = await html2canvas(chartElement, CHART_EXPORT_CONFIG);
        }
        
        // Restore hidden PDF elements
        hiddenOriginals.forEach(({ el, display }) => {
          el.style.display = display;
        });
        
        try {
          const imgData = canvas.toDataURL('image/png', 1.0);
          const contentWidth = pageWidth - (margin * 2);
          const aspectRatio = canvas.height / canvas.width;
          
          // Calculate dimensions maintaining aspect ratio
          let finalWidth = contentWidth;
          let finalHeight = contentWidth * aspectRatio;
          
          // Cap height but maintain proper aspect ratio
          if (finalHeight > CHART_EXPORT_CONFIG.maxChartHeightMm) {
            finalHeight = CHART_EXPORT_CONFIG.maxChartHeightMm;
            finalWidth = finalHeight / aspectRatio;
          }
          
          // Check if chart fits on current page
          if (yPos + finalHeight > pageHeight - margin - 10 || yPos > pageHeight - 70) {
            pdf.addPage();
            yPos = margin;
          }
          
          // Center chart horizontally if width is less than content width
          const chartX = margin + (contentWidth - finalWidth) / 2;
          
          // Draw chart title
          pdf.setFontSize(10);
          pdf.setTextColor(8, 145, 178);
          pdf.setFont(undefined, 'bold');
          pdf.text('Growth Comparison Curve', margin, yPos);
          yPos += 6;
          
          // Draw border around chart
          pdf.setDrawColor(200, 200, 200);
          pdf.setLineWidth(0.3);
          pdf.rect(chartX - 1, yPos - 1, finalWidth + 2, finalHeight + 2, 'S');
          
          // Add chart image
          pdf.addImage(imgData, 'PNG', chartX, yPos, finalWidth, finalHeight);
          yPos += finalHeight + 8;
          
          // Chart legend
          pdf.setFontSize(7);
          pdf.setFont(undefined, 'normal');
          pdf.setTextColor(100, 100, 100);
          pdf.text('Blue Line: Model Growth | Red Line: Actual Growth (SGR Extrapolated) | Markers: Sample Points', margin, yPos);
          yPos += 8;
          
        } catch (chartError) {
          // Chart capture failed
          pdf.setFontSize(9);
          pdf.setTextColor(150, 150, 150);
          pdf.text('(Growth curve visualization could not be captured)', margin, yPos);
          yPos += 10;
        }
      }

      // ===== SAMPLE COMPARISONS TABLE =====
      if (result.comparisons && Array.isArray(result.comparisons) && result.comparisons.length > 0) {
        if (yPos > pageHeight - 50) {
          pdf.addPage();
          yPos = margin;
        }

        pdf.setFontSize(10);
        pdf.setTextColor(8, 145, 178);
        pdf.setFont(undefined, 'bold');
        pdf.text('Sample Comparisons', margin, yPos);
        yPos += 6;

        const tableWidth = pageWidth - (margin * 2);
        const colWidths = [18, 28, 22, 28, 28, 25, 30];
        const headers = ['#', 'Date', 'Days', 'Actual (g)', 'Model (g)', 'Diff (%)', 'Status'];
        const rowHeight = 6;
        
        // Table header
        pdf.setFillColor(8, 145, 178);
        pdf.rect(margin, yPos, tableWidth, rowHeight, 'F');
        
        pdf.setFontSize(7);
        pdf.setTextColor(255, 255, 255);
        pdf.setFont(undefined, 'bold');
        
        let xPos = margin + 2;
        for (let i = 0; i < headers.length; i++) {
          pdf.text(headers[i], xPos, yPos + 4);
          xPos += colWidths[i];
        }
        yPos += rowHeight;

        // Table rows
        pdf.setFont(undefined, 'normal');
        for (let index = 0; index < result.comparisons.length; index++) {
          const comp = result.comparisons[index];
          if (!comp) continue;
          
          if (yPos > pageHeight - 15) {
            pdf.addPage();
            yPos = margin;
          }
          
          if (index % 2 === 0) {
            pdf.setFillColor(248, 250, 252);
            pdf.rect(margin, yPos, tableWidth, rowHeight, 'F');
          }
          
          pdf.setTextColor(55, 65, 81);
          xPos = margin + 2;
          
          pdf.text('S' + (comp.sample_number || index + 1), xPos, yPos + 4);
          xPos += colWidths[0];
          
          pdf.text(String(comp.sample_date || '-'), xPos, yPos + 4);
          xPos += colWidths[1];
          
          pdf.text(String(comp.days_since_arrival || 0), xPos, yPos + 4);
          xPos += colWidths[2];
          
          pdf.text(String(comp.actual_weight_g || 0), xPos, yPos + 4);
          xPos += colWidths[3];
          
          // Use adjusted model weight when best-fit adjustment is active
          const pdfAdjFactor = calculatedBestFitAdjustment?.factor || 1;
          const pdfModelWeight = pdfAdjFactor !== 1
            ? Math.round(comp.model_weight_g * pdfAdjFactor * 10) / 10
            : comp.model_weight_g;
          pdf.text(String(pdfModelWeight || 0), xPos, yPos + 4);
          xPos += colWidths[4];
          
          const diffPercent = pdfAdjFactor !== 1
            ? Math.round(((comp.actual_weight_g - pdfModelWeight) / pdfModelWeight) * 100 * 10) / 10
            : (comp.difference_percentage || 0);
          if (diffPercent >= 0) {
            pdf.setTextColor(22, 163, 74);
          } else {
            pdf.setTextColor(220, 38, 38);
          }
          pdf.text((diffPercent > 0 ? '+' : '') + diffPercent + '%', xPos, yPos + 4);
          xPos += colWidths[5];
          
          // Status indicator
          pdf.setTextColor(55, 65, 81);
          if (diffPercent > 5) {
            pdf.text('Above Model', xPos, yPos + 4);
          } else if (diffPercent < -5) {
            pdf.text('Below Model', xPos, yPos + 4);
          } else {
            pdf.text('On Track', xPos, yPos + 4);
          }
          
          yPos += rowHeight;
        }
        yPos += 8;
      }

      // ===== MISSING FISH INTELLIGENCE (CONDITIONAL - Only if run) =====
      if (showMissingFishIntelligence && missingFishIntelligence && mfiPdfRef.current) {
        pdf.addPage();
        yPos = margin;

        // Section header
        pdf.setFillColor(147, 51, 234);
        pdf.rect(margin, yPos, pageWidth - (margin * 2), 8, 'F');
        pdf.setFontSize(10);
        pdf.setTextColor(255, 255, 255);
        pdf.setFont(undefined, 'bold');
        pdf.text('FCR Signal Analysis', margin + 4, yPos + 5.5);
        yPos += 12;

        // Capture each top-level section individually to avoid splitting components
        const mfiElement = mfiPdfRef.current;
        const mfiOriginalBg = mfiElement.style.backgroundColor;
        mfiElement.style.backgroundColor = '#ffffff';

        const sections = mfiElement.querySelectorAll(':scope > div, :scope > table');
        const contentWidth = pageWidth - (margin * 2);

        for (const section of sections) {
          if (section.offsetHeight < 5) continue; // skip invisible
          try {
            const sectionCanvas = await html2canvas(section, {
              scale: 2,
              useCORS: true,
              logging: false,
              backgroundColor: '#ffffff',
              scrollX: 0,
              scrollY: -window.scrollY,
              width: Math.max(1100, section.offsetWidth),
            });

            const imgData = sectionCanvas.toDataURL('image/png');
            const aspect = sectionCanvas.height / sectionCanvas.width;
            const imgHeight = contentWidth * aspect;

            // If section doesn't fit on current page, start new page
            if (yPos + imgHeight > pageHeight - margin) {
              pdf.addPage();
              yPos = margin;
            }

            pdf.addImage(imgData, 'PNG', margin, yPos, contentWidth, imgHeight);
            yPos += imgHeight + 2;
          } catch (e) {
            // MFI section capture failed silently
          }
        }

        mfiElement.style.backgroundColor = mfiOriginalBg;
      }

      // ===== FOOTER ON ALL PAGES =====
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(7);
        pdf.setTextColor(150, 150, 150);
        pdf.text(
          'Precision Fish Detection - Aquaculture Analysis Tool | Page ' + i + ' of ' + totalPages,
          pageWidth / 2,
          pageHeight - 6,
          { align: 'center' }
        );
      }

      // Save PDF - mobile-safe approach
      const fileName = 'growth_vs_model_' + (formData.batch_code || 'analysis') + '_' + new Date().toISOString().split('T')[0] + '.pdf';
      const isMobileSave = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      if (isMobileSave) {
        const blob = pdf.output('blob');
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl, '_blank');
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
      } else {
        pdf.save(fileName);
      }
      toast.success('PDF exported successfully!');
      
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Failed to export PDF: ' + (error.message || 'Unknown error'));
    } finally {
      setExportingPdf(false);
    }
  };

  // ── Biological FCR for Growth vs Model ──
  // Lightweight computation from existing result data
  const growthFCR = useMemo(() => {
    if (!result || !result.daily_feed_data || result.daily_feed_data.length === 0) return null;
    const reportedN = result.starting_fish_number;
    const startingWeightG = result.arrival_average_weight_g;
    if (!reportedN || reportedN <= 0 || !startingWeightG || startingWeightG <= 0) return null;

    const dailyData = result.daily_feed_data;
    const lastDay = dailyData[dailyData.length - 1];
    const totalFeedKg = lastDay.cumulative_feed_kg || dailyData.reduce((sum, d) => sum + (d.feed_kg || d.daily_feed_kg || 0), 0);
    if (totalFeedKg <= 0) return null;

    // Mortality biomass from daily fish_count declines
    let mortalityBiomassKg = 0;
    for (let i = 1; i < dailyData.length; i++) {
      const prevCount = dailyData[i - 1].fish_count || 0;
      const currCount = dailyData[i].fish_count || 0;
      const lost = prevCount - currCount;
      if (lost > 0) {
        const weightG = dailyData[i].average_weight_g || dailyData[i].model_weight_g || startingWeightG;
        mortalityBiomassKg += (lost * weightG) / 1000;
      }
    }

    const reportedRemaining = lastDay.fish_count || 0;
    const lastWeightG = lastDay.average_weight_g || lastDay.model_weight_g || 0;
    // Total deductions from starting count
    const totalDeductions = reportedN - reportedRemaining;

    return { totalFeedKg, startingWeightG, mortalityBiomassKg, reportedN, reportedRemaining, lastWeightG, totalDeductions };
  }, [result]);

  const growthFCRResult = useMemo(() => {
    if (!growthFCR) return null;
    const { totalFeedKg, startingWeightG, mortalityBiomassKg, reportedN, reportedRemaining, lastWeightG, totalDeductions } = growthFCR;
    const adjustedN = assumedFishNumber || reportedN;
    const estimatedMissing = reportedN - adjustedN;

    // Reported
    const reportedStartBiomass = (reportedN * startingWeightG) / 1000;
    const reportedClosingBiomass = (reportedRemaining * lastWeightG) / 1000;
    const reportedBiomassGain = reportedClosingBiomass + mortalityBiomassKg - reportedStartBiomass;
    const reportedFCR = reportedBiomassGain > 0 ? totalFeedKg / reportedBiomassGain : null;

    // Adjusted
    const adjustedStartBiomass = (adjustedN * startingWeightG) / 1000;
    const adjustedRemaining = Math.max(0, adjustedN - totalDeductions);
    const adjustedClosingBiomass = (adjustedRemaining * lastWeightG) / 1000;
    const adjustedBiomassGain = adjustedClosingBiomass + mortalityBiomassKg - adjustedStartBiomass;
    const adjustedFCR = adjustedBiomassGain > 0 ? totalFeedKg / adjustedBiomassGain : null;

    return {
      reported: { startBiomass: reportedStartBiomass, closingBiomass: reportedClosingBiomass, biomassGain: reportedBiomassGain, fcr: reportedFCR, remaining: reportedRemaining },
      adjusted: { startBiomass: adjustedStartBiomass, closingBiomass: adjustedClosingBiomass, biomassGain: adjustedBiomassGain, fcr: adjustedFCR, remaining: adjustedRemaining },
      reportedN,
      adjustedN,
      estimatedMissing,
    };
  }, [growthFCR, assumedFishNumber]);

  // ── Feed Intensity — Reported vs Adjusted (Growth Model) ──
  const growthFeedIntensity = useMemo(() => {
    if (!growthFCR || !result?.daily_feed_data) return null;
    const { totalFeedKg, reportedN, startingWeightG } = growthFCR;
    const adjustedN = assumedFishNumber || reportedN;
    if (reportedN <= 0 || totalFeedKg <= 0) return null;

    const reportedFeedPerFishG = (totalFeedKg * 1000) / reportedN;
    const adjustedFeedPerFishG = adjustedN > 0 ? (totalFeedKg * 1000) / adjustedN : 0;

    const feedRows = result.daily_feed_data.filter(d => (d.feed_kg || d.daily_feed_kg || 0) > 0);
    if (feedRows.length === 0) return null;

    let reportedRateSum = 0, adjustedRateSum = 0;
    for (const row of feedRows) {
      const dailyFeed = row.feed_kg || row.daily_feed_kg || 0;
      const weightG = row.average_weight_g || row.model_weight_g || startingWeightG;
      const rBiomass = (reportedN * weightG) / 1000;
      const aBiomass = adjustedN > 0 ? (adjustedN * weightG) / 1000 : 0;
      if (rBiomass > 0) reportedRateSum += (dailyFeed / rBiomass) * 100;
      if (aBiomass > 0) adjustedRateSum += (dailyFeed / aBiomass) * 100;
    }

    return {
      reportedFeedPerFishG,
      adjustedFeedPerFishG,
      avgReportedFeedRate: reportedRateSum / feedRows.length,
      avgAdjustedFeedRate: adjustedN > 0 ? adjustedRateSum / feedRows.length : 0,
      reportedN,
      adjustedN,
      isAdjusted: adjustedN !== reportedN,
    };
  }, [result, growthFCR, assumedFishNumber]);

  // ═══════════════════════════════════════════════════════════════════════════
  // GLOBAL BEST-FIT ADJUSTED MODEL CALCULATION
  // Calculate ONE adjustment percentage that scales the standard model up/down
  // to minimize total error across valid samples (excluding suspect jump samples).
  // This adjusted model becomes the baseline for jump detection.
  // ═══════════════════════════════════════════════════════════════════════════
  const calculatedBestFitAdjustment = useMemo(() => {
    if (!result || !result.comparisons || result.comparisons.length === 0) return null;
    if (!result.model_trajectory || result.model_trajectory.length === 0) return null;
    
    const originalFishNumber = parseInt(formData.starting_fish_number) || result.original_fish_number || 0;
    if (originalFishNumber <= 0) return null;
    
    // Build model lookup by day
    const modelByDay = {};
    result.model_trajectory.forEach(p => { modelByDay[p.day] = p.weight; });
    
    // Get all samples with their model weights
    const samplesWithModel = result.comparisons
      .map((c, idx) => {
        const modelWeight = modelByDay[c.days_since_arrival];
        return {
          idx,
          day: c.days_since_arrival,
          actual: c.actual_weight_g,
          model: modelWeight || 0,
          ratio: modelWeight > 0 ? c.actual_weight_g / modelWeight : null,
          isExcluded: excludedSamples.has(idx),
        };
      })
      .filter(s => s.model > 0 && !s.isExcluded);
    
    if (samplesWithModel.length < 2) {
      return {
        percent: 0,
        factor: 1,
        method: 'insufficient_data',
        validSampleCount: samplesWithModel.length,
        originalFishNumber,
        suspectJumpIndices: [],
        isManualOverride: manualFishOverride !== null,
        manualPercent: manualFishOverride !== null ? manualFishOverride : null,
        reason: 'Insufficient samples for best-fit calculation (need at least 2 valid samples)',
      };
    }
    
    // ═══════════════════════════════════════════════════════════════════════════
    // STEP 1: Calculate best-fit scaling factor using WEIGHTED least squares
    //
    // Uses model-weight-squared weighting (w_i = model_i²):
    //   k = sum(w_i * actual_i * model_i) / sum(w_i * model_i²)
    //     = sum(model_i³ * actual_i) / sum(model_i⁴)
    //
    // WHY WEIGHTED? With equal weights, the fit is dominated by early small
    // samples (which are noisy and represent historical growth, not current
    // state). Model-weight-squared weighting gives proportionally more influence
    // to larger (later) samples, which:
    //   1. Have more stable growth rates (less biological noise)
    //   2. Represent the current state (relevant for fish count estimation)
    //   3. Cover a larger portion of the total biomass growth
    // ═══════════════════════════════════════════════════════════════════════════
    let sumWeightedActualTimesModel = 0;
    let sumWeightedModelSquared = 0;
    
    samplesWithModel.forEach(s => {
      const weight = s.model * s.model;  // w_i = model_i²
      sumWeightedActualTimesModel += weight * s.actual * s.model;
      sumWeightedModelSquared += weight * s.model * s.model;
    });
    
    let bestFitFactor = sumWeightedModelSquared > 0
      ? sumWeightedActualTimesModel / sumWeightedModelSquared
      : 1;
    
    // If manual override is set, use that instead
    if (manualFishOverride !== null) {
      // Manual override is specified as a percentage adjustment (-50 to +100)
      bestFitFactor = 1 + (manualFishOverride / 100);
    }
    
    // Clamp to reasonable range (30% to 150% of original model)
    bestFitFactor = Math.max(0.3, Math.min(1.5, bestFitFactor));
    
    const bestFitPercent = (bestFitFactor - 1) * 100; // Convert to percentage (positive = above model, negative = below)
    
    // ═══════════════════════════════════════════════════════════════════════════
    // STEP 2: Calculate fit quality (R² and RMSE) using same weights
    // ═══════════════════════════════════════════════════════════════════════════
    let totalSquaredError = 0;
    let totalVariance = 0;
    const weightedMeanActual = samplesWithModel.reduce((sum, s) => sum + s.model * s.model * s.actual, 0)
      / samplesWithModel.reduce((sum, s) => sum + s.model * s.model, 0);
    
    samplesWithModel.forEach(s => {
      const weight = s.model * s.model;
      const predicted = s.model * bestFitFactor;
      totalSquaredError += weight * Math.pow(s.actual - predicted, 2);
      totalVariance += weight * Math.pow(s.actual - weightedMeanActual, 2);
    });
    
    const rSquared = totalVariance > 0 ? 1 - (totalSquaredError / totalVariance) : 0;
    const rmse = Math.sqrt(totalSquaredError / samplesWithModel.reduce((sum, s) => sum + s.model * s.model, 0));
    
    // ═══════════════════════════════════════════════════════════════════════════
    // STEP 3: Generate explanation
    // ═══════════════════════════════════════════════════════════════════════════
    let reason = '';
    if (manualFishOverride !== null) {
      reason = `Manual adjustment of ${bestFitPercent >= 0 ? '+' : ''}${bestFitPercent.toFixed(1)}% applied. All analysis uses this adjusted baseline.`;
    } else if (Math.abs(bestFitPercent) < 2) {
      reason = 'The standard model closely matches your sample data — no significant adjustment needed.';
    } else if (bestFitPercent > 0) {
      reason = `Model adjusted +${bestFitPercent.toFixed(1)}% to best fit your ${samplesWithModel.length} sample points. Actual growth is running above the standard model.`;
    } else {
      reason = `Model adjusted ${bestFitPercent.toFixed(1)}% to best fit your ${samplesWithModel.length} sample points. Actual growth is running below the standard model.`;
    }
    
    return {
      percent: Math.round(bestFitPercent * 10) / 10,
      factor: bestFitFactor,
      method: 'least_squares',
      validSampleCount: samplesWithModel.length,
      totalSampleCount: samplesWithModel.length,
      suspectJumpIndices: [],
      rSquared: Math.round(rSquared * 100) / 100,
      rmse: Math.round(rmse * 10) / 10,
      originalFishNumber,
      isManualOverride: manualFishOverride !== null,
      manualPercent: manualFishOverride,
      reason,
      // For display: what the adjustment means (relative to adjusted model, not original)
      interpretation: Math.abs(bestFitPercent) <= 5
        ? 'Samples align well with the adjusted model — stock count appears consistent'
        : `The adjusted model is ${Math.abs(bestFitPercent).toFixed(0)}% ${bestFitPercent < 0 ? 'below' : 'above'} the standard curve. All growth analysis uses this adjusted baseline.`,
    };
  }, [result, formData.starting_fish_number, manualFishOverride, excludedSamples]);


  // ★ UNIFIED INTERPRETATION — uses adjusted model as the sole reference
  // Replaces backend interpretation which compares against original model
  const adjustedInterpretation = useMemo(() => {
    if (!result?.comparisons?.length) return result?.interpretation || '';
    const factor = calculatedBestFitAdjustment?.factor || 1;
    const adjPercent = Math.round(factor * 100);
    const lastComp = result.comparisons[result.comparisons.length - 1];
    const adjustedModelWeight = lastComp.model_weight_g * factor;
    const actualWeight = lastComp.actual_weight_g;
    const adjDeviation = adjustedModelWeight > 0 && actualWeight > 0
      ? ((actualWeight - adjustedModelWeight) / adjustedModelWeight * 100)
      : 0;

    const parts = [];
    // Single clear status against adjusted model
    if (factor !== 1) {
      parts.push(`Using adjusted model (${adjPercent}% of standard) as the reference baseline.`);
    }
    if (Math.abs(adjDeviation) <= 5) {
      parts.push(`Latest sample is within ±5% of the ${factor !== 1 ? 'adjusted' : ''} model (${adjDeviation >= 0 ? '+' : ''}${adjDeviation.toFixed(1)}%) — growth tracking as expected.`);
    } else if (adjDeviation > 5) {
      parts.push(`Latest sample is ${adjDeviation.toFixed(1)}% above the adjusted model — actual growth exceeding fitted expectations.`);
    } else {
      parts.push(`Latest sample is ${Math.abs(adjDeviation).toFixed(1)}% below the adjusted model — growth lagging behind fitted expectations.`);
    }

    // MFI context (one clear direction)
    if (showMissingFishIntelligence && missingFishIntelligence) {
      const strengthScore = missingFishIntelligence?.ssot?.detectionStrengthScore || 0;
      const hasMissing = missingFishIntelligence?.ssot?.hasMissingFish;
      const jumpDetected = missingFishIntelligence?.trajectoryJump?.jump_detected;
      const rangeLabel = missingFishIntelligence?.ssot?.missingFishRangeLabel;

      // ★ PRD — assertive "Missing fish signal detected" voice is allowed ONLY at HIGH
      //   confidence (score ≥ 70). At moderate → canonical "deviation pattern" wording.
      //   At low / indication-only → canonical "Indicative signal" with estimate withheld.
      if (hasMissing && jumpDetected && strengthScore >= 70) {
        parts.push(`Missing fish signal detected via jump analysis (${rangeLabel || 'range unavailable'}). Confidence: high — validate with field count.`);
      } else if (hasMissing && strengthScore >= 40) {
        parts.push('A deviation pattern is observed that may indicate stock discrepancy. Additional samples recommended before acting on the estimate.');
      } else if (hasMissing && strengthScore < 40) {
        parts.push('Indicative signal detected — estimate withheld due to insufficient confirmation. Collect additional samples to validate.');
      } else if (!hasMissing) {
        // PRD wording-fix #1 (revised): when ANY indicative signal is
        // active — jump detected (confirmed OR not), FCR conflict, low-
        // confidence indicative SSOT mode, OR `decisionClass` resolved to
        // MONITOR_ONLY — qualify BOTH sentences. Saying "Stock tracking
        // normal" is contradictory when the same report shows an indicative
        // signal / jump pattern / withheld estimate; the calibrated copy
        // tells the reader the current trajectory is close to the adjusted
        // model BUT low-confidence signals are still under review.
        const ssot = missingFishIntelligence?.ssot || {};
        const tj = missingFishIntelligence?.trajectoryJump || {};
        const decisionClass = result?.decisionContext?.decisionClass
          || missingFishIntelligence?.decisionContext?.decisionClass;
        const anyIndicativeSignal = !!(
          jumpDetected ||
          tj.jump_event_detected ||
          tj.trajectory_jump_detected ||
          tj.jump_event_confirmed ||
          tj.trajectory_jump_confirmed ||
          tj.jump_confirmation_pending ||
          ssot.fcrConflict ||
          ssot.confidenceMode === 'signal' ||
          ssot.confidenceMode === 'evidence' ||
          ssot.detectionStrength === 'Low' ||
          ssot.dataReliabilityLabel === 'Low' ||
          decisionClass === 'MONITOR_ONLY' ||
          decisionClass === 'VALIDATION_REQUIRED'
        );
        if (anyIndicativeSignal) {
          parts.push(
            'No confirmed missing fish signal detected. Growth is currently tracking close to the adjusted model, but low-confidence deviation signals remain under review.'
          );
        } else {
          parts.push(
            'No missing fish signal detected. Stock tracking normal against the adjusted model.'
          );
        }
      }
    }

    return parts.join(' ');
  }, [result, calculatedBestFitAdjustment, showMissingFishIntelligence, missingFishIntelligence]);


  // ★ LIVE RECALCULATION: Re-run intelligence when Best Fit scaling factor changes.
  // This makes the adjusted model have a dynamic, real-time effect on jump estimates.
  // Placed AFTER calculatedBestFitAdjustment useMemo to avoid TDZ.
  const lastFactorRef = useRef(null);
  useEffect(() => {
    if (!showMissingFishIntelligence || !result?.comparisons?.length || !calculatedBestFitAdjustment) return;
    const newFactor = calculatedBestFitAdjustment.factor;
    // Skip the first render (initial computation) — only react to changes
    if (lastFactorRef.current === null) {
      lastFactorRef.current = newFactor;
      return;
    }
    if (lastFactorRef.current === newFactor) return;
    lastFactorRef.current = newFactor;

    const rerun = async () => {
      let wModelFCR = null;
      try {
        const lastComp = result.comparisons[result.comparisons.length - 1];
        const lastWeight = parseFloat(lastComp.actual_weight_g);
        const authToken = token; // from useAuth
        const resp = await axios.get(`${BACKEND_URL}/api/model-fcr`, {
          params: { 
            species: formData.species, 
            weight_g: lastWeight, 
            genetic_origin: formData.genetic_origin,
            fcr_version_id: formData.fcr_version_id || undefined
          },
          headers: { 'Authorization': `Bearer ${token}` },
        });
        wModelFCR = resp.data.model_fcr;
      } catch { /* ignore */ }
      const reIntel = generateMissingFishIntelligence({
        growthModelResult: result,
        weightBasedModelFCR: wModelFCR,
        detectionMethod,
        excludedIndices: excludedSamples,
        modelScalingFactor: newFactor,
      });
      if (reIntel) {
        setMissingFishIntelligence(reIntel);
        setGraphInsight(reIntel.graphInsight);
        if (reIntel.trajectoryJump) setTrajectoryJump(reIntel.trajectoryJump);
      }
    };
    rerun();
  }, [calculatedBestFitAdjustment?.factor]); // Re-run when scaling factor changes


  // Prepare chart data following business requirements:
  // - Blue line (Model Growth): Pure theoretical SGR growth, NOT affected by samples
  // - Green line (Adjusted Model): Best-fit adjusted model based on missing fish
  // - Red line (Actual Growth): SGR-based growth that re-anchors at each sample point
  // - Teal dots (Sample Points): Actual sample weight points
  const chartData = useMemo(() => {
    if (!result) return [];
    
    // Build sample points array including arrival (for the dots)
    const samplePoints = [
      { day: 0, weight: result.arrival_average_weight_g, deviation: 0, compIdx: -1 }
    ];
    
    if (result.comparisons) {
      result.comparisons.forEach((c, idx) => {
        samplePoints.push({
          day: c.days_since_arrival,
          weight: c.actual_weight_g,
          deviation: c.difference_percentage,
          compIdx: idx,
        });
      });
    }
    
    // Sort sample points by day
    samplePoints.sort((a, b) => a.day - b.day);
    
    // Create model trajectory map (pure theoretical growth - blue line)
    const modelByDay = {};
    if (result.model_trajectory) {
      result.model_trajectory.forEach(p => {
        modelByDay[p.day] = p.weight;
      });
    }
    
    // Recalculate actual growth trajectory excluding excluded samples
    // Uses model trajectory growth ratios to interpolate between included anchors
    const actualByDay = {};
    const includedAnchors = samplePoints.filter(
      sp => sp.compIdx === -1 || !excludedSamples.has(sp.compIdx)
    );
    
    if (excludedSamples.size === 0 && result.actual_growth_trajectory) {
      // No exclusions — use original backend trajectory
      result.actual_growth_trajectory.forEach(p => {
        actualByDay[p.day] = p.weight;
      });
    } else if (includedAnchors.length > 0) {
      // Recalculate: grow from each included anchor using model growth ratios
      for (let seg = 0; seg < includedAnchors.length; seg++) {
        const anchor = includedAnchors[seg];
        const nextAnchor = includedAnchors[seg + 1];
        const endDay = nextAnchor ? nextAnchor.day : anchor.day;
        
        // Place anchor point
        actualByDay[anchor.day] = anchor.weight;
        
        // Grow from anchor to next anchor using model growth ratios
        if (endDay > anchor.day && modelByDay[anchor.day]) {
          const modelAtAnchor = modelByDay[anchor.day];
          for (let d = anchor.day + 1; d < endDay; d++) {
            if (modelByDay[d] != null && modelAtAnchor > 0) {
              actualByDay[d] = anchor.weight * (modelByDay[d] / modelAtAnchor);
            }
          }
        }
        
        // Place next anchor (re-anchor to its actual weight)
        if (nextAnchor) {
          actualByDay[nextAnchor.day] = nextAnchor.weight;
        }
      }
    }
    
    // Get max day from model trajectory
    const maxDay = result.model_trajectory && result.model_trajectory.length > 0
      ? Math.max(...result.model_trajectory.map(p => p.day))
      : (samplePoints.length > 0 ? samplePoints[samplePoints.length - 1].day : 0);
    
    // Determine the last included sample day for red line cutoff
    const lastIncludedDay = includedAnchors.length > 0
      ? includedAnchors[includedAnchors.length - 1].day
      : 0;
    
    // Get best-fit adjustment factor for adjusted model line
    const adjustmentFactor = calculatedBestFitAdjustment?.factor || 1;
    
    // Build chart data for each day
    const chartPoints = [];
    
    for (let day = 0; day <= maxDay; day++) {
      const modelWeight = modelByDay[day] !== undefined ? modelByDay[day] : null;
      
      // Calculate adjusted model weight (model × adjustment factor)
      // This represents expected weight if there are fewer fish (each fish is larger)
      const adjustedModelWeight = modelWeight !== null && adjustmentFactor !== 1
        ? modelWeight * adjustmentFactor
        : null;
      
      // Use recalculated trajectory for the red line — only up to last included sample
      const actualWeight = day <= lastIncludedDay && actualByDay[day] !== undefined 
        ? actualByDay[day] 
        : null;
      
      // Show sample dots — but excluded samples still shown (as gray markers via dot renderer)
      const sampleInfo = samplePoints.find(s => s.day === day);
      const isSampleDay = !!sampleInfo;
      const sampleWeight = isSampleDay ? sampleInfo?.weight : null;
      const sampleDeviation = isSampleDay && sampleInfo?.deviation !== undefined ? sampleInfo.deviation : null;
      
      // Calculate ±5% deviation band around model weight
      const modelUpper = modelWeight !== null ? modelWeight * 1.05 : null;
      const modelLower = modelWeight !== null ? modelWeight * 0.95 : null;
      
      // Calculate ±5% deviation band around adjusted model
      const adjustedUpper = adjustedModelWeight !== null ? adjustedModelWeight * 1.05 : null;
      const adjustedLower = adjustedModelWeight !== null ? adjustedModelWeight * 0.95 : null;
      
      chartPoints.push({
        day: day,
        'Model Growth': modelWeight,              // Blue line - pure theoretical SGR (unadjusted)
        'Adjusted Model': adjustedModelWeight,    // Green line - best-fit adjusted model
        'Model Upper': modelUpper,                // +5% band upper (original model)
        'Model Lower': modelLower,                // -5% band lower (original model)
        'Adjusted Upper': adjustedUpper,          // +5% band upper (adjusted model)
        'Adjusted Lower': adjustedLower,          // -5% band lower (adjusted model)
        'Actual Growth': actualWeight,            // Red line - SGR re-anchored at included samples
        'Sample Points': sampleWeight,            // Teal dots - actual measurements
        'Sample Deviation': sampleDeviation,      // Deviation % at sample points
      });
    }
    
    return chartPoints;
  }, [result, excludedSamples, calculatedBestFitAdjustment]);
  
  // Build chart data with comparison analyses overlaid (normalized by Day index)
  const chartDataWithComparisons = useMemo(() => {
    if (comparisonAnalyses.length === 0 || chartData.length === 0) {
      return chartData;
    }

    // Find max day across current chart and all comparison trajectories
    let maxDay = chartData.length > 0 ? chartData[chartData.length - 1].day : 0;
    comparisonAnalyses.forEach(comp => {
      if (comp.model_trajectory) {
        const compMax = Math.max(...comp.model_trajectory.map(p => p.day));
        if (compMax > maxDay) maxDay = compMax;
      }
      if (comp.actual_growth_trajectory) {
        const compMax = Math.max(...comp.actual_growth_trajectory.map(p => p.day));
        if (compMax > maxDay) maxDay = compMax;
      }
    });

    // Build index maps for fast lookup per comparison
    const compMaps = comparisonAnalyses.map(comp => {
      const modelMap = {};
      const actualMap = {};
      if (comp.model_trajectory) comp.model_trajectory.forEach(p => { modelMap[p.day] = p.weight; });
      if (comp.actual_growth_trajectory) comp.actual_growth_trajectory.forEach(p => { actualMap[p.day] = p.weight; });
      return { modelMap, actualMap };
    });

    // Create chart data map from current analysis
    const currentByDay = {};
    chartData.forEach(p => { currentByDay[p.day] = p; });

    // Build enhanced data for every day up to maxDay
    const enhancedData = [];
    for (let day = 0; day <= maxDay; day++) {
      const base = currentByDay[day] || { day };
      const enhanced = { ...base };
      
      compMaps.forEach((maps, compIdx) => {
        const compKey = `comp_${compIdx}`;
        if (maps.modelMap[day] !== undefined) enhanced[`${compKey}_model`] = maps.modelMap[day];
        if (maps.actualMap[day] !== undefined) enhanced[`${compKey}_actual`] = maps.actualMap[day];
      });
      
      enhancedData.push(enhanced);
    }
    
    return enhancedData;
  }, [chartData, comparisonAnalyses]);
  
  // Get all saved records for comparison (combined ongoing + closed)
  const allRecordsForComparison = useMemo(() => {
    const all = [...(records.ongoing || []), ...(records.closed || [])];
    // Exclude current active record
    return all.filter(r => r.id !== activeRecordId);
  }, [records, activeRecordId]);

  // Fetch comparison data (with trajectories) for a specific record
  const toggleComparisonRecord = async (record) => {
    const isSelected = comparisonAnalyses.some(c => c.id === record.id);
    
    if (isSelected) {
      // Remove from comparison
      setComparisonAnalyses(prev => prev.filter(c => c.id !== record.id));
      // Clean up visibility state
      const idx = comparisonAnalyses.findIndex(c => c.id === record.id);
      if (idx >= 0) {
        setLineVisibility(prev => {
          const reset = { ...prev };
          delete reset[`comp_${idx}_model`];
          delete reset[`comp_${idx}_actual`];
          return reset;
        });
      }
      return;
    }

    if (comparisonAnalyses.length >= 5) {
      toast.error('Maximum 5 comparisons allowed');
      return;
    }

    if (!record.has_trajectories) {
      toast.error('This record has no stored analysis data. Run an analysis first.');
      return;
    }

    // Fetch comparison data with trajectories
    setLoadingComparisonId(record.id);
    try {
      const authToken = token; // from useAuth
      const response = await axios.get(
        `${BACKEND_URL}/api/growth-records/${record.id}/comparison-data`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setComparisonAnalyses(prev => [...prev, response.data]);
    } catch (err) {
      toast.error('Failed to load comparison data');
    } finally {
      setLoadingComparisonId(null);
    }
  };
  
  // Check if adjustment is applied
  const isAdjusted = result && result.model_adjustment_percent !== 100;
  
  // Custom label renderer for sample point deviation labels
  const renderDeviationLabel = (props) => {
    const { cx, cy, payload } = props;
    const deviation = payload?.['Sample Deviation'];
    
    // Don't show label for arrival point (day 0) or if no deviation data
    if (!deviation || payload?.day === 0) return null;
    
    // Determine label color based on deviation threshold
    let labelColor = '#6b7280'; // neutral gray
    if (deviation > 3) labelColor = '#16a34a'; // green for positive
    else if (deviation < -3) labelColor = '#dc2626'; // red for negative
    
    const labelText = deviation > 0 ? `+${deviation}%` : `${deviation}%`;
    
    return (
      <text 
        x={cx} 
        y={cy - 14} 
        fill={labelColor} 
        fontSize={10} 
        fontWeight={600}
        textAnchor="middle"
      >
        {labelText}
      </text>
    );
  };

  return (
    <div className="min-h-screen bg-[#F3F4F6] dark:bg-[#0b1220] transition-colors duration-200" data-testid="growth-model-page">
      <Header />
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        <div className="flex items-center space-x-4">
          <Button
            onClick={() => navigate('/')}
            data-testid="back-button"
            variant="outline"
            className="border-[#0891b2] text-[#0891b2] hover:bg-[#0891b2] hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-[#0891b2]">Growth vs Model Analysis</h1>
            <p className="text-gray-600">Compare actual growth curve against expected model</p>
          </div>
        </div>

        {/* ★ Above-the-fold feature video — PRD item 1 */}
        <FeatureVideoHero
          featureName="Growth vs Model Analysis"
          youtubeUrl={HELP_URLS?.growthModelAnalysis?.youtube}
        />

        {/* Try-with-Example bar — admin-curated presets */}
        <ModuleExamplesBar
          examples={growthModelExamples}
          onSelect={loadExampleAndRun}
          moduleLabel="Growth vs Model"
          isReadOnly={isReadOnly}
        />

        {error && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <div className="flex items-center space-x-2 text-red-800">
                <AlertTriangle className="w-5 h-5" />
                <span>{error}</span>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="glass-card border-slate-200">
          <CardHeader>
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-[#0e7490] rounded-lg">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <div>
                <CardTitle className="text-[#0e7490]">Farm Information</CardTitle>
                <CardDescription className="text-gray-600">Enter basic farm and arrival data</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Read-Only Mode Banner */}
            {isReadOnly && (
              <div className="mb-4 p-4 bg-amber-50 border-2 border-amber-400 rounded-lg" data-testid="read-only-banner">
                <div className="flex items-center gap-2">
                  <Lock className="w-5 h-5 text-amber-600" />
                  <div>
                    <p className="text-sm font-bold text-amber-800">Read-Only Mode</p>
                    <p className="text-xs text-amber-700">Your account is pending approval. You can explore the interface but cannot run analyses or fill in fields.</p>
                  </div>
                </div>
              </div>
            )}
            <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
              <fieldset disabled={isReadOnly} className={isReadOnly ? 'opacity-60' : ''}>
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200 mb-4">
                <p className="text-sm text-blue-800">
                  <strong>Mandatory:</strong> Species, Genetic Origin, Temperature, Arrival Date, Arrival Weight, and at least 1 Sample. 
                  All other fields are optional.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="species">Species <span className="text-red-600">*</span></Label>
                  <Select
                    value={formData.species}
                    onValueChange={(value) => handleFormChange('species', value)}
                    disabled={isReadOnly}
                  >
                    <SelectTrigger className="bg-slate-50 border-[#0891b2]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-80">
                      <SelectGroup>
                        <SelectLabel className="text-[#0891b2] font-semibold">Available Species</SelectLabel>
                        <SelectItem value="Seabream">Seabream (Sparus aurata)</SelectItem>
                        <SelectItem value="Seabass" data-testid="species-option-seabass">
                          Seabass (Dicentrarchus labrax)
                        </SelectItem>
                        <SelectItem value="Tilapia" data-testid="species-option-tilapia">
                          Tilapia (Oreochromis niloticus)
                        </SelectItem>
                        {userSpecies.filter(s => s.value === 'Tripletail').map(s => (
                          <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                        ))}
                      </SelectGroup>
                      <SelectGroup>
                        <SelectLabel className="text-gray-400 font-semibold">Coming Soon</SelectLabel>
                        {COMING_SOON_SPECIES.filter(s => !s.value.includes('Oreochromis')).map(species => (
                          <SelectItem 
                            key={species.value} 
                            value={species.value} 
                            disabled 
                            className="text-gray-400 italic cursor-not-allowed"
                          >
                            {species.label} (Coming soon)
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>

                {/* Genetic Origin - Only show for Seabream (Tilapia / Seabass have no genetic strain) */}
                {formData.species !== 'Tilapia' && formData.species !== 'Seabass' && formData.species !== 'Tripletail' && (
                  <div className="space-y-2">
                    <Label htmlFor="genetic_origin">Genetic Origin <span className="text-red-600">*</span></Label>
                    <Select
                      value={formData.genetic_origin}
                      onValueChange={(value) => {
                        handleFormChange('genetic_origin', value);
                        // Reset FCR version to default for new origin
                        const matchingOrigin = fcrOrigins.find(o => o.name === value);
                        if (matchingOrigin?.default_version_id) {
                          handleFormChange('fcr_version_id', matchingOrigin.default_version_id);
                        }
                      }}
                    >
                      <SelectTrigger className="bg-slate-50 border-[#0891b2]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Greek">Greek</SelectItem>
                        <SelectItem value="French">French</SelectItem>
                        <SelectItem value="Italian">Italian</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* FCR Version Selector - Show all versions from all origins */}
                {fcrOrigins.length > 0 && (
                  <div className="space-y-2">
                    <Label htmlFor="fcr_version">
                      FCR Version
                      <span className="text-xs text-gray-500 font-normal ml-2">(Optional)</span>
                    </Label>
                    <Select
                      value={formData.fcr_version_id || ''}
                      onValueChange={(value) => handleFormChange('fcr_version_id', value)}
                      data-testid="fcr-version-select"
                    >
                      <SelectTrigger className="bg-emerald-50 border-emerald-300">
                        <SelectValue placeholder="Select version" />
                      </SelectTrigger>
                      <SelectContent>
                        {fcrOrigins.flatMap(origin =>
                          (origin.versions || []).map(v => (
                            <SelectItem key={v.id} value={v.id}>
                              {origin.name} — {v.label || `${v.version_name} (${v.multiplier_percent}%)`}
                              {v.id === origin.default_version_id && ' ★'}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500">
                      FCR multiplier version affects biological FCR calculations
                    </p>
                    <FCRVersionGuidanceNote />
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="model_adjustment_percent" className="flex items-center space-x-2">
                    <span>Model Adjustment (%)</span>
                    <span className="text-xs text-gray-500 font-normal">(What-if Scenario)</span>
                  </Label>
                  <Input
                    id="model_adjustment_percent"
                    type="number"
                    step="1"
                    min="50"
                    max="150"
                    placeholder="100 (default)"
                    value={formData.model_adjustment_percent}
                    onChange={(e) => handleFormChange('model_adjustment_percent', e.target.value)}
                    className="bg-yellow-50 border-yellow-400 focus:border-yellow-500"
                  />
                  <p className="text-xs text-gray-600">
                    Default 100%. Use 110 for +10% model growth, 90 for -10% model growth. Range: 50-150%
                  </p>
                </div>

                {/* Model Source Selection */}
                <div className="space-y-2 p-3 bg-cyan-50 rounded-lg border border-cyan-200">
                  <Label className="flex items-center space-x-2">
                    <span>Model Source</span>
                    <span className="text-xs text-cyan-600 font-normal bg-cyan-100 px-2 py-0.5 rounded">New</span>
                  </Label>
                  <Select
                    value={formData.model_source}
                    onValueChange={(value) => {
                      handleFormChange('model_source', value);
                      if (value === 'TPS') {
                        handleFormChange('custom_model_id', '');
                      }
                    }}
                    data-testid="model-source-select"
                  >
                    <SelectTrigger className="bg-white">
                      <SelectValue placeholder="Select model source" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="TPS">TPS Standard Model (Default)</SelectItem>
                      <SelectItem value="CUSTOM" disabled={approvedModels.length === 0}>
                        My Approved Models {approvedModels.length === 0 && '(None available)'}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  
                  {formData.model_source === 'CUSTOM' && (
                    <div className="mt-2">
                      <Label className="text-sm text-gray-600 mb-1">Select Your Custom SGR Model</Label>
                      {loadingModels ? (
                        <p className="text-xs text-gray-500">Loading models...</p>
                      ) : approvedModels.length === 0 ? (
                        <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                          No approved models available yet. Upload and get approval from Dashboard.
                        </p>
                      ) : (
                        <Select
                          value={formData.custom_model_id}
                          onValueChange={(value) => handleFormChange('custom_model_id', value)}
                          data-testid="custom-model-select"
                        >
                          <SelectTrigger className="bg-white">
                            <SelectValue placeholder="Select your model" />
                          </SelectTrigger>
                          <SelectContent>
                            {approvedModels.map(model => (
                              <SelectItem key={model.id} value={model.id}>
                                {model.model_name} ({model.species || 'Seabream'})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="arrival_date">Arrival Date <span className="text-red-600">*</span></Label>
                  <Input
                    id="arrival_date"
                    type="date"
                    value={formData.arrival_date}
                    onChange={(e) => handleFormChange('arrival_date', e.target.value)}
                    required
                    className="bg-slate-50 border-[#0891b2]"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="arrival_average_weight_g">Arrival Average Weight (g) <span className="text-red-600">*</span></Label>
                  <Input
                    id="arrival_average_weight_g"
                    type="number"
                    step="0.1"
                    min={(formData.species || '').toLowerCase().includes('tilapia') ? '20' : '0.1'}
                    placeholder={(formData.species || '').toLowerCase().includes('tilapia') ? 'Min 20g' : 'e.g., 2.5'}
                    value={formData.arrival_average_weight_g}
                    onChange={(e) => handleFormChange('arrival_average_weight_g', e.target.value)}
                    required
                    className="bg-slate-50 border-[#0891b2]"
                  />
                  {(formData.species || '').toLowerCase().includes('tilapia') && (
                    <p className="text-xs text-emerald-700" data-testid="tilapia-min-weight-note">
                      For Tilapia: minimum average weight is <strong>20 g</strong>.
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="region">Geographic Region (Optional)</Label>
                  <Input
                    id="region"
                    placeholder="e.g., Mediterranean"
                    value={formData.region}
                    onChange={(e) => handleFormChange('region', e.target.value)}
                    className="bg-slate-50 border-gray-300"
                  />
                </div>

                {/* Temperature Input - Different UI for Tilapia vs Seabream. Tripletail needs no temperature. */}
                {formData.species === 'Tripletail' ? null : formData.species === 'Tilapia' ? (
                  <div
                    className="space-y-2 col-span-2 bg-slate-50 text-slate-600 px-3 py-2.5 rounded-md border border-slate-200"
                    data-testid="tilapia-static-model-banner"
                  >
                    <div className="flex items-start gap-2">
                      <Info className="w-3.5 h-3.5 mt-0.5 text-slate-400 shrink-0" />
                      <div className="space-y-0.5">
                        <p className="text-xs font-medium text-slate-700">
                          Static growth model — temperature-agnostic.
                        </p>
                        <p className="text-[11px] text-slate-500 leading-relaxed">
                          Tilapia uses a static time-driven curve (Day 1 → 219, 20g → 1,402.45g). No monthly temperature profile is required; to shift the curve use the <strong className="font-medium text-slate-600">admin model %</strong>. Missing-fish confidence is auto-clamped one level down.
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label htmlFor="temperature_region">Temperature Region <span className="text-red-600">*</span></Label>
                    <Select
                      value={formData.temperature_region}
                      onValueChange={(value) => handleFormChange('temperature_region', value)}
                      required
                    >
                      <SelectTrigger className="bg-slate-50 border-[#0891b2]">
                        <SelectValue placeholder="Select temperature region" />
                      </SelectTrigger>
                      <SelectContent>
                        {/* TPS Installed Profiles */}
                        {temperatureRegions.filter(r => typeof r === 'string').length > 0 && (
                          <>
                            <div className="px-2 py-1 text-xs font-semibold text-gray-500 bg-gray-100">
                              TPS Installed Profiles
                            </div>
                            {temperatureRegions.filter(r => typeof r === 'string').map((region) => (
                              <SelectItem key={region} value={region}>
                                {region}
                              </SelectItem>
                            ))}
                          </>
                        )}
                        
                        {/* My Custom Temperature Models */}
                        {temperatureRegions.filter(r => typeof r === 'object' && r.isCustom).length > 0 && (
                          <>
                            <div className="px-2 py-1 text-xs font-semibold text-orange-600 bg-orange-50 mt-1">
                              My Temperature Models
                            </div>
                            {temperatureRegions.filter(r => typeof r === 'object' && r.isCustom).map((model) => (
                              <SelectItem key={model.id} value={`custom:${model.id}`}>
                                {model.displayName}
                              </SelectItem>
                            ))}
                          </>
                        )}
                        
                        {/* Shared/Public Temperature Models */}
                        {temperatureRegions.filter(r => typeof r === 'object' && r.isPublic).length > 0 && (
                          <>
                            <div className="px-2 py-1 text-xs font-semibold text-purple-600 bg-purple-50 mt-1">
                              Shared Models (Available to All)
                            </div>
                            {temperatureRegions.filter(r => typeof r === 'object' && r.isPublic).map((model) => (
                              <SelectItem key={model.id} value={`custom:${model.id}`}>
                                {model.displayName}
                              </SelectItem>
                            ))}
                          </>
                        )}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500">Select the Mediterranean region or your approved custom temperature model</p>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="unit">Unit Name <span className="text-amber-600 text-xs">(Required for saving)</span></Label>
                  <Input
                    id="unit"
                    placeholder="e.g., Cage A1"
                    value={formData.unit}
                    onChange={(e) => handleFormChange('unit', e.target.value)}
                    className="bg-slate-50 border-gray-300"
                    data-testid="unit-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="batch_code">Batch Code <span className="text-amber-600 text-xs">(Required for saving)</span></Label>
                  <Input
                    id="batch_code"
                    placeholder="e.g., 2024-B001"
                    value={formData.batch_code}
                    onChange={(e) => handleFormChange('batch_code', e.target.value)}
                    className="bg-slate-50 border-gray-300"
                    data-testid="batch-code-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="starting_fish_number">Starting Fish Number <span className="text-rose-500">*</span></Label>
                  <Input
                    id="starting_fish_number"
                    type="number"
                    placeholder="e.g., 50000"
                    value={formData.starting_fish_number}
                    onChange={(e) => handleFormChange('starting_fish_number', e.target.value)}
                    className="bg-slate-50 border-gray-300"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="average_mortality_percentage">Average Mortality % (Optional)</Label>
                  <Input
                    id="average_mortality_percentage"
                    type="number"
                    step="0.1"
                    placeholder="e.g., 2.5"
                    value={formData.average_mortality_percentage}
                    onChange={(e) => handleFormChange('average_mortality_percentage', e.target.value)}
                    className="bg-slate-50 border-gray-300"
                  />
                </div>
              </div>

              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-lg font-semibold text-[#0e7490] mb-4">
                  Weight Samples (Minimum 1 Required, Maximum 7)
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Provide at least one sample. Leave optional samples empty if not available.
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  {weightSamples.map((sample, index) => (
                    <div key={index} className={`p-4 rounded-lg border ${sample.required ? 'bg-blue-50 border-blue-300' : 'bg-gray-50 border-gray-200'}`}>
                      <h4 className="font-medium text-gray-800 mb-3">
                        {sample.label}
                        {sample.required && <span className="text-red-600 ml-1">*</span>}
                      </h4>
                      <div className="space-y-2">
                        <div>
                          <Label className="text-sm">Date</Label>
                          <Input
                            type="date"
                            value={sample.sample_date}
                            onChange={(e) => handleSampleChange(index, 'sample_date', e.target.value)}
                            required={sample.required}
                            className="bg-white border-gray-300"
                          />
                        </div>
                        <div>
                          <Label className="text-sm">Weight (g)</Label>
                          <Input
                            type="number"
                            step="0.1"
                            placeholder="e.g., 25.5"
                            value={sample.actual_weight_g}
                            onChange={(e) => handleSampleChange(index, 'actual_weight_g', e.target.value)}
                            required={sample.required}
                            className="bg-white border-gray-300"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  type="submit"
                  disabled={loading || isReadOnly}
                  className="flex-1 bg-[#0891b2] hover:bg-[#06748c] text-white"
                  data-testid="analyze-btn"
                >
                  {isReadOnly ? (
                    <>
                      <Lock className="w-4 h-4 mr-2" />
                      Pending Approval
                    </>
                  ) : loading ? 'Analyzing...' : `Analyze Growth vs Model${formatCostLabel('growth_model')}`}
                </Button>
                <Button
                  type="button"
                  onClick={() => saveRecord(false)}
                  disabled={!formData.unit || !formData.batch_code || isReadOnly}
                  variant="outline"
                  className="flex-1 sm:flex-none border-amber-500 text-amber-600 hover:bg-amber-50 hover:text-amber-700"
                  data-testid="save-record-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {activeRecordId ? 'Update Record' : 'Save Record'}
                </Button>
                <UpdateCuratedExampleButton
                  module="growth_model"
                  species={loadedCurated?.species}
                  currentSpecies={formData.species}
                  token={token}
                  isAdmin={!!user?.is_admin}
                  sourceRecordId={loadedCurated?.source_record_id}
                  currentRecordId={activeRecordId}
                  currentName={loadedCurated?.current_name}
                  buildRecordPatch={() => ({
                    species: formData.species,
                    genetic_origin: formData.genetic_origin || '',
                    // FCR version + model source must persist or the curated
                    // payload silently falls back to the species default
                    // (e.g. Seabream → Greek v1) and overrides the admin's edit.
                    fcr_version_id: formData.fcr_version_id || '',
                    model_source: formData.model_source || 'TPS',
                    custom_model_id: formData.custom_model_id || '',
                    temperature_region: formData.temperature_region || '',
                    arrival_date: formData.arrival_date,
                    arrival_average_weight_g: parseFloat(formData.arrival_average_weight_g) || 0,
                    starting_fish_number: formData.starting_fish_number ? parseInt(formData.starting_fish_number) : null,
                    average_mortality_percentage: formData.average_mortality_percentage ? parseFloat(formData.average_mortality_percentage) : null,
                    model_adjustment_percent: parseFloat(formData.model_adjustment_percent) || 100,
                    samples: weightSamples
                      .filter(s => s.sample_date && s.actual_weight_g)
                      .map(s => ({
                        sample_number: s.sample_number,
                        sample_date: s.sample_date,
                        actual_weight_g: parseFloat(s.actual_weight_g),
                      })),
                  })}
                  onUpdated={() => {
                    // refetch curated examples so the badge/source name updates
                    refetchExamples?.();
                  }}
                />
              </div>
              </fieldset>
              {activeRecordId && (
                <p className="text-xs text-amber-600 text-center">
                  Currently editing: {formData.unit} - {formData.batch_code}
                </p>
              )}
            </form>
          </CardContent>
        </Card>

        {result && (
          <>
            <Card className="glass-card border-[#0891b2] shadow-lg">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                    <CardTitle className="text-[#0891b2]">Growth Comparison Results</CardTitle>
                  </div>
                  {user?.is_admin && (
                  <Button
                    onClick={handleExportPdf}
                    disabled={exportingPdf}
                    variant="outline"
                    size="sm"
                    className="text-purple-600 border-purple-300 hover:bg-purple-50"
                    data-testid="export-pdf-btn"
                  >
                    <Download className="w-4 h-4 mr-1" />
                    {exportingPdf ? 'Exporting...' : 'Export PDF'}
                  </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <h3 className="font-semibold text-blue-900 mb-2 text-sm sm:text-base">Interpretation</h3>
                  <p className="text-blue-800 text-xs sm:text-sm leading-relaxed">{adjustedInterpretation || result.interpretation}</p>
                </div>

                {/* Run Missing Fish Intelligence — prominent top position */}
                <div className="flex flex-col items-center gap-3">
                  <Button
                    onClick={runMissingFishIntelligence}
                    disabled={!hasSampleWeights || isRecordClosed}
                    className="bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-50 px-6 py-3 text-base"
                    data-testid="run-missing-fish-intelligence-btn"
                  >
                    <Brain className="w-5 h-5 mr-2" />
                    Run Missing Intelligence
                  </Button>

                  {/* View All Data Toggle — always visible below MFI button */}
                  {showMissingFishIntelligence && missingFishIntelligence && overriddenOutput?.finalDecision && (
                    overriddenOutput.finalDecision._originalConfidenceMode
                      ? overriddenOutput.finalDecision._originalConfidenceMode !== 'decision'
                      : overriddenOutput.finalDecision.confidenceMode !== 'decision'
                  ) && (
                    <div className="w-full max-w-3xl" data-testid="view-all-data-section">
                      <GuardrailsToggle
                        active={showAllDataOverride}
                        onChange={setShowAllDataOverride}
                        ariaLabel="Toggle Indicative Report Mode (bypass confidence guardrails)"
                        testId="view-all-data-toggle"
                      />
                    </div>
                  )}
                </div>

                {/* ★ PLANNING ESTIMATE SUMMARY — Single headline answer */}
                {!isRecordClosed && showMissingFishIntelligence && missingFishIntelligence && (
                  <PlanningEstimateSummary
                    ssot={missingFishIntelligence?.ssot}
                    finalDecision={overriddenOutput?.finalDecision}
                    methodResolution={missingFishIntelligence?.methodResolution}
                    decisionContext={missingFishIntelligence?.decisionContext}
                    guardrailsOff={showAllDataOverride}
                  />
                )}

                {/* Stock Integrity Score - displays after Missing Fish Intelligence is run */}
                {!isRecordClosed && showMissingFishIntelligence && missingFishIntelligence && (
                  <GrowthModelStockIntegrity 
                    missingFishIntelligence={missingFishIntelligence} 
                  />
                )}

                {/* ═══════════════════════════════════════════════════════════════════════════ */}
                {/* USER-INPUT BIOLOGICAL FCR COMPARISON — Improve accuracy with reported FCR */}
                {/* ═══════════════════════════════════════════════════════════════════════════ */}
                {/* ═══════════════════════════════════════════════════════════════════════════ */}
                {/* FCR vs JUMP SIDE-BY-SIDE COMPARISON — Methods are INDEPENDENT */}
                {/* FCR: Uses model FCR from backend lookup (same as Snapshot) */}
                {/* Jump: Uses trajectory analysis (completely separate calculation) */}
                {/* ═══════════════════════════════════════════════════════════════════════════ */}
                {!isRecordClosed && showMissingFishIntelligence && missingFishIntelligence && (() => {
                  const originalFishNumber = missingFishIntelligence.originalFishNumber || 0;
                  
                  // ═══════════════════════════════════════════════════════════════════════════
                  // FCR ESTIMATE — from user input, using model FCR from backend lookup
                  // This is COMPLETELY SEPARATE from Jump analysis
                  // ═══════════════════════════════════════════════════════════════════════════
                  const hasCustomFcrInput = userFcrComparison && userFcrComparison.reportedFcr > 0;
                  const fcrEstimate = hasCustomFcrInput ? userFcrComparison.estimatedMissing : 0;
                  const reportedFcr = hasCustomFcrInput ? userFcrComparison.reportedFcr : null;
                  const modelFcr = missingFishIntelligence.sharedMetrics?.model_fcr || null;
                  const fcrGapPercent = hasCustomFcrInput ? userFcrComparison.fcrGapPercent : null;
                  
                  // ═══════════════════════════════════════════════════════════════════════════
                  // JUMP ESTIMATE — from trajectory analysis (weight-based)
                  // This is COMPLETELY SEPARATE from FCR analysis
                  // ═══════════════════════════════════════════════════════════════════════════
                  const jumpEstimate = trajectoryJump?.jump_event_confirmed 
                    ? (originalFishNumber - (trajectoryJump?.best_fit_fish_number || originalFishNumber))
                    : 0;
                  
                  // Show panel if either method has an estimate OR if model FCR is available for user to input FCR
                  const hasAnyEstimate = fcrEstimate > 0 || jumpEstimate > 0;
                  const canCalculateFcr = modelFcr && modelFcr > 0;
                  
                  if (!hasAnyEstimate && !canCalculateFcr && !trajectoryJump?.jump_event_confirmed) return null;
                  
                  // Determine recommendation (only when BOTH have positive estimates)
                  const hasBothEstimates = fcrEstimate > 0 && jumpEstimate > 0;
                  const lowerEstimate = hasBothEstimates 
                    ? Math.min(fcrEstimate, jumpEstimate)
                    : (fcrEstimate > 0 ? fcrEstimate : jumpEstimate);
                  const recommendedMethod = hasBothEstimates 
                    ? (lowerEstimate === fcrEstimate ? 'fcr' : 'jump')
                    : null;
                  
                  return (
                    <div className="bg-gradient-to-r from-indigo-50 via-purple-50 to-indigo-50 border-2 border-indigo-300 rounded-xl p-5 shadow-md" data-testid="fcr-jump-comparison-panel">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="p-2 bg-indigo-600 rounded-lg">
                          <GitCompareArrows className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <h4 className="text-base font-bold text-indigo-900">FCR vs Jump Analysis Comparison</h4>
                          <p className="text-xs text-indigo-600">Side-by-side comparison of missing fish estimates from both detection methods</p>
                        </div>
                      </div>
                      
                      {/* Important disclaimers about Growth vs Model limitations */}
                      <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 mb-4">
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                          <div className="text-xs text-amber-800">
                            <p className="font-semibold mb-1">Important: Growth vs Model Limitations</p>
                            <ul className="list-disc list-inside space-y-0.5 text-amber-700">
                              <li><strong>Jump Analysis is the primary method</strong> — FCR estimates shown here are for reference only</li>
                              <li>Growth vs Model analysis is <strong>less accurate</strong> than Spreadsheet Uploader due to absence of daily feed data</li>
                              <li>FCR reference uses <strong>{formData.species}{formData.species === 'Seabream' && formData.genetic_origin ? ` (${formData.genetic_origin})` : ''}</strong> model lookup at {missingFishIntelligence.sharedMetrics?.actual_weight?.toFixed(0) || 'N/A'}g</li>
                            </ul>
                            <p className="mt-2 text-amber-800 font-medium">
                              For accurate FCR-based analysis, use the <a href="/spreadsheet-comparison" className="text-indigo-700 underline hover:text-indigo-900">Spreadsheet Uploader</a> with your daily feed records.
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Side-by-side comparison - ALWAYS SHOWN when at least one estimate exists */}
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        {/* FCR-Based Estimate — shows estimate when user enters biological FCR */}
                        <div className={`rounded-lg p-4 border-2 ${
                          hasCustomFcrInput && fcrEstimate > 0 ? 'bg-indigo-50 border-indigo-300' 
                          : hasCustomFcrInput && userFcrComparison?.interpretation === 'extra_fish_or_error' ? 'bg-amber-50 border-amber-300'
                          : 'bg-gray-50 border-gray-200'
                        }`}>
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wider font-medium">
                                FCR-Based Estimate
                              </p>
                              <p className="text-[10px] text-amber-600 font-medium">
                                {hasCustomFcrInput ? 'Based on reported biological FCR' : 'Enter your biological FCR below to unlock'}
                              </p>
                            </div>
                          </div>
                          <p className={`text-2xl font-bold ${
                            hasCustomFcrInput && fcrEstimate > 0 ? 'text-indigo-700' 
                            : hasCustomFcrInput && userFcrComparison?.interpretation === 'extra_fish_or_error' ? 'text-amber-600'
                            : hasCustomFcrInput ? 'text-green-600'
                            : 'text-gray-400'
                          }`} data-testid="auto-fcr-estimate-value">
                            {hasCustomFcrInput 
                              ? (fcrEstimate > 0 
                                  ? fcrEstimate.toLocaleString()
                                  : userFcrComparison?.interpretation === 'extra_fish_or_error' ? 'N/A' 
                                  : '0')
                              : '—'}
                          </p>
                          {hasCustomFcrInput && fcrEstimate > 0 && reportedFcr && modelFcr ? (
                            <p className="text-xs text-indigo-600 mt-1">
                              Your FCR {reportedFcr.toFixed(2)} &lt; model {modelFcr.toFixed(2)} → {Math.abs(fcrGapPercent).toFixed(1)}% gap
                            </p>
                          ) : hasCustomFcrInput && userFcrComparison?.interpretation === 'extra_fish_or_error' && reportedFcr && modelFcr ? (
                            <p className="text-xs text-amber-700 mt-1">
                              FCR {reportedFcr.toFixed(2)} &gt; model {modelFcr.toFixed(2)} — likely extra fish or sample error
                            </p>
                          ) : hasCustomFcrInput && reportedFcr && modelFcr ? (
                            <p className="text-xs text-green-600 mt-1">
                              FCR {reportedFcr.toFixed(2)} matches model {modelFcr.toFixed(2)} — no signal
                            </p>
                          ) : canCalculateFcr ? (
                            <p className="text-xs text-gray-400 mt-1">
                              Model FCR: {modelFcr.toFixed(2)} — enter your FCR below to see estimate
                            </p>
                          ) : (
                            <p className="text-xs text-gray-400 mt-1">
                              Model FCR unavailable for this species/weight
                            </p>
                          )}
                          {!hasCustomFcrInput && (
                            <p className="text-[10px] text-teal-600 mt-2 bg-teal-50 border border-teal-200 rounded px-2 py-1">
                              Scroll down to "Enter Your Biological FCR" to activate this estimate
                            </p>
                          )}
                        </div>
                        
                        {/* Jump-Based Estimate - PRIMARY METHOD */}
                        {(() => {
                          const isLowConfidence = trajectoryJump?.estimation_quality === 'poor' || (trajectoryJump?.gap_days && trajectoryJump.gap_days > 120);
                          return (
                        <div className={`rounded-lg p-4 border-2 ${
                          jumpEstimate > 0 && !isLowConfidence ? 'bg-purple-50 border-purple-300' : 'bg-gray-50 border-gray-200'
                        }`}>
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wider font-medium">Jump Analysis Estimate</p>
                              <p className="text-[10px] text-purple-600 font-medium">Primary Method</p>
                            </div>
                            {jumpEstimate > 0 && !isLowConfidence && (
                              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-purple-600 text-white">
                                PRIMARY
                              </span>
                            )}
                            {jumpEstimate > 0 && isLowConfidence && (
                              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-500 text-white">
                                INDICATIVE
                              </span>
                            )}
                          </div>
                          {/* Low confidence: suppress the big number, show indicative headline instead */}
                          {jumpEstimate > 0 && isLowConfidence ? (
                            <div>
                              <p className="text-lg font-bold text-amber-700">
                                Indicative signal detected
                              </p>
                              <p className="text-xs text-amber-600 mt-1">
                                Insufficient data for confirmation — an estimate of ~{jumpEstimate.toLocaleString()} fish is indicated but{' '}
                                {trajectoryJump?.gap_days > 60
                                  ? `the ${trajectoryJump.gap_days}-day gap between samples makes this unreliable.`
                                  : 'additional samples are needed for confirmation.'}
                              </p>
                              <p className="text-[10px] text-gray-500 mt-1">
                                Collect intermediate samples to validate this signal.
                              </p>
                            </div>
                          ) : (
                            <p className={`text-2xl font-bold ${
                              jumpEstimate > 0 ? 'text-purple-700' : 'text-gray-400'
                            }`} data-testid="auto-jump-estimate-value">
                              {jumpEstimate > 0 ? jumpEstimate.toLocaleString() : '—'}
                            </p>
                          )}
                          {jumpEstimate > 0 && !isLowConfidence ? (
                            <p className="text-xs text-gray-500 mt-1">
                              {trajectoryJump?.gap_days > 60
                                ? `Blended estimate (${Math.round((trajectoryJump.trajectory_weight || 1) * 100)}% trajectory, ${Math.round((1 - (trajectoryJump.trajectory_weight || 1)) * 100)}% adjusted model)`
                                : 'Based on weight trajectory analysis'}
                            </p>
                          ) : jumpEstimate === 0 ? (
                            <p className="text-xs text-gray-400 mt-1">
                              No jump signal detected
                            </p>
                          ) : null}
                          {trajectoryJump?.jump_event_confirmed && (
                            <p className="text-xs text-purple-600 mt-1">
                              Jump {isLowConfidence ? 'indicated' : 'confirmed'} on {trajectoryJump.confirmed_jump_date || 'sample analysis'}
                            </p>
                          )}
                        </div>
                          );
                        })()}
                      </div>
                      
                      {/* Recommendation Banner */}
                      {hasBothEstimates && (
                        <div className="bg-green-100 border-2 border-green-400 rounded-lg p-4 mb-4" data-testid="recommended-estimate-banner">
                          <div className="flex items-start gap-3">
                            <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="text-sm font-bold text-green-800" data-testid="recommended-estimate-value">
                                Planning Estimate: {lowerEstimate.toLocaleString()} missing fish
                              </p>
                              <p className="text-xs text-green-700 mt-1">
                                Using the lower of FCR ({fcrEstimate.toLocaleString()}) and Jump ({jumpEstimate.toLocaleString()}) — conservative approach reduces risk of overstating losses.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                      {!hasBothEstimates && jumpEstimate > 0 && !(trajectoryJump?.estimation_quality === 'poor' || (trajectoryJump?.gap_days && trajectoryJump.gap_days > 120)) && (
                        <div className="bg-purple-100 border-2 border-purple-400 rounded-lg p-4 mb-4" data-testid="recommended-estimate-banner">
                          <div className="flex items-start gap-3">
                            <CheckCircle className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="text-sm font-bold text-purple-800" data-testid="recommended-estimate-value">
                                Jump Analysis Estimate: {jumpEstimate.toLocaleString()} missing fish
                              </p>
                              <p className="text-xs text-purple-700 mt-1">
                                Based on trajectory analysis (primary method). Validate with a field count before adjusting records.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                      {!hasBothEstimates && jumpEstimate > 0 && (trajectoryJump?.estimation_quality === 'poor' || (trajectoryJump?.gap_days && trajectoryJump.gap_days > 120)) && (
                        <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4 mb-4" data-testid="recommended-estimate-banner">
                          <div className="flex items-start gap-3">
                            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="text-sm font-bold text-amber-800" data-testid="recommended-estimate-value">
                                Indicative signal detected — insufficient data for confirmation
                              </p>
                              <p className="text-xs text-amber-700 mt-1">
                                A jump pattern was detected (~{jumpEstimate.toLocaleString()} fish indicated) but the {trajectoryJump?.gap_days}-day sampling gap makes this unreliable. Collect intermediate samples to confirm or dismiss this signal.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Method Conflict/Explanation Note */}
                      {missingFishIntelligence.ssot?.methodConflictComparison?.conflictExplanation && !hasCustomFcrInput ? (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                          <p className="text-xs text-amber-800">
                            <span className="font-semibold">Note:</span> {missingFishIntelligence.ssot.methodConflictComparison.conflictExplanation}
                          </p>
                        </div>
                      ) : hasCustomFcrInput && userFcrComparison?.explanation ? (
                        <div className={`rounded-lg p-3 mb-4 ${
                          userFcrComparison.interpretation === 'missing_fish' ? 'bg-red-50 border border-red-200' 
                          : userFcrComparison.interpretation === 'extra_fish_or_error' ? 'bg-amber-50 border border-amber-200'
                          : 'bg-green-50 border border-green-200'
                        }`}>
                          <p className={`text-xs ${
                            userFcrComparison.interpretation === 'missing_fish' ? 'text-red-800' 
                            : userFcrComparison.interpretation === 'extra_fish_or_error' ? 'text-amber-800'
                            : 'text-green-800'
                          }`}>
                            {userFcrComparison.explanation}
                          </p>
                        </div>
                      ) : null}
                      
                      {/* Manual FCR Input - Always show when model FCR is available */}
                      {canCalculateFcr && (
                        <div className="mt-4 pt-4 border-t border-indigo-200">
                          <div className="bg-white rounded-lg p-4 border border-indigo-200">
                            <div className="flex items-center justify-between mb-2">
                              <label className="block text-sm font-medium text-gray-700">
                                Enter Your Biological FCR
                              </label>
                              <span className="text-xs text-indigo-600 font-medium">
                                Model FCR: {modelFcr.toFixed(3)}
                              </span>
                            </div>
                            <div className="flex items-center gap-3">
                              <input
                                type="number"
                                step="0.01"
                                min="0.5"
                                max="5"
                                value={userReportedFCR}
                                onChange={(e) => setUserReportedFCR(e.target.value)}
                                className="w-28 px-3 py-2 text-lg font-semibold border-2 border-indigo-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                placeholder="e.g. 1.85"
                                data-testid="user-fcr-input"
                              />
                              <div className="text-xs text-gray-500">
                                <p>Enter your reported biological FCR for a <strong>reference estimate</strong>.</p>
                                <p className="text-gray-400 mt-1">If your FCR &lt; model FCR → missing fish detected</p>
                                <p className="text-amber-600 mt-1 font-medium">Note: This is for reference only. Jump Analysis is the primary method in Growth vs Model.</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Show message if model FCR is not available */}
                      {!canCalculateFcr && (
                        <div className="mt-4 pt-4 border-t border-indigo-200">
                          <p className="text-xs text-red-500">
                            Model FCR could not be retrieved from the database. FCR-based analysis is unavailable.
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Confirmed Sample Jump Detection Alert */}
                {!isRecordClosed && trajectoryJump && (trajectoryJump.jump_event_detected || trajectoryJump.trajectory_jump_detected) && (
                  <TrajectoryJumpAlert trajectoryJump={trajectoryJump} methodResolution={missingFishIntelligence?.methodResolution} ssot={missingFishIntelligence?.ssot} overriddenOutput={overriddenOutput} />
                )}

                {/* Suspect Sample Flags — shown even without Missing Fish Intelligence */}
                {!isRecordClosed && trajectoryJump?.suspect_samples?.length > 0 && (
                  <div className="space-y-2 mb-4" data-testid="gvm-suspect-samples">
                    {trajectoryJump.suspect_samples.map((ss, idx) => (
                      <div key={ss.sampleDate || `ss-${idx}`} className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-300 rounded-lg">
                        <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs font-semibold text-amber-800" data-testid={`gvm-suspect-flag-${idx}`}>
                            {ss.flag}
                          </p>
                          <p className="text-[11px] text-amber-700 mt-0.5">
                            Sample on {ss.sampleDate}: {ss.sampleWeight}g (expected ~{ss.expectedWeight}g from prior trajectory)
                            {ss.reversionWeight != null && ` \u2014 next sample reverted to ${ss.reversionWeight}g`}
                          </p>
                          <p className="text-[11px] text-amber-600 mt-0.5">{ss.reason}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* FCR-Based Detection now rendered inside the shared MissingFishIntelligenceReport component */}

                <div className="bg-white p-4 md:p-6 rounded-lg border border-gray-200 shadow-sm" ref={pdfExportRef}>
                  {/* Chart Header with Expand Button */}
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-gray-800 text-base md:text-lg">
                      Growth Curve Comparison
                    </h3>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setChartExpanded(true)}
                      className="md:hidden text-[#0891b2] border-[#0891b2] hover:bg-[#0891b2] hover:text-white print:hidden"
                      data-pdf-hide="true"
                    >
                      <Maximize2 className="w-4 h-4 mr-1" />
                      Expand
                    </Button>
                  </div>

                  {/* Graph Insight */}
                  {graphInsight && (
                    <div className="mb-3 px-3 py-2 bg-cyan-50 border-l-4 border-cyan-500 rounded-r-md" data-testid="graph-insight-growth-model">
                      <p className="text-[10px] font-semibold text-cyan-700 uppercase tracking-wider mb-0.5">Key Insight</p>
                      <p className="text-xs text-cyan-800">{graphInsight}</p>
                    </div>
                  )}

                  {/* Chart Container - Mobile optimized height */}
                  <div className="flex gap-4">
                    {/* Chart */}
                    <div className="flex-1 h-[280px] md:h-[450px]" data-chart-container="true">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart 
                          data={comparisonAnalyses.length > 0 ? chartDataWithComparisons : chartData}
                          margin={{ top: 10, right: 10, left: 0, bottom: 30 }}
                        >
                        <CartesianGrid 
                          strokeDasharray="3 3" 
                          stroke="#e5e7eb" 
                          opacity={0.4}
                        />
                        <XAxis 
                          dataKey="day" 
                          label={{ 
                            value: 'Days', 
                            position: 'insideBottom', 
                            offset: -5,
                            style: { 
                              fontSize: '11px', 
                              fontWeight: '600',
                              fill: '#374151' 
                            }
                          }}
                          tick={{ fill: '#6b7280', fontSize: 10 }}
                          stroke="#9ca3af"
                          tickCount={6}
                        />
                        <YAxis 
                          label={{ 
                            value: 'Weight (g)', 
                            angle: -90, 
                            position: 'insideLeft',
                            offset: 10,
                            style: { 
                              fontSize: '11px', 
                              fontWeight: '600',
                              fill: '#374151',
                              textAnchor: 'middle'
                            }
                          }}
                          tick={{ fill: '#6b7280', fontSize: 10 }}
                          stroke="#9ca3af"
                          width={45}
                          tickCount={5}
                        />
                        <Tooltip 
                          contentStyle={{
                            backgroundColor: '#ffffff',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                            padding: '8px',
                          fontSize: '12px'
                        }}
                        labelStyle={{ 
                          fontWeight: '600', 
                          color: '#111827',
                          marginBottom: '4px',
                          fontSize: '11px'
                        }}
                        itemStyle={{ fontSize: '11px', padding: '2px 0' }}
                      />
                      
                      {/* ±5% Deviation Band around Model Growth - Shaded area */}
                      {lineVisibility.modelGrowth && (
                        <>
                          <Area
                            type="monotone"
                            dataKey="Model Upper"
                            stroke="none"
                            fill="#3b82f6"
                            fillOpacity={0.08}
                            connectNulls={false}
                            legendType="none"
                            tooltipType="none"
                          />
                          <Area
                            type="monotone"
                            dataKey="Model Lower"
                            stroke="none"
                            fill="#ffffff"
                            fillOpacity={1}
                            connectNulls={false}
                            legendType="none"
                            tooltipType="none"
                          />
                        </>
                      )}
                      
                      {/* Comparison analyses lines (behind current, dashed) */}
                      {comparisonAnalyses.map((compRecord, compIdx) => {
                        const colorIdx = Math.min(compIdx, COMPARISON_COLORS.length - 1);
                        const colors = COMPARISON_COLORS[colorIdx];
                        const compKey = `comp_${compIdx}`;
                        const compLabel = compRecord.unit ? `${compRecord.unit} ${compRecord.batch_code || ''}`.trim() : (compRecord.cage_name || compRecord.name || `Cycle ${compIdx + 1}`);
                        const isModelVisible = lineVisibility[`${compKey}_model`] !== false;
                        const isActualVisible = lineVisibility[`${compKey}_actual`] !== false;
                        return (
                          <Fragment key={compRecord.id || compIdx}>
                            {isModelVisible && (
                              <Line 
                                type="monotone" 
                                dataKey={`${compKey}_model`}
                                stroke={colors.model}
                                strokeWidth={2} 
                                dot={false} 
                                name={`${compLabel} (Model)`}
                                connectNulls={true}
                                strokeDasharray="6 3"
                                strokeOpacity={0.7}
                              />
                            )}
                            {isActualVisible && (
                              <Line 
                                type="monotone" 
                                dataKey={`${compKey}_actual`}
                                stroke={colors.actual}
                                strokeWidth={2} 
                                dot={false}
                                name={`${compLabel} (Actual)`}
                                connectNulls={true}
                                strokeOpacity={0.7}
                              />
                            )}
                          </Fragment>
                        );
                      })}
                      
                      {/* Model Growth - Blue line (Pure theoretical SGR growth) */}
                      {lineVisibility.modelGrowth && (
                        <Line 
                          type="monotone" 
                          dataKey="Model Growth" 
                          name={isAdjusted ? `Model (${result.model_adjustment_percent}%)` : "Model (SGR)"}
                          stroke="#3b82f6"
                          strokeWidth={2.5}
                          dot={false}
                          activeDot={{ r: 5 }}
                          connectNulls={false}
                        />
                      )}
                      
                      {/* Adjusted Model - Green line (Best-fit adjusted for missing fish) */}
                      {lineVisibility.adjustedModel && calculatedBestFitAdjustment && calculatedBestFitAdjustment.percent !== 0 && (
                        <Line 
                          type="monotone" 
                          dataKey="Adjusted Model" 
                          name={`Adjusted Model (${Math.round((1 + calculatedBestFitAdjustment.percent / 100) * 100)}%)`}
                          stroke="#10b981"
                          strokeWidth={2.5}
                          dot={false}
                          activeDot={{ r: 5 }}
                          connectNulls={false}
                          strokeDasharray="8 4"
                        />
                      )}
                      
                      {/* Actual Growth - Red line (SGR-based re-anchored at samples) */}
                      {lineVisibility.actualGrowth && (
                        <Line 
                          type="monotone" 
                          dataKey="Actual Growth" 
                          name="Actual (SGR-corrected)"
                          stroke="#ef4444"
                          strokeWidth={2.5}
                          dot={false}
                          activeDot={{ r: 5 }}
                          connectNulls={false}
                        />
                      )}
                      
                      {/* Sample Points - Teal dots (Actual measurements) with deviation labels */}
                      {lineVisibility.samplePoints && (
                        <Line 
                          type="monotone" 
                          dataKey="Sample Points" 
                          name="Samples"
                          stroke="none"
                          strokeWidth={0}
                          connectNulls={false}
                          dot={(props) => {
                            const { cx, cy, payload, index } = props;
                            if (cx == null || cy == null || payload?.['Sample Points'] == null) return null;
                            // Determine the comparison index for this sample point
                            // Sample points include arrival (day 0) which is NOT in comparisons
                            // So we need to find which comparison index this day maps to
                            let compIdx = -1;
                            if (result?.comparisons) {
                              compIdx = result.comparisons.findIndex(c => c.days_since_arrival === payload?.day);
                            }
                            const isExcluded = compIdx >= 0 && excludedSamples.has(compIdx);
                            const isSuspect = compIdx >= 0 && suspectComparisonIndices.has(compIdx);
                            // Highlight jump sample
                            const isJump = trajectoryJump?.jump_sample_day != null && payload?.day === trajectoryJump.jump_sample_day;
                            if (isExcluded) {
                              return (
                                <g key={`dot-${payload.day}`}>
                                  <circle cx={cx} cy={cy} r={6} fill="#9ca3af" stroke="#fff" strokeWidth={2} opacity={0.5} />
                                  <line x1={cx - 4} y1={cy - 4} x2={cx + 4} y2={cy + 4} stroke="#6b7280" strokeWidth={2} />
                                  <line x1={cx + 4} y1={cy - 4} x2={cx - 4} y2={cy + 4} stroke="#6b7280" strokeWidth={2} />
                                </g>
                              );
                            }
                            if (isJump) {
                              return (
                                <g key={`dot-${payload.day}`}>
                                  <circle cx={cx} cy={cy} r={12} fill="rgba(245,158,11,0.2)" stroke="#f59e0b" strokeWidth={2} />
                                  <circle cx={cx} cy={cy} r={6} fill="#f59e0b" stroke="#fff" strokeWidth={2} />
                                </g>
                              );
                            }
                            if (isSuspect) {
                              return (
                                <g key={`dot-${payload.day}`}>
                                  <circle cx={cx} cy={cy} r={8} fill="rgba(245,158,11,0.15)" stroke="#f59e0b" strokeWidth={1.5} strokeDasharray="3 2" />
                                  <circle cx={cx} cy={cy} r={5} fill="#f59e0b" stroke="#fff" strokeWidth={1.5} />
                                </g>
                              );
                            }
                            return <circle key={`dot-${payload.day}`} cx={cx} cy={cy} r={6} fill="#0891b2" stroke="#fff" strokeWidth={2} />;
                          }}
                          activeDot={{ 
                            r: 8,
                            fill: '#0e7490'
                          }}
                          label={renderDeviationLabel}
                        />
                      )}

                      {/* Loss window shading for trajectory jump */}
                      {trajectoryJump?.jump_event_detected && trajectoryJump?.loss_window_start_day != null && trajectoryJump?.loss_window_end_day != null && (
                        <ReferenceArea
                          x1={trajectoryJump.loss_window_start_day}
                          x2={trajectoryJump.loss_window_end_day}
                          fill="#f59e0b"
                          fillOpacity={0.08}
                          stroke="#f59e0b"
                          strokeOpacity={0.3}
                          strokeDasharray="3 3"
                          label={{ value: 'Loss Window', position: 'insideTop', fill: '#d97706', fontSize: 10, fontWeight: 600 }}
                        />
                      )}

                      {/* Incorrect sample annotations */}
                      {missingFishIntelligence?.analyticalSignals?.incorrectSamples?.samples?.map((s, idx) => (
                        <ReferenceLine
                          key={`incorrect-sample-${idx}`}
                          x={s.daysInWater}
                          stroke="#dc2626"
                          strokeWidth={2}
                          strokeDasharray="4 3"
                          label={{ value: '\u26A0', position: 'top', fill: '#dc2626', fontSize: 14, fontWeight: 700 }}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                    </div>
                    
                    {/* Line Visibility Controls - Checkboxes beside graph */}
                    <div className="w-48 hidden md:block" data-pdf-hide="true">
                      <div className="bg-gray-50 rounded-lg border border-gray-200 p-3">
                        <h4 className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1">
                          <GitCompareArrows className="w-3 h-3" />
                          Line Visibility
                        </h4>
                        
                        {/* Current analysis lines */}
                        <div className="space-y-1.5 mb-3">
                          <label className="flex items-center gap-2 cursor-pointer hover:bg-white rounded px-1 py-0.5">
                            <input
                              type="checkbox"
                              checked={lineVisibility.modelGrowth}
                              onChange={(e) => setLineVisibility(prev => ({ ...prev, modelGrowth: e.target.checked }))}
                              className="w-3.5 h-3.5 rounded border-blue-500 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="w-3 h-0.5 bg-blue-500 rounded"></span>
                            <span className="text-xs text-gray-700">Model Growth (Unadjusted)</span>
                          </label>
                          {calculatedBestFitAdjustment && calculatedBestFitAdjustment.percent !== 0 && (
                            <label className="flex items-center gap-2 cursor-pointer hover:bg-white rounded px-1 py-0.5">
                              <input
                                type="checkbox"
                                checked={lineVisibility.adjustedModel}
                                onChange={(e) => setLineVisibility(prev => ({ ...prev, adjustedModel: e.target.checked }))}
                                className="w-3.5 h-3.5 rounded border-emerald-500 text-emerald-600 focus:ring-emerald-500"
                              />
                              <span className="w-3 h-0.5 bg-emerald-500 rounded" style={{ borderStyle: 'dashed' }}></span>
                              <span className="text-xs text-gray-700">Adjusted Model ({Math.round((1 + calculatedBestFitAdjustment.percent / 100) * 100)}%)</span>
                            </label>
                          )}
                          <label className="flex items-center gap-2 cursor-pointer hover:bg-white rounded px-1 py-0.5">
                            <input
                              type="checkbox"
                              checked={lineVisibility.actualGrowth}
                              onChange={(e) => setLineVisibility(prev => ({ ...prev, actualGrowth: e.target.checked }))}
                              className="w-3.5 h-3.5 rounded border-red-500 text-red-600 focus:ring-red-500"
                            />
                            <span className="w-3 h-0.5 bg-red-500 rounded"></span>
                            <span className="text-xs text-gray-700">Actual Growth</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer hover:bg-white rounded px-1 py-0.5">
                            <input
                              type="checkbox"
                              checked={lineVisibility.samplePoints}
                              onChange={(e) => setLineVisibility(prev => ({ ...prev, samplePoints: e.target.checked }))}
                              className="w-3.5 h-3.5 rounded border-teal-500 text-teal-600 focus:ring-teal-500"
                            />
                            <span className="w-2 h-2 bg-teal-500 rounded-full"></span>
                            <span className="text-xs text-gray-700">Sample Points</span>
                          </label>
                        </div>
                        
                        {/* Comparison analyses visibility */}
                        {comparisonAnalyses.length > 0 && (
                          <>
                            <div className="border-t border-gray-200 pt-2 mt-2">
                              <p className="text-xs font-medium text-purple-700 mb-1.5">Compared Cycles</p>
                              {comparisonAnalyses.map((comp, idx) => {
                                const colors = COMPARISON_COLORS[idx % COMPARISON_COLORS.length];
                                const compKey = `comp_${idx}`;
                                const compLabel = comp.unit ? `${comp.unit} ${comp.batch_code || ''}`.trim() : (comp.cage_name || comp.name || `Cycle ${idx + 1}`);
                                return (
                                  <div key={comp.id || idx} className="space-y-1 mb-2">
                                    <label className="flex items-center gap-2 cursor-pointer hover:bg-white rounded px-1 py-0.5">
                                      <input
                                        type="checkbox"
                                        checked={lineVisibility[`${compKey}_model`] !== false}
                                        onChange={(e) => setLineVisibility(prev => ({ ...prev, [`${compKey}_model`]: e.target.checked }))}
                                        className="w-3.5 h-3.5 rounded"
                                        style={{ accentColor: colors.model }}
                                      />
                                      <span className="w-3 h-0.5 rounded" style={{ backgroundColor: colors.model }}></span>
                                      <span className="text-xs text-gray-600 truncate">{compLabel} (M)</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer hover:bg-white rounded px-1 py-0.5">
                                      <input
                                        type="checkbox"
                                        checked={lineVisibility[`${compKey}_actual`] !== false}
                                        onChange={(e) => setLineVisibility(prev => ({ ...prev, [`${compKey}_actual`]: e.target.checked }))}
                                        className="w-3.5 h-3.5 rounded"
                                        style={{ accentColor: colors.actual }}
                                      />
                                      <span className="w-3 h-0.5 rounded" style={{ backgroundColor: colors.actual }}></span>
                                      <span className="text-xs text-gray-600 truncate">{compLabel} (A)</span>
                                    </label>
                                  </div>
                                );
                              })}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Legend Chips - Mobile optimized */}
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <div className="flex flex-wrap items-center justify-center gap-2 md:gap-4">
                      {/* Model chip */}
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-50 border border-blue-200 text-xs md:text-sm font-medium text-blue-700">
                        <span className="w-3 h-0.5 bg-blue-500 rounded"></span>
                        Model (SGR)
                      </span>
                      {/* Actual chip */}
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-50 border border-red-200 text-xs md:text-sm font-medium text-red-700">
                        <span className="w-3 h-0.5 bg-red-500 rounded"></span>
                        Actual (SGR-corrected)
                      </span>
                      {/* Samples chip */}
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-teal-50 border border-teal-200 text-xs md:text-sm font-medium text-teal-700">
                        <span className="w-2 h-2 bg-teal-500 rounded-full"></span>
                        Samples
                      </span>
                      {/* Suspect chip */}
                      {suspectComparisonIndices.size > 0 && (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 border border-amber-200 text-xs md:text-sm font-medium text-amber-700">
                          <span className="w-2 h-2 bg-amber-500 rounded-full border border-dashed border-amber-600"></span>
                          Suspect
                        </span>
                      )}
                      {/* Excluded chip */}
                      {excludedSamples.size > 0 && (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-100 border border-gray-300 text-xs md:text-sm font-medium text-gray-500">
                          <span className="w-2 h-2 bg-gray-400 rounded-full"></span>
                          Excluded
                        </span>
                      )}
                      {/* Info button */}
                      <button
                        onClick={() => setShowLegendInfo(true)}
                        className="p-1.5 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                        aria-label="Legend info"
                      >
                        <Info className="w-4 h-4" />
                      </button>
                    </div>
                    {isAdjusted && (
                      <p className="text-xs text-yellow-700 bg-yellow-50 px-3 py-1.5 rounded border border-yellow-200 text-center mt-2">
                        ⚠️ Model adjusted to {result.model_adjustment_percent}%
                      </p>
                    )}
                    {/* Deviation band legend note. PRD wording-fix #4
                        (Growth vs Model): never hardcode "seabream" — read
                        the active species off the form/result so Tilapia
                        and Seabass reports use their own species name (or
                        a generic fallback). */}
                    <p className="text-xs text-gray-500 text-center mt-2 italic">
                      The shaded area represents expected biological variation (±5%) around the {(formData?.species || result?.species || '').toString().toLowerCase() || 'species'} growth model.
                    </p>

                    {/* How to Read This Graph */}
                    <div className="mt-3 px-3 py-2.5 bg-gray-50 rounded-lg border border-gray-200" data-testid="growth-chart-reading-guide">
                      <h4 className="text-xs font-semibold text-gray-700 mb-1.5 flex items-center gap-1.5">
                        <Info className="w-3.5 h-3.5 text-slate-500" />
                        How to Read This Graph
                      </h4>
                      <ul className="space-y-1 text-[11px] text-gray-600 leading-relaxed">
                        <li className="flex items-start gap-2">
                          <span className="w-4 h-0.5 mt-1.5 shrink-0 bg-red-500" style={{height: '2px'}}></span>
                          <span><strong className="text-gray-700">Actual Growth (red line)</strong> — Real sampled fish weights over time. Each point represents a measured weight from your cage.</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="w-4 h-0.5 mt-1.5 shrink-0 bg-blue-500" style={{borderTop: '2px dashed #3b82f6'}}></span>
                          <span><strong className="text-gray-700">Model Line (blue)</strong> — Expected biological growth under standard conditions for this species and temperature profile.</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="w-4 h-0.5 mt-1.5 shrink-0 bg-emerald-500" style={{borderTop: '2px dashed #10b981'}}></span>
                          <span><strong className="text-gray-700">Adjusted Model (green line)</strong> — The standard model automatically scaled to best match your actual sample data. It removes systematic over- or under-prediction so you can see true deviations more clearly.</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="w-1.5 h-1.5 mt-1.5 shrink-0 rounded-full bg-gray-500"></span>
                          <span><strong className="text-gray-700">Reading deviations</strong> — When actual growth diverges from the adjusted model, it may indicate missing fish, sampling inconsistencies, or feeding mismatches. The larger and more persistent the gap, the stronger the signal.</span>
                        </li>
                        {/* ★ PRD item 5 — aligned with the jump-analysis logic. */}
                        <li className="flex items-start gap-2" data-testid="guide-anomaly-circle">
                          <span className="w-3 h-3 mt-1 shrink-0 rounded-full border-2 border-orange-500 bg-transparent"></span>
                          <span><strong className="text-gray-700">Orange circle around a sample</strong> — That sample was flagged as a possible anomaly (sampling error, net not fully closed, or a single bad batch). It is excluded from the adjusted-model fit, so the line isn&apos;t pulled toward a likely-bad point. Revisit the field record to confirm or remove it.</span>
                        </li>
                        <li className="flex items-start gap-2" data-testid="guide-incorrect-sample">
                          <span className="relative w-4 h-4 mt-0.5 shrink-0 flex items-start justify-center">
                            <span className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-0.5" style={{borderLeft: '2px dashed #dc2626'}}></span>
                            <span className="relative text-[10px] leading-none text-red-600 font-bold">⚠</span>
                          </span>
                          <span><strong className="text-gray-700">Incorrect-Sample Flag (red dashed vertical line with ⚠)</strong> — Anchored to a specific sample date, this marker means the recorded weight is inconsistent with the surrounding feed and growth evidence (e.g. a weight jump that the feed ledger cannot support). Treat that sample as suspect: re-check the field notes, scale calibration, and whether mortality was correctly logged before acting on downstream metrics.</span>
                        </li>
                        <li className="flex items-start gap-2" data-testid="guide-loss-window">
                          <span className="w-4 h-3 mt-1 shrink-0 rounded-sm" style={{backgroundColor: 'rgba(245,158,11,0.15)', borderLeft: '2px solid #f59e0b', borderRight: '2px solid #f59e0b'}}></span>
                          <span><strong className="text-gray-700">Loss Window (amber shaded band)</strong> — A vertical date range highlighted in faint amber with <em>Loss Window</em> labelled on top. It marks the most probable interval during which a trajectory jump (biomass loss) occurred — the period between the last sample that matched the model and the first confirmed deviation. Use it as the focus window for reviewing mortality, escapes, or data gaps.</span>
                        </li>
                      </ul>
                    </div>

                    {/* ★ Context-aware Wingman trigger prompts — directly below
                        the "How to Read This Graph" guide per PRD. */}
                    <WingmanTriggerPrompts moduleName="Growth vs Model" />

                    {/* ═══════════════════════════════════════════════════════════════════════════ */}
                    {/* BEST FIT ADJUSTED MODEL — Directly below chart */}
                    {/* ═══════════════════════════════════════════════════════════════════════════ */}
                    {!isRecordClosed && result && calculatedBestFitAdjustment && (
                      <div className="mt-4 bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-300 rounded-xl p-5 shadow-sm" data-testid="best-fit-adjustment-panel">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="p-2 bg-emerald-600 rounded-lg">
                            <TrendingUp className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <h4 className="text-base font-bold text-emerald-900">Best Fit Adjusted Model</h4>
                            <p className="text-xs text-emerald-700">The standard growth model automatically adjusted to best match your actual sample data — helping isolate real deviations from systematic model differences</p>
                          </div>
                        </div>
                        <div className="bg-white rounded-lg p-4 border border-emerald-200 mb-4">
                          <div className={`rounded-lg p-4 text-center mb-4 ${
                            Math.abs(calculatedBestFitAdjustment.percent) < 2 ? 'bg-gray-100' 
                              : calculatedBestFitAdjustment.percent > 0 ? 'bg-amber-100 border border-amber-300' : 'bg-blue-100 border border-blue-300'
                          }`}>
                            <p className="text-sm text-gray-600 mb-1">Model Adjustment</p>
                            <p className={`text-3xl font-bold ${
                              Math.abs(calculatedBestFitAdjustment.percent) < 2 ? 'text-gray-700' 
                                : calculatedBestFitAdjustment.percent > 0 ? 'text-amber-700' : 'text-blue-700'
                            }`} data-testid="adjustment-percentage">
                              Model adjusted to {Math.round((1 + calculatedBestFitAdjustment.percent / 100) * 100)}%
                            </p>
                            <p className="text-sm text-gray-500 mt-1">
                              ({calculatedBestFitAdjustment.percent >= 0 ? '+' : ''}{calculatedBestFitAdjustment.percent}% from standard model)
                            </p>
                            {calculatedBestFitAdjustment.isManualOverride && (
                              <span className="text-[10px] text-amber-600 font-medium">(Manual Override)</span>
                            )}
                          </div>
                          <div className="grid grid-cols-3 gap-4 text-center mb-4">
                            <div className="bg-white rounded-lg p-2.5 border border-slate-100 shadow-sm text-center">
                              <p className="text-[10px] text-slate-400 uppercase tracking-wider">Valid Samples</p>
                              <p className="text-lg font-bold text-gray-800" data-testid="valid-sample-count">
                                {calculatedBestFitAdjustment.validSampleCount || 0}
                              </p>
                            </div>
                            <div className="bg-white rounded-lg p-2.5 border border-slate-100 shadow-sm text-center">
                              <p className="text-[10px] text-slate-400 uppercase tracking-wider">Model Fit Accuracy</p>
                              <p className={`text-lg font-bold ${calculatedBestFitAdjustment.rSquared >= 0.9 ? 'text-green-600' : calculatedBestFitAdjustment.rSquared >= 0.7 ? 'text-amber-600' : 'text-red-600'}`} data-testid="r-squared">
                                {calculatedBestFitAdjustment.rSquared?.toFixed(2) || 'N/A'}
                              </p>
                            </div>
                            <div className="bg-white rounded-lg p-2.5 border border-slate-100 shadow-sm text-center">
                              <p className="text-[10px] text-slate-400 uppercase tracking-wider">Avg. Deviation</p>
                              <p className="text-lg font-bold text-gray-800" data-testid="rmse">{calculatedBestFitAdjustment.rmse?.toFixed(1) || 'N/A'}g</p>
                            </div>
                          </div>
                          <div className="text-sm text-gray-700">{calculatedBestFitAdjustment.reason}</div>
                          {calculatedBestFitAdjustment.interpretation && Math.abs(calculatedBestFitAdjustment.percent) >= 5 && (
                            <div className={`mt-3 p-2 rounded-lg text-xs ${calculatedBestFitAdjustment.percent > 0 ? 'bg-amber-50 text-amber-800' : 'bg-blue-50 text-blue-800'}`}>
                              <strong>Interpretation:</strong> {calculatedBestFitAdjustment.interpretation}
                            </div>
                          )}
                        </div>
                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-4">
                          <div className="flex items-start gap-2">
                            <Brain className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
                            <div className="text-xs text-purple-800">
                              <p className="font-semibold mb-1">Jump Detection Baseline</p>
                              <p>Jump analysis compares samples against this <strong>adjusted model ({Math.round((1 + calculatedBestFitAdjustment.percent / 100) * 100)}% of standard)</strong>, not the original unadjusted model.</p>
                            </div>
                          </div>
                        </div>
                        <div className="bg-white rounded-lg p-4 border border-emerald-200">
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <h5 className="text-sm font-semibold text-gray-800">Manual Model Adjustment</h5>
                              <p className="text-[10px] text-gray-500">Fine-tune the model scaling percentage to test different fit scenarios</p>
                            </div>
                            {manualFishOverride !== null && (
                              <button onClick={() => setManualFishOverride(null)} className="text-xs text-red-600 hover:text-red-800 font-medium px-2 py-1 rounded bg-red-50 hover:bg-red-100" data-testid="reset-manual-override">Reset to Auto-Fit</button>
                            )}
                          </div>
                          <div className="space-y-3">
                            <div>
                              <div className="flex justify-between text-xs text-gray-500 mb-1">
                                <span>-30%</span>
                                <span className={`font-medium ${(manualFishOverride !== null ? manualFishOverride : Math.round(calculatedBestFitAdjustment.percent)) >= 0 ? 'text-amber-700' : 'text-blue-700'}`}>
                                  {manualFishOverride !== null ? (manualFishOverride >= 0 ? '+' : '') + manualFishOverride + '%' : (calculatedBestFitAdjustment.percent >= 0 ? '+' : '') + calculatedBestFitAdjustment.percent + '% (auto)'}
                                </span>
                                <span>+50%</span>
                              </div>
                              <input type="range" min="-30" max="50" step="1" value={manualFishOverride !== null ? manualFishOverride : Math.round(calculatedBestFitAdjustment.percent)} onChange={(e) => setManualFishOverride(parseInt(e.target.value))} className="w-full h-2 bg-gradient-to-r from-blue-200 via-gray-200 to-amber-200 rounded-lg appearance-none cursor-pointer" data-testid="manual-adjustment-slider" />
                            </div>
                            <div className="flex items-center gap-3">
                              <label className="text-xs text-gray-600">Adjustment %:</label>
                              <input type="number" min="-50" max="100" value={manualFishOverride !== null ? manualFishOverride : Math.round(calculatedBestFitAdjustment.percent)} onChange={(e) => { const val = parseInt(e.target.value) || 0; setManualFishOverride(Math.max(-50, Math.min(100, val))); }} className="w-20 px-2 py-1 text-sm border border-emerald-300 rounded focus:ring-1 focus:ring-emerald-500 text-center" data-testid="manual-adjustment-input" />
                              <span className="text-xs text-gray-500">→ Adjusted model = Standard model × <span className="font-semibold text-emerald-700">{(1 + (manualFishOverride !== null ? manualFishOverride : calculatedBestFitAdjustment.percent) / 100).toFixed(2)}</span></span>
                            </div>
                            <div className="text-[10px] text-gray-500 bg-gray-50 rounded p-2 space-y-1">
                              <p><strong>How it works:</strong> Graph and jump analysis update <span className="text-emerald-600 font-medium">live</span> as you adjust.</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* ====== FCR ANALYSIS PANEL ====== */}
                    {!isRecordClosed && showMissingFishIntelligence && missingFishIntelligence && (
                      <div className="mt-4" data-pdf-hide="true">
                        <FCRAnalysisPanel
                          fcrBasedDetection={missingFishIntelligence?.fcrBasedDetection}
                          fcrPanelMissingFish={null}
                          originalFishNumber={result?.starting_fish_number}
                          methodResolution={missingFishIntelligence?.methodResolution}
                          ssot={missingFishIntelligence?.ssot}
                        />
                      </div>
                    )}

                    {/* ====== SAMPLE JUMP ANALYSIS PANEL ====== */}
                    {!isRecordClosed && trajectoryJump && (
                      <div className="mt-4" data-pdf-hide="true">
                        <SampleJumpAnalysisPanel
                          trajectoryJump={trajectoryJump}
                          assumedFishNumber={assumedFishNumber}
                          onAssumedFishNumberChange={setAssumedFishNumber}
                          originalFishNumber={result?.starting_fish_number}
                          hasFeedData={false}
                          methodResolution={missingFishIntelligence?.methodResolution}
                          ssot={missingFishIntelligence?.ssot}
                        />
                      </div>
                    )}

                    {/* ====== BIOLOGICAL FCR PANEL ====== */}
                    {growthFCRResult && (
                      <div className="mt-4 bg-white rounded-lg border border-slate-200 shadow-sm p-4" data-pdf-hide="true" data-testid="biological-fcr-panel">
                        <div className="flex items-center gap-2 mb-3">
                          <Scale className="w-5 h-5 text-indigo-600" />
                          <h3 className="text-sm font-bold text-slate-800">Closing Biological FCR</h3>
                        </div>

                        {/* Dual FCR Display — Reported vs Adjusted */}
                        <div className="grid grid-cols-2 gap-4 mb-3">
                          <div className="bg-white rounded-lg p-4 border-l-4 border-l-slate-400 border border-slate-100 shadow-md text-center" data-testid="fcr-reported-section">
                            <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium mb-2">Closing Biological FCR (Reported Count)</p>
                            {growthFCRResult.reported.fcr !== null ? (
                              <p className="text-2xl font-bold text-slate-800" data-testid="fcr-reported-value">{growthFCRResult.reported.fcr.toFixed(2)}</p>
                            ) : (
                              <p className="text-sm text-gray-400 italic" data-testid="fcr-reported-value">Unavailable</p>
                            )}
                            <p className="text-[10px] text-gray-400 mt-0.5">Reported Fish Count: <span className="font-semibold text-gray-600" data-testid="fcr-reported-fish-count">{growthFCRResult.reportedN.toLocaleString()}</span></p>
                          </div>
                          <div className={`rounded-lg p-4 border-l-4 shadow-md text-center ${growthFCRResult.estimatedMissing > 0 ? 'bg-white border-l-amber-400 border border-amber-50' : 'bg-white border-l-slate-400 border border-slate-100'}`} data-testid="fcr-adjusted-section">
                            <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium mb-2">Closing Biological FCR (Adjusted Count)</p>
                            {growthFCRResult.adjusted.fcr !== null ? (
                              <p className={`text-2xl font-bold ${growthFCRResult.estimatedMissing > 0 ? 'text-amber-700' : 'text-slate-800'}`} data-testid="fcr-adjusted-value">{growthFCRResult.adjusted.fcr.toFixed(2)}</p>
                            ) : (
                              <p className="text-sm text-gray-400 italic" data-testid="fcr-adjusted-value">Unavailable</p>
                            )}
                            <p className="text-[10px] text-gray-400 mt-0.5">Adjusted Fish Count: <span className="font-semibold text-gray-600" data-testid="fcr-adjusted-fish-count">{growthFCRResult.adjustedN.toLocaleString()}</span></p>
                            {/* ★ Mode-gated: Hide precise missing fish count in signal mode */}
                            {growthFCRResult.estimatedMissing > 0 && overriddenOutput?.finalDecision?.confidenceMode !== 'signal' && (
                              <p className="text-[10px] text-amber-600 mt-0.5">
                                {overriddenOutput?.finalDecision?.confidenceMode === 'evidence' ? '~' : ''}Estimated Missing Fish: <span className="font-semibold" data-testid="fcr-estimated-missing">{growthFCRResult.estimatedMissing.toLocaleString()}</span>
                              </p>
                            )}
                            {growthFCRResult.estimatedMissing > 0 && overriddenOutput?.finalDecision?.confidenceMode === 'signal' && (
                              <p className="text-[10px] text-amber-500 mt-0.5 italic">Indicative deviation observed — insufficient data for confirmation</p>
                            )}
                          </div>
                        </div>

                        {/* Breakdown */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                          <div className="bg-cyan-50 rounded-lg p-2.5 border border-cyan-200">
                            <p className="text-[10px] text-cyan-600 uppercase tracking-wider font-medium">Feed Used</p>
                            <p className="text-sm font-bold text-cyan-900" data-testid="fcr-feed-used">{growthFCR.totalFeedKg.toLocaleString(undefined, { maximumFractionDigits: 0 })} kg</p>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-2.5 border border-gray-200">
                            <p className="text-[10px] text-gray-500 uppercase tracking-wider font-medium">Starting Biomass</p>
                            <p className="text-sm font-bold text-gray-800" data-testid="fcr-starting-biomass">{growthFCRResult.reported.startBiomass.toLocaleString(undefined, { maximumFractionDigits: 0 })} kg</p>
                          </div>
                          <div className="bg-red-50 rounded-lg p-2.5 border border-red-200">
                            <p className="text-[10px] text-red-600 uppercase tracking-wider font-medium">Mortality Biomass</p>
                            <p className="text-sm font-bold text-red-800" data-testid="fcr-mortality-biomass">{growthFCR.mortalityBiomassKg.toLocaleString(undefined, { maximumFractionDigits: 0 })} kg</p>
                          </div>
                          <div className="bg-indigo-50 rounded-lg p-2.5 border border-indigo-200">
                            <p className="text-[10px] text-indigo-600 uppercase tracking-wider font-medium">Closing Biomass</p>
                            <p className="text-sm font-bold text-indigo-800" data-testid="fcr-closing-biomass">{growthFCRResult.reported.closingBiomass.toLocaleString(undefined, { maximumFractionDigits: 0 })} kg</p>
                          </div>
                        </div>

                        {/* ── Feed Intensity — Reported vs Adjusted ── */}
                        {growthFeedIntensity && (
                          <div className="mt-3 pt-3 border-t border-slate-200" data-testid="feed-intensity-section">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                              <div className="space-y-0.5">
                                <p className="text-[10px] text-gray-500 uppercase tracking-wider">Reported Feed Rate</p>
                                <p className="text-sm font-bold text-slate-800" data-testid="reported-feed-rate">{growthFeedIntensity.avgReportedFeedRate.toFixed(2)}%</p>
                              </div>
                              {overriddenOutput?.finalDecision?.missingFishUserVisible && growthFeedIntensity.isAdjusted && (
                              <div className="space-y-0.5">
                                <p className="text-[10px] text-gray-500 uppercase tracking-wider">Adjusted Feed Rate</p>
                                <p className="text-sm font-bold text-amber-700" data-testid="adjusted-feed-rate">{growthFeedIntensity.avgAdjustedFeedRate.toFixed(2)}%</p>
                              </div>
                              )}
                              <div className="space-y-0.5">
                                <p className="text-[10px] text-gray-500 uppercase tracking-wider">Reported Feed / Fish</p>
                                <p className="text-sm font-bold text-slate-800" data-testid="reported-feed-per-fish">{growthFeedIntensity.reportedFeedPerFishG.toFixed(0)} g</p>
                              </div>
                              {overriddenOutput?.finalDecision?.missingFishUserVisible && growthFeedIntensity.isAdjusted && (
                              <div className="space-y-0.5">
                                <p className="text-[10px] text-gray-500 uppercase tracking-wider">Adjusted Feed / Fish</p>
                                <p className="text-sm font-bold text-amber-700" data-testid="adjusted-feed-per-fish">{growthFeedIntensity.adjustedFeedPerFishG.toFixed(0)} g</p>
                              </div>
                              )}
                            </div>
                            {overriddenOutput?.finalDecision?.missingFishUserVisible && growthFeedIntensity.isAdjusted && (
                              <p className="text-[10px] text-slate-500 mt-2 italic" data-testid="feed-intensity-note">
                                Historical feed delivered is unchanged. Under the adjusted fish count, feed per fish and biomass-relative feeding rate increase. The same historical feed distributed across fewer fish results in a higher biological feeding rate.
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    {!growthFCRResult && showMissingFishIntelligence && missingFishIntelligence && (
                      <div className="mt-4 bg-sky-50 border border-sky-200 rounded-lg p-4" data-testid="fcr-unavailable-notice">
                        <p className="text-sm text-sky-800">
                          FCR analysis unavailable due to missing feed data. Upload to Spreadsheet Uploader to enable full FCR, biomass validation, and missing fish detection.
                        </p>
                      </div>
                    )}
                    
                    {/* ====== CYCLE COMPARISON SELECTOR ====== */}
                    <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200" data-pdf-hide="true" data-testid="comparison-selector">
                      <div className="flex items-center justify-between mb-2">
                        <button
                          onClick={() => {
                            setShowComparisonSelector(!showComparisonSelector);
                            if (!showRecords && allRecordsForComparison.length === 0) fetchRecords();
                          }}
                          className="text-sm font-medium text-gray-700 flex items-center gap-2 hover:text-purple-700 transition-colors"
                          data-testid="toggle-comparison-selector"
                        >
                          <GitCompareArrows className="w-4 h-4 text-purple-600" />
                          Compare with Saved Analysis
                          {comparisonAnalyses.length > 0 && (
                            <span className="bg-purple-100 text-purple-700 text-xs px-1.5 py-0.5 rounded-full font-semibold">
                              {comparisonAnalyses.length}
                            </span>
                          )}
                          {showComparisonSelector ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                        </button>
                        {comparisonAnalyses.length > 0 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setComparisonAnalyses([]);
                              setLineVisibility(prev => {
                                const reset = { ...prev };
                                Object.keys(reset).forEach(key => {
                                  if (key.startsWith('comp_')) delete reset[key];
                                });
                                return reset;
                              });
                            }}
                            className="text-gray-500 hover:text-red-600 h-6 px-2"
                            data-testid="clear-all-comparisons"
                          >
                            <X className="w-3 h-3 mr-1" />
                            Clear All
                          </Button>
                        )}
                      </div>

                      {/* Selected comparisons summary chips */}
                      {comparisonAnalyses.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-2">
                          {comparisonAnalyses.map((comp, idx) => {
                            const colors = COMPARISON_COLORS[idx % COMPARISON_COLORS.length];
                            const label = comp.unit ? `${comp.unit} ${comp.batch_code || ''}`.trim() : `Cycle ${idx + 1}`;
                            return (
                              <span key={comp.id} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-purple-50 border border-purple-200 text-purple-700">
                                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: colors.actual }} />
                                {label}
                                <button onClick={() => toggleComparisonRecord(comp)} className="ml-0.5 hover:text-red-600">
                                  <X className="w-3 h-3" />
                                </button>
                              </span>
                            );
                          })}
                          <span className="text-xs text-gray-400 self-center">(normalized by Day 0)</span>
                        </div>
                      )}
                      
                      {/* Expanded record selector */}
                      {showComparisonSelector && (
                        <div className="mt-2 max-h-52 overflow-y-auto rounded border border-gray-200 bg-white" data-testid="comparison-record-list">
                          {allRecordsForComparison.length === 0 ? (
                            <p className="text-xs text-gray-500 italic p-3 text-center">
                              {recordsLoading ? 'Loading saved records...' : 'No other saved analyses available. Save a record first.'}
                            </p>
                          ) : (
                            <table className="w-full text-xs">
                              <thead className="sticky top-0 bg-gray-100">
                                <tr className="text-gray-500 text-left">
                                  <th className="py-1.5 px-2 w-8"></th>
                                  <th className="py-1.5 px-2">Unit / Cage</th>
                                  <th className="py-1.5 px-2">Batch</th>
                                  <th className="py-1.5 px-2">Species</th>
                                  <th className="py-1.5 px-2">Arrival</th>
                                  <th className="py-1.5 px-2">Samples</th>
                                  <th className="py-1.5 px-2">Data</th>
                                </tr>
                              </thead>
                              <tbody>
                                {allRecordsForComparison.map((record) => {
                                  const isSelected = comparisonAnalyses.some(c => c.id === record.id);
                                  const colorIdx = comparisonAnalyses.findIndex(c => c.id === record.id);
                                  const color = isSelected && colorIdx >= 0 ? COMPARISON_COLORS[colorIdx % COMPARISON_COLORS.length] : null;
                                  const isLoading = loadingComparisonId === record.id;
                                  
                                  return (
                                    <tr
                                      key={record.id}
                                      className={`border-t border-gray-100 cursor-pointer transition-colors ${
                                        isSelected ? 'bg-purple-50' : 'hover:bg-gray-50'
                                      } ${!record.has_trajectories && !isSelected ? 'opacity-50' : ''}`}
                                      onClick={() => toggleComparisonRecord(record)}
                                      data-testid={`compare-row-${record.id}`}
                                    >
                                      <td className="py-1.5 px-2 text-center">
                                        {isLoading ? (
                                          <span className="inline-block w-3.5 h-3.5 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
                                        ) : isSelected && color ? (
                                          <span className="inline-block w-3.5 h-3.5 rounded border-2" style={{ backgroundColor: color.actual, borderColor: color.model }} />
                                        ) : (
                                          <span className="inline-block w-3.5 h-3.5 rounded border-2 border-gray-300" />
                                        )}
                                      </td>
                                      <td className="py-1.5 px-2 font-medium text-gray-800">{record.unit}</td>
                                      <td className="py-1.5 px-2 text-gray-600">{record.batch_code}</td>
                                      <td className="py-1.5 px-2 text-gray-600">{record.species}</td>
                                      <td className="py-1.5 px-2 text-gray-500">{record.arrival_date}</td>
                                      <td className="py-1.5 px-2 text-gray-500">{record.samples?.length || 0}</td>
                                      <td className="py-1.5 px-2">
                                        {record.has_trajectories ? (
                                          <span className="text-green-600 font-medium">Ready</span>
                                        ) : (
                                          <span className="text-gray-400" title="Run an analysis on this record to enable comparison">No data</span>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Daily Data Table - Shows all daily chart data */}
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm mt-4">
                  <div 
                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => setShowDailyTable(!showDailyTable)}
                    data-testid="toggle-daily-table-btn"
                  >
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-gray-800">Daily Data Table</h3>
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{chartData.length} days</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500">{showDailyTable ? 'Click to minimize' : 'Click to expand'}</span>
                      {showDailyTable ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                    </div>
                  </div>
                  
                  {showDailyTable && (
                    <div className="overflow-x-auto border-t">
                      <div className="max-h-96 overflow-y-auto">
                        <table className="w-full text-xs sm:text-sm">
                          <thead className="sticky top-0 bg-slate-50 z-10">
                            <tr>
                              <th className="border-b border-slate-200 px-2 py-2.5 text-left font-semibold text-slate-600 text-[10px] uppercase tracking-wider">Day</th>
                              <th className="border-b border-slate-200 px-2 py-2.5 text-right font-semibold text-blue-500 text-[10px] uppercase tracking-wider">Model (g)</th>
                              <th className="border-b border-slate-200 px-2 py-2.5 text-right font-semibold text-rose-500 text-[10px] uppercase tracking-wider">Actual (g)</th>
                              <th className="border-b border-slate-200 px-2 py-2.5 text-center font-semibold text-slate-600 text-[10px] uppercase tracking-wider">Sample</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(showAllDailyRows ? chartData : chartData.slice(0, 50)).map((row, idx) => (
                              <tr key={row.day != null ? `d${row.day}` : `gmd-${idx}`} className={`hover:bg-slate-50 transition-colors ${row['Sample Points'] !== null ? 'bg-amber-50/50' : idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`}>
                                <td className="border-b border-slate-100 px-2 py-1.5">{row.day}</td>
                                <td className="border-b border-slate-100 px-2 py-1.5 text-right text-blue-600 font-medium">
                                  {row['Model Growth'] !== null ? row['Model Growth'].toFixed(2) : '-'}
                                </td>
                                <td className="border-b border-slate-100 px-2 py-1.5 text-right text-rose-600 font-medium">
                                  {row['Actual Growth'] !== null ? row['Actual Growth'].toFixed(2) : '-'}
                                </td>
                                <td className="border-b border-slate-100 px-2 py-1.5 text-center">
                                  {row['Sample Points'] !== null && (
                                    <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full">
                                      {row['Sample Points'].toFixed(1)}g
                                    </span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {chartData.length > 50 && (
                        <div className="flex items-center justify-center gap-2 py-3 border-t bg-gray-50">
                          <span className="text-sm text-gray-600">
                            {showAllDailyRows ? `Showing all ${chartData.length} days` : `Showing 50 of ${chartData.length} days`}
                          </span>
                          <button
                            onClick={(e) => { e.stopPropagation(); setShowAllDailyRows(!showAllDailyRows); }}
                            className="text-sm text-[#0891b2] hover:text-[#0e7490] font-medium underline"
                            data-testid="show-all-daily-rows-btn"
                          >
                            {showAllDailyRows ? 'Show less' : 'Show all days'}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-gray-800 text-sm md:text-base">Detailed Comparison</h3>
                    <div className="flex items-center gap-3">
                      {suspectComparisonIndices.size > 0 && (
                        <label className="flex items-center gap-1.5 cursor-pointer" data-testid="exclude-suspect-toggle">
                          <input
                            type="checkbox"
                            checked={[...suspectComparisonIndices].every(i => excludedSamples.has(i))}
                            onChange={(e) => {
                              setExcludedSamples(prev => {
                                const next = new Set(prev);
                                if (e.target.checked) {
                                  suspectComparisonIndices.forEach(i => next.add(i));
                                } else {
                                  suspectComparisonIndices.forEach(i => next.delete(i));
                                }
                                return next;
                              });
                            }}
                            className="w-3.5 h-3.5 rounded border-amber-500 text-amber-600 focus:ring-amber-500"
                          />
                          <span className="text-xs text-amber-700 font-medium">Exclude suspect samples</span>
                        </label>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowAllColumns(!showAllColumns)}
                        className="md:hidden text-xs text-[#0891b2]"
                      >
                        {showAllColumns ? 'Show less' : 'Show all columns'}
                      </Button>
                    </div>
                  </div>
                  <div className="overflow-x-auto -mx-4 md:mx-0">
                    <div className="min-w-full inline-block align-middle px-4 md:px-0">
                      <table className="w-full border-collapse text-xs md:text-sm" data-testid="detailed-comparison-table">
                        <thead>
                          <tr className="bg-gray-100">
                            <th className="border border-gray-300 px-2 md:px-4 py-1.5 md:py-2 sticky left-0 bg-gray-100 z-10 text-left">Sample</th>
                            <th className="border border-gray-300 px-2 md:px-4 py-1.5 md:py-2">Days</th>
                            <th className="border border-gray-300 px-2 md:px-4 py-1.5 md:py-2">Actual</th>
                            <th className="border border-gray-300 px-2 md:px-4 py-1.5 md:py-2">Model</th>
                            <th className={`border border-gray-300 px-2 md:px-4 py-1.5 md:py-2 ${!showAllColumns ? 'hidden md:table-cell' : ''}`}>Diff (g)</th>
                            <th className="border border-gray-300 px-2 md:px-4 py-1.5 md:py-2">Diff %</th>
                            <th className="border border-gray-300 px-1 md:px-2 py-1.5 md:py-2 w-10 text-center" title="Exclude from analysis">Excl.</th>
                          </tr>
                        </thead>
                        <tbody>
                          {result.comparisons.map((comp, idx) => {
                            const isSuspect = suspectComparisonIndices.has(idx);
                            const isExcluded = excludedSamples.has(idx);
                            // When best-fit adjustment is active, show adjusted model weight and recalculated deviation
                            const adjFactor = calculatedBestFitAdjustment?.factor || 1;
                            const displayModelWeight = adjFactor !== 1
                              ? Math.round(comp.model_weight_g * adjFactor * 10) / 10
                              : comp.model_weight_g;
                            const displayDiffG = adjFactor !== 1
                              ? Math.round((comp.actual_weight_g - displayModelWeight) * 10) / 10
                              : comp.difference_g;
                            const displayDiffPct = adjFactor !== 1
                              ? Math.round(((comp.actual_weight_g - displayModelWeight) / displayModelWeight) * 100 * 10) / 10
                              : comp.difference_percentage;
                            return (
                            <tr key={comp.sample_number} className={`hover:bg-gray-50 ${isExcluded ? 'opacity-50 bg-gray-50' : isSuspect ? 'bg-amber-50' : ''}`}>
                              <td className={`border border-gray-300 px-2 md:px-4 py-1.5 md:py-2 text-center sticky left-0 z-10 font-medium ${isExcluded ? 'bg-gray-50' : isSuspect ? 'bg-amber-50' : 'bg-white'}`}>
                                <span className={isExcluded ? 'line-through text-gray-400' : ''}>
                                  {comp.sample_number}
                                </span>
                                {isSuspect && !isExcluded && (
                                  <span className="block text-[10px] text-amber-600 font-medium leading-tight mt-0.5">Likely erroneous</span>
                                )}
                              </td>
                              <td className={`border border-gray-300 px-2 md:px-4 py-1.5 md:py-2 text-center ${isExcluded ? 'line-through text-gray-400' : ''}`}>{comp.days_since_arrival}</td>
                              <td className={`border border-gray-300 px-2 md:px-4 py-1.5 md:py-2 text-right ${isExcluded ? 'line-through text-gray-400' : ''}`}>{comp.actual_weight_g}</td>
                              <td className={`border border-gray-300 px-2 md:px-4 py-1.5 md:py-2 text-right ${isExcluded ? 'line-through text-gray-400' : ''}`}>{displayModelWeight}</td>
                              <td className={`border border-gray-300 px-2 md:px-4 py-1.5 md:py-2 text-right font-semibold ${
                                isExcluded ? 'line-through text-gray-400' : displayDiffG > 0 ? 'text-green-600' : 'text-red-600'
                              } ${!showAllColumns ? 'hidden md:table-cell' : ''}`}>
                                {displayDiffG > 0 ? '+' : ''}{displayDiffG}
                              </td>
                              <td className={`border border-gray-300 px-2 md:px-4 py-1.5 md:py-2 text-right font-semibold ${
                                isExcluded ? 'line-through text-gray-400' : displayDiffPct > 0 ? 'text-green-600' : 'text-red-600'
                              }`}>
                                {displayDiffPct > 0 ? '+' : ''}{displayDiffPct}%
                              </td>
                              <td className="border border-gray-300 px-1 md:px-2 py-1.5 md:py-2 text-center">
                                <input
                                  type="checkbox"
                                  checked={isExcluded}
                                  onChange={() => toggleSampleExclusion(idx)}
                                  className="w-3.5 h-3.5 rounded border-gray-400 text-gray-600 focus:ring-gray-500 cursor-pointer"
                                  title={isExcluded ? 'Include this sample' : 'Exclude this sample from analysis'}
                                  data-testid={`exclude-sample-${idx}`}
                                />
                              </td>
                            </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      {excludedSamples.size > 0 && (
                        <p className="text-xs text-gray-500 mt-2 italic" data-testid="excluded-samples-note">
                          {excludedSamples.size} sample{excludedSamples.size > 1 ? 's' : ''} excluded from trend and confidence calculations.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Growth Signal Analysis panels now rendered inside the shared MissingFishIntelligenceReport component */}
                
                {/* Detection Method Selector */}
                {!isRecordClosed && showMissingFishIntelligence && missingFishIntelligence && (
                  <div className="mt-4 bg-white p-4 rounded-lg border border-indigo-200 shadow-sm" data-testid="detection-method-selector">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-800">Detection Method</h4>
                        <p className="text-[10px] text-gray-500 mt-0.5">Primary method for estimating missing fish</p>
                      </div>
                      <div className="flex gap-1 bg-gray-100 rounded-lg p-0.5" data-testid="detection-method-toggle">
                        <button
                          className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${detectionMethod === 'jump' ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                          onClick={async () => {
                            setDetectionMethod('jump');
                            let wModelFCR = null;
                            try {
                              const lastComp = result.comparisons[result.comparisons.length - 1];
                              const lastWeight = parseFloat(lastComp.actual_weight_g);
                              const authToken = token; // from useAuth
                              const resp = await axios.get(`${BACKEND_URL}/api/model-fcr`, {
                                params: { 
                                  species: formData.species, 
                                  weight_g: lastWeight, 
                                  genetic_origin: formData.genetic_origin,
                                  fcr_version_id: formData.fcr_version_id || undefined
                                },
                                headers: { 'Authorization': `Bearer ${token}` },
                              });
                              wModelFCR = resp.data.model_fcr;
                            } catch {}
                            const reIntel = generateMissingFishIntelligence({ growthModelResult: result, weightBasedModelFCR: wModelFCR, detectionMethod: 'jump', excludedIndices: excludedSamples, modelScalingFactor: calculatedBestFitAdjustment?.factor || 1 });
                            if (reIntel) {
                              setMissingFishIntelligence(reIntel);
                              setAssumedFishNumber(reIntel.initialAssumedFishNumber);
                              setTrajectoryJump(reIntel.trajectoryJump);
                              setGraphInsight(reIntel.graphInsight);
                            }
                          }}
                          data-testid="method-jump"
                        >Jump Analysis</button>
                        <button
                          className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${detectionMethod === 'fcr' ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                          onClick={async () => {
                            setDetectionMethod('fcr');
                            let wModelFCR = null;
                            try {
                              const lastComp = result.comparisons[result.comparisons.length - 1];
                              const lastWeight = parseFloat(lastComp.actual_weight_g);
                              const authToken = token; // from useAuth
                              const resp = await axios.get(`${BACKEND_URL}/api/model-fcr`, {
                                params: { 
                                  species: formData.species, 
                                  weight_g: lastWeight, 
                                  genetic_origin: formData.genetic_origin,
                                  fcr_version_id: formData.fcr_version_id || undefined
                                },
                                headers: { 'Authorization': `Bearer ${token}` },
                              });
                              wModelFCR = resp.data.model_fcr;
                            } catch {}
                            const reIntel = generateMissingFishIntelligence({ growthModelResult: result, weightBasedModelFCR: wModelFCR, detectionMethod: 'fcr', excludedIndices: excludedSamples });
                            if (reIntel) {
                              setMissingFishIntelligence(reIntel);
                              setAssumedFishNumber(reIntel.initialAssumedFishNumber);
                              setTrajectoryJump(reIntel.trajectoryJump);
                              setGraphInsight(reIntel.graphInsight);
                            }
                          }}
                          data-testid="method-fcr"
                        >FCR Analysis</button>
                      </div>
                    </div>
                    {/* Method resolution status */}
                    {missingFishIntelligence.methodResolution && (
                      <div className="mt-3 pt-3 border-t border-gray-100" data-testid="method-resolution-status">
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px]">
                          <span className="text-gray-500">Selected: <span className="font-semibold text-gray-700">{missingFishIntelligence.methodResolution.selectedMethod === 'jump' ? 'Jump Analysis' : 'FCR Analysis'}</span></span>
                          <span className="text-gray-500">Active: <span className={`font-semibold ${missingFishIntelligence.methodResolution.fallbackOccurred ? 'text-amber-600' : 'text-indigo-700'}`}>{missingFishIntelligence.methodResolution.activeMethod === 'jump' ? 'Jump Analysis' : 'FCR Analysis'}</span></span>
                          {missingFishIntelligence.methodResolution.fallbackOccurred && (
                            <span className="text-amber-600 font-medium bg-amber-50 px-1.5 py-0.5 rounded">{missingFishIntelligence.methodResolution.fallbackReason || 'Fallback occurred'}</span>
                          )}
                          {missingFishIntelligence.methodResolution.jumpDetectedButBelowModel && !missingFishIntelligence.methodResolution.fallbackOccurred && (
                            <span className="text-gray-500 bg-gray-50 px-1.5 py-0.5 rounded">Jump detected but samples below model</span>
                          )}
                          {missingFishIntelligence.methodResolution.jumpAvailable && (
                            <span className="text-green-600">Jump: {(missingFishIntelligence.methodResolution.jumpFishNumber || 0).toLocaleString()} fish</span>
                          )}
                          {missingFishIntelligence.methodResolution.fcrAvailable && (
                            <span className="text-blue-600">FCR: {(missingFishIntelligence.methodResolution.fcrFishNumber || 0).toLocaleString()} fish</span>
                          )}
                          {!missingFishIntelligence.methodResolution.jumpAvailable && !missingFishIntelligence.methodResolution.fcrAvailable && (
                            <span className="text-gray-400">No detection signal from either method</span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Closed cage message - shows to non-admins OR when admin sees a closed record */}
                {isRecordClosedRaw && !isAdmin && (
                  <div className="mt-4 bg-slate-50 border border-slate-300 rounded-lg p-4 text-center" data-testid="closed-cage-intelligence-msg">
                    <Lock className="w-5 h-5 text-slate-400 mx-auto mb-2" />
                    <p className="text-sm font-medium text-slate-700">Missing fish estimation not applicable.</p>
                    <p className="text-xs text-slate-500 mt-1">Cage is closed and final fish numbers have already materialized.</p>
                  </div>
                )}
                
                {/* Admin override notice - shows when admin is viewing a closed record */}
                {isRecordClosedRaw && isAdmin && (
                  <div className="mt-4 bg-amber-50 border-2 border-amber-400 rounded-lg p-4" data-testid="admin-override-notice">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-amber-100 rounded-lg">
                        <Unlock className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-amber-800">Admin Override Active</p>
                        <p className="text-xs text-amber-700">This record is closed, but you have admin access to view and edit.</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Missing Fish Intelligence Results — Shared Component */}
                {!isRecordClosed && showMissingFishIntelligence && missingFishIntelligence && (
                  <div className="mt-6 space-y-4" ref={mfiPdfRef}>
                    {/* Admin Hidden Sections Overview */}
                    <AdminHiddenSectionsOverview className="mb-4" />
                    
                    <MissingFishIntelligenceReport 
                      data={missingFishIntelligence}
                      economicImpact={missingFishIntelligence?.economicImpact}
                      dataQuality={missingFishIntelligence?.dataQuality}
                      mortalityCheck={missingFishIntelligence?.mortalityCheck}
                      samplingRecommendation={missingFishIntelligence?.samplingRecommendation}
                      overfeeding={missingFishIntelligence?.overfeedingRisk}
                      trajectoryJump={trajectoryJump}
                      fcrBasedDetection={missingFishIntelligence?.fcrBasedDetection}
                      growthFit={missingFishIntelligence?.growthFit}
                      methodResolution={missingFishIntelligence?.methodResolution}
                      sharedMetrics={missingFishIntelligence?.sharedMetrics}
                      ssot={missingFishIntelligence?.ssot}
                      overriddenOutput={overriddenOutput}
                      showDeviationTable={true}
                      showExplanations={true}
                      onShowAllDataChange={setShowAllDataOverride}
                      showAllDataOverride={showAllDataOverride}
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* Wingman Proactive Alert */}
        {result && showMissingFishIntelligence && missingFishIntelligence && (
          <WingmanNudge patterns={detectGrowthModelPatterns({ missingFishIntelligence, calculatedBestFitAdjustment, result })} />
        )}

        {/* Wingman Insight for Growth Analysis */}
        {result && (
          <WingmanInsight
            analysisType="Growth vs Model Analysis"
            analysisData={{
              species: result.species,
              model_name: result.model_name,
              starting_weight: result.starting_weight,
              starting_fish: result.starting_fish_number,
              current_weight: result.comparisons?.[result.comparisons.length - 1]?.actual_weight_g,
              total_days: result.total_days,
              model_adjustment: result.model_adjustment_percent,
              sample_count: result.comparisons?.length,
              // Missing fish & jump detection
              missing_fish_detected: missingFishIntelligence?.ssot?.hasMissingFish || false,
              estimated_missing: missingFishIntelligence?.ssot?.missingFishCount || 0,
              jump_detected: missingFishIntelligence?.trajectoryJump?.jump_detected || false,
              jump_estimated_missing: missingFishIntelligence?.trajectoryJump?.estimated_missing_fish || 0,
              jump_loss_window: missingFishIntelligence?.trajectoryJump?.loss_window || null,
              detection_strength: missingFishIntelligence?.ssot?.detectionStrength || null,
              confidence_mode: missingFishIntelligence?.ssot?.confidenceMode || null,
              // ★ SSOT — pass actionability so AI can gate definitive wording
              actionability: missingFishIntelligence?.ssot?.actionability || missingFishIntelligence?.decisionContext?.decisionClass || null,
              decision_class: missingFishIntelligence?.decisionContext?.decisionClass || null,
              best_fit_adjustment: calculatedBestFitAdjustment || null,
              model_fcr: missingFishIntelligence?.sharedMetrics?.model_fcr || null,
              // ★ Jump detection reasoning context for AI explanation
              unconfirmed_jumps: missingFishIntelligence?.trajectoryJump?.suspect_samples?.length || 0,
              unconfirmed_jump_reason: missingFishIntelligence?.trajectoryJump?.jump_event_note || null,
              // Per-sample deviation pattern (concise summary for AI)
              sample_deviation_pattern: result.comparisons?.map(c => ({
                day: c.day,
                actual_g: c.actual_weight_g,
                model_g: c.model_weight_g,
                deviation_pct: c.model_weight_g > 0 ? Math.round((c.actual_weight_g - c.model_weight_g) / c.model_weight_g * 1000) / 10 : 0
              })) || [],
              // Best-fit context
              best_fit_factor: calculatedBestFitAdjustment?.factor || 1,
              best_fit_r_squared: calculatedBestFitAdjustment?.rSquared || null,
              // Adjusted model interpretation
              adjusted_interpretation: adjustedInterpretation || null,
              // Data quality
              data_quality_score: missingFishIntelligence?.dataQuality?.overallScore || null
            }}
          />
        )}


        {/* Record History Panel */}
        {activeRecordId && (
          <RecordHistoryPanel
            recordId={activeRecordId}
            recordType="growth"
            recordName={formData?.unit || 'Unknown'}
          />
        )}

        {/* Growth Records Section */}
        <Card className="border-slate-200 shadow-md" data-testid="growth-records-card">
          <CardHeader 
            className="cursor-pointer hover:bg-slate-50 transition-colors"
            onClick={() => { setShowRecords(!showRecords); if (!showRecords) fetchRecords(); }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <FolderOpen className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <CardTitle className="text-amber-700">Growth Records</CardTitle>
                  <CardDescription className="text-gray-600">Save, update, and manage cage analyses</CardDescription>
                </div>
              </div>
              {showRecords ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
            </div>
          </CardHeader>
          
          {showRecords && (
            <CardContent className="space-y-4">
              {/* Action Buttons */}
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearForm}
                  className="text-gray-600 border-gray-300"
                  data-testid="new-record-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-1" />
                  New Record
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-red-600 border-red-300 hover:bg-red-50"
                  disabled={selectedGrowthRecordIds.size === 0}
                  onClick={deleteSelectedGrowthRecords}
                  data-testid="delete-selected-btn"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete Selected ({selectedGrowthRecordIds.size})
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const allIds = [...records.ongoing, ...records.closed].map(r => r.id);
                    setSelectedGrowthRecordIds(new Set(allIds));
                  }}
                  disabled={selectedGrowthRecordIds.size === records.ongoing.length + records.closed.length}
                  data-testid="select-all-records-btn"
                  className="text-gray-600 border-gray-300"
                >
                  Select All
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDeleteAllDialog(true)}
                  className="text-red-600 border-red-300 hover:bg-red-50"
                  disabled={records.ongoing.length + records.closed.length === 0}
                  data-testid="delete-all-btn"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete All
                </Button>
                <div className="flex-1" />
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-[140px] h-8 text-xs">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="updated_at">Last Updated</SelectItem>
                    <SelectItem value="unit">Unit</SelectItem>
                    <SelectItem value="batch_code">Batch Code</SelectItem>
                    <SelectItem value="arrival_date">Arrival Date</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
                  className="px-2"
                >
                  {sortOrder === 'desc' ? '↓' : '↑'}
                </Button>
              </div>

              {/* Records Tabs */}
              <Tabs defaultValue="ongoing" className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="ongoing" className="text-sm">
                    Ongoing Cages ({records.ongoing.length})
                  </TabsTrigger>
                  <TabsTrigger value="closed" className="text-sm">
                    Closed Cage Analysis ({records.closed.length})
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="ongoing" className="mt-4">
                  {recordsLoading ? (
                    <p className="text-center text-gray-500 py-8">Loading records...</p>
                  ) : records.ongoing.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">No ongoing cage records. Run an analysis and save it to create one.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs md:text-sm border-collapse">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="border px-2 py-1.5 w-8 text-center">
                              <input
                                type="checkbox"
                                className="w-3 h-3 rounded"
                                checked={records.ongoing.length > 0 && records.ongoing.every(r => selectedGrowthRecordIds.has(r.id))}
                                ref={(el) => { if (el) el.indeterminate = records.ongoing.some(r => selectedGrowthRecordIds.has(r.id)) && !records.ongoing.every(r => selectedGrowthRecordIds.has(r.id)); }}
                                onChange={(e) => {
                                  setSelectedGrowthRecordIds(prev => {
                                    const n = new Set(prev);
                                    records.ongoing.forEach(r => e.target.checked ? n.add(r.id) : n.delete(r.id));
                                    return n;
                                  });
                                }}
                                data-testid="ongoing-select-all-checkbox"
                              />
                            </th>
                            <th className="border px-2 py-1.5 text-left font-medium">Unit</th>
                            <th className="border px-2 py-1.5 text-left font-medium">Batch Code</th>
                            <th className="border px-2 py-1.5 text-left font-medium hidden md:table-cell">Species</th>
                            <th className="border px-2 py-1.5 text-left font-medium hidden sm:table-cell">Arrival</th>
                            <th className="border px-2 py-1.5 text-left font-medium hidden lg:table-cell">Last Sample</th>
                            <th className="border px-2 py-1.5 text-left font-medium hidden md:table-cell">Updated</th>
                            <th className="border px-2 py-1.5 text-center font-medium">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {records.ongoing.map((record) => (
                            <tr key={record.id} className={`hover:bg-gray-50 ${activeRecordId === record.id ? 'bg-amber-50' : ''}`}>
                              <td className="border px-2 py-1.5 text-center">
                                <input
                                  type="checkbox"
                                  className="w-3 h-3 rounded"
                                  checked={selectedGrowthRecordIds.has(record.id)}
                                  onChange={(e) => {
                                    setSelectedGrowthRecordIds(prev => {
                                      const n = new Set(prev);
                                      e.target.checked ? n.add(record.id) : n.delete(record.id);
                                      return n;
                                    });
                                  }}
                                  data-testid={`ongoing-record-checkbox-${record.id}`}
                                />
                              </td>
                              <td className="border px-2 py-1.5 font-medium">{record.unit}</td>
                              <td className="border px-2 py-1.5">{record.batch_code}</td>
                              <td className="border px-2 py-1.5 hidden md:table-cell">{record.species}</td>
                              <td className="border px-2 py-1.5 hidden sm:table-cell">{record.arrival_date}</td>
                              <td className="border px-2 py-1.5 hidden lg:table-cell">{record.last_sample_date || '-'}</td>
                              <td className="border px-2 py-1.5 hidden md:table-cell">{new Date(record.updated_at).toLocaleDateString()}</td>
                              <td className="border px-2 py-1.5">
                                <div className="flex items-center justify-center gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => loadRecord(record)}
                                    className="h-7 px-2 text-[#0891b2] hover:text-[#0e7490]"
                                    title="Load record"
                                  >
                                    <FolderOpen className="w-3.5 h-3.5" />
                                  </Button>
                                  <InlineRenameDouble
                                    value1={record.unit}
                                    value2={record.batch_code}
                                    onSave={(updates) => renameGrowthRecord(record.id, updates)}
                                    disabled={isDemo}
                                  />
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => toggleCageClosed(record.id)}
                                    className="h-7 px-2 text-gray-500 hover:text-gray-700"
                                    title="Close cage"
                                  >
                                    <Lock className="w-3.5 h-3.5" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => deleteRecord(record.id)}
                                    className="h-7 px-2 text-red-500 hover:text-red-700"
                                    title="Delete record"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="closed" className="mt-4">
                  {recordsLoading ? (
                    <p className="text-center text-gray-500 py-8">Loading records...</p>
                  ) : records.closed.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">No closed cage records.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs md:text-sm border-collapse">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="border px-2 py-1.5 w-8 text-center">
                              <input
                                type="checkbox"
                                className="w-3 h-3 rounded"
                                checked={records.closed.length > 0 && records.closed.every(r => selectedGrowthRecordIds.has(r.id))}
                                ref={(el) => { if (el) el.indeterminate = records.closed.some(r => selectedGrowthRecordIds.has(r.id)) && !records.closed.every(r => selectedGrowthRecordIds.has(r.id)); }}
                                onChange={(e) => {
                                  setSelectedGrowthRecordIds(prev => {
                                    const n = new Set(prev);
                                    records.closed.forEach(r => e.target.checked ? n.add(r.id) : n.delete(r.id));
                                    return n;
                                  });
                                }}
                                data-testid="closed-select-all-checkbox"
                              />
                            </th>
                            <th className="border px-2 py-1.5 text-left font-medium">Unit</th>
                            <th className="border px-2 py-1.5 text-left font-medium">Batch Code</th>
                            <th className="border px-2 py-1.5 text-left font-medium hidden md:table-cell">Species</th>
                            <th className="border px-2 py-1.5 text-left font-medium hidden sm:table-cell">Arrival</th>
                            <th className="border px-2 py-1.5 text-left font-medium hidden lg:table-cell">Last Sample</th>
                            <th className="border px-2 py-1.5 text-left font-medium hidden md:table-cell">Updated</th>
                            <th className="border px-2 py-1.5 text-center font-medium">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {records.closed.map((record) => (
                            <tr key={record.id} className={`hover:bg-gray-50 ${activeRecordId === record.id ? 'bg-amber-50' : ''}`}>
                              <td className="border px-2 py-1.5 text-center">
                                <input
                                  type="checkbox"
                                  className="w-3 h-3 rounded"
                                  checked={selectedGrowthRecordIds.has(record.id)}
                                  onChange={(e) => {
                                    setSelectedGrowthRecordIds(prev => {
                                      const n = new Set(prev);
                                      e.target.checked ? n.add(record.id) : n.delete(record.id);
                                      return n;
                                    });
                                  }}
                                  data-testid={`closed-record-checkbox-${record.id}`}
                                />
                              </td>
                              <td className="border px-2 py-1.5 font-medium text-gray-500">{record.unit}</td>
                              <td className="border px-2 py-1.5 text-gray-500">{record.batch_code}</td>
                              <td className="border px-2 py-1.5 hidden md:table-cell text-gray-500">{record.species}</td>
                              <td className="border px-2 py-1.5 hidden sm:table-cell text-gray-500">{record.arrival_date}</td>
                              <td className="border px-2 py-1.5 hidden lg:table-cell text-gray-500">{record.last_sample_date || '-'}</td>
                              <td className="border px-2 py-1.5 hidden md:table-cell text-gray-500">{new Date(record.updated_at).toLocaleDateString()}</td>
                              <td className="border px-2 py-1.5">
                                <div className="flex items-center justify-center gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => loadRecord(record)}
                                    className="h-7 px-2 text-[#0891b2] hover:text-[#0e7490]"
                                    title="Load record"
                                  >
                                    <FolderOpen className="w-3.5 h-3.5" />
                                  </Button>
                                  <InlineRenameDouble
                                    value1={record.unit}
                                    value2={record.batch_code}
                                    onSave={(updates) => renameGrowthRecord(record.id, updates)}
                                    disabled={isDemo}
                                  />
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => toggleCageClosed(record.id)}
                                    className="h-7 px-2 text-green-500 hover:text-green-700"
                                    title="Reopen cage"
                                  >
                                    <Unlock className="w-3.5 h-3.5" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => deleteRecord(record.id)}
                                    className="h-7 px-2 text-red-500 hover:text-red-700"
                                    title="Delete record"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </CardContent>
          )}
        </Card>

        {/* Help & Benefits Section */}
        <HelpBenefits 
          youtubeUrl={HELP_URLS.growthModelAnalysis.youtube}
          benefitsUrl={HELP_URLS.growthModelAnalysis.benefits}
          featureName="Growth vs Model Analysis"
        />

        {/* Glossary footer (collapsible) — visible on every Growth-vs-Model result */}
        {result && <ReportGlossary />}
      </div>

      {/* Replace Record Confirmation Dialog */}
      <Dialog open={showReplaceDialog} onOpenChange={setShowReplaceDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-amber-700">Record Already Exists</DialogTitle>
            <DialogDescription>
              A record with Unit &ldquo;<strong>{formData.unit}</strong>&rdquo; and Batch Code &ldquo;<strong>{formData.batch_code}</strong>&rdquo; already exists.
              Would you like to replace it with the current data?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowReplaceDialog(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={() => saveRecord(true)}
            >
              Replace Record
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete All Confirmation Dialog */}
      <Dialog open={showDeleteAllDialog} onOpenChange={setShowDeleteAllDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-700">Delete All Records</DialogTitle>
            <DialogDescription>
              This will permanently delete all your growth records ({records.ongoing.length + records.closed.length} records).
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-4">
            <Label htmlFor="delete-confirm">Type &ldquo;DELETE ALL&rdquo; to confirm:</Label>
            <Input
              id="delete-confirm"
              value={deleteAllConfirmText}
              onChange={(e) => setDeleteAllConfirmText(e.target.value)}
              placeholder="DELETE ALL"
              className="border-red-300"
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => { setShowDeleteAllDialog(false); setDeleteAllConfirmText(''); }}>
              Cancel
            </Button>
            <Button 
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={deleteAllRecords}
              disabled={deleteAllConfirmText !== 'DELETE ALL'}
            >
              Delete All Records
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Expanded Chart Modal (Focus Mode) */}
      <Dialog open={chartExpanded} onOpenChange={setChartExpanded}>
        <DialogContent className="max-w-[100vw] w-full h-[100vh] max-h-[100vh] p-0 m-0 border-0 rounded-none">
          <div className="flex flex-col h-full bg-white">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <DialogTitle className="text-lg font-semibold text-[#0891b2]">
                Growth vs Model
              </DialogTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setChartExpanded(false)}
                className="p-2"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            {/* Chart - Takes most of the space */}
            <div className="flex-1 p-4 min-h-0">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart 
                  data={chartData}
                  margin={{ top: 10, right: 20, left: 10, bottom: 40 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" opacity={0.4} />
                  <XAxis 
                    dataKey="day" 
                    label={{ 
                      value: 'Days Since Arrival', 
                      position: 'insideBottom', 
                      offset: -10,
                      style: { fontSize: '12px', fontWeight: '600', fill: '#374151' }
                    }}
                    tick={{ fill: '#6b7280', fontSize: 11 }}
                    stroke="#9ca3af"
                  />
                  <YAxis 
                    label={{ 
                      value: 'Weight (g)', 
                      angle: -90, 
                      position: 'insideLeft',
                      offset: 10,
                      style: { fontSize: '12px', fontWeight: '600', fill: '#374151', textAnchor: 'middle' }
                    }}
                    tick={{ fill: '#6b7280', fontSize: 11 }}
                    stroke="#9ca3af"
                    width={50}
                  />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: '#ffffff',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                      padding: '10px',
                      fontSize: '12px'
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="Model Growth" 
                    name={isAdjusted ? `Model (${result?.model_adjustment_percent}%)` : "Model (SGR)"}
                    stroke="#3b82f6"
                    strokeWidth={3}
                    dot={false}
                    activeDot={{ r: 6 }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="Actual Growth" 
                    name="Actual (SGR-corrected)"
                    stroke="#ef4444"
                    strokeWidth={3}
                    dot={false}
                    activeDot={{ r: 6 }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="Sample Points" 
                    name="Samples"
                    stroke="none"
                    dot={{ fill: '#0891b2', stroke: '#ffffff', strokeWidth: 2, r: 7 }}
                    activeDot={{ r: 9, fill: '#0e7490' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
            
            {/* Legend chips at bottom */}
            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <div className="flex flex-wrap items-center justify-center gap-3">
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-200 text-sm font-medium text-blue-700">
                  <span className="w-4 h-0.5 bg-blue-500 rounded"></span>
                  Model (SGR)
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-50 border border-red-200 text-sm font-medium text-red-700">
                  <span className="w-4 h-0.5 bg-red-500 rounded"></span>
                  Actual (SGR)
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-teal-50 border border-teal-200 text-sm font-medium text-teal-700">
                  <span className="w-2.5 h-2.5 bg-teal-500 rounded-full"></span>
                  Samples
                </span>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Legend Info Modal */}
      <Dialog open={showLegendInfo} onOpenChange={setShowLegendInfo}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-[#0891b2]">Understanding the Chart</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-gray-700">
            <div className="flex items-start gap-3">
              <span className="w-4 h-0.5 bg-blue-500 rounded mt-2 flex-shrink-0"></span>
              <div>
                <p className="font-semibold text-blue-700">Model (SGR)</p>
                <p className="text-gray-600">Optimal growth curve based on SGR potential for the species and temperature.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="w-4 h-0.5 bg-red-500 rounded mt-2 flex-shrink-0"></span>
              <div>
                <p className="font-semibold text-red-700">Actual (SGR path)</p>
                <p className="text-gray-600">Growth trajectory that follows model SGR between samples, then snaps to measured weights.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="w-2.5 h-2.5 bg-teal-500 rounded-full mt-1 flex-shrink-0"></span>
              <div>
                <p className="font-semibold text-teal-700">Samples</p>
                <p className="text-gray-600">Actual measured weight data points from your samples.</p>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
